import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Star, Camera, Clock, ArrowLeft, User, List, Map, Navigation, Edit2 } from 'lucide-react';
import MapView from './MapView';
import Toast from './Toast';
import packageJson from '../package.json';

// GPS & Geocoding Imports
import { getUserLocation, formatDistance, sortToiletsByDistance } from './geolocation';
import { reverseGeocode } from './geocoding';
import { searchCities } from './geocoding';

// Internationalization
import { getRandomFunFact } from './funFacts';
import { getEasterEgg } from './easterEggs';
import { normalizeCityName } from './cityAliases';
import { t as translate, getCurrentLanguage, setLanguage, hasLanguageBeenSet, availableAmenities, translateAmenity } from './translations';

// Firebase Imports
import { 
  getToiletsByCity,
  addToilet,
  updateToilet,
  uploadPhoto,
  reportClosure,
  deleteToilet,
  deletePhoto,
  approveToilet,
  rejectToilet,
  deleteReview,
  getAllReports,
  deleteReportsForToilet,
  closeToilet,
  reopenToilet,
  getAllToilets,
  reportComment,
  getAllCommentReports,
  resolveCommentReport,
  deleteReportsForComment,
} from './firebaseHelpers';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { db } from './firebase';
import { collection, query, where, getDocs, updateDoc, deleteDoc, addDoc, doc, getDoc } from 'firebase/firestore';
import { checkAdminAuth, loginAdmin, logoutAdmin } from './adminAuth';

// Photo-Helper Import 
import { 
  createPhotoObject,
  getApprovedPhotos, 
  getPhotoUrl
} from './photoHelpers';

// Offline-Helpers
import {
  isOnline,
  setupNetworkListeners,
  cacheToiletsByCity,
  getCachedToiletsByCity,
  addToOfflineQueue,
  getOfflineQueue,
  removeFromOfflineQueue
} from './offlineHelper';
import OfflineBanner from './OfflineBanner';

import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// Helper-Funktion für Theme-Color
const setThemeColor = (color) => {
  let metaThemeColor = document.querySelector("meta[name='theme-color']");
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(metaThemeColor);
  }
  metaThemeColor.setAttribute('content', color);
};

const sanitizeComment = (text) => {
  if (!text) return '';
  
  return text
    .replace(/[<>]/g, '') 
    .replace(/["']/g, '') 
    .replace(/\s+/g, ' ') 
    .slice(0, 300)
    .trim();
};

const sanitizeName = (text) => {
  if (!text) return '';
  
  return text
    .replace(/[<>]/g, '')
    .replace(/["']/g, '')
    .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_.,]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 100)
    .trim();
};

const sanitizeAddress = (text) => {
  if (!text) return '';
  
  return text
    .replace(/[<>]/g, '')
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 150)
    .trim();
};

const sanitizePrice = (text) => {
  if (!text) return '';
  
  return text
    .replace(/[^0-9.,€\s]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 20)
    .trim();
};


const sanitizeOpeningHours = (text) => {
  if (!text) return '';
  
  return text
    .replace(/[<>]/g, '')
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 100)
    .trim();
};

const APP_VERSION = packageJson.version;

const App = () => {
  const [lang, setLang] = useState(() => getCurrentLanguage() || 'de');
  const [scr, setScr] = useState('welcome');
  const [city, setCity] = useState('');
  const cityRef = React.useRef('');
  const [cityIn, setCityIn] = useState('');
  const [sel, setSel] = useState(null);
  const [hoveredToilet, setHoveredToilet] = useState(null); 
  const [view, setView] = useState('map');
  const [rRat, setRRat] = useState(0);
  const [rCom, setRCom] = useState('');
  const [uPho, setUPho] = useState([]);
  const [hRat, setHRat] = useState(0);
  const [uRev, setURev] = useState([]);
  const [tData, setTData] = useState({});
  const [loading, setLoading] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [sq, setSq] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); 
  const [searchMode, setSearchMode] = useState('toilet'); 
  const [showCS, setShowCS] = useState(false);
  const [filterAccessible, setFilterAccessible] = useState(false);
  const [filterFree, setFilterFree] = useState(false);
  const [showWS, setShowWS] = useState(false);
  const [detailCitySuggestions, setDetailCitySuggestions] = useState([]);
  const [searchingDetailCities, setSearchingDetailCities] = useState(false);
  const [sNam, setSNam] = useState('');
  const [sAdd, setSAdd] = useState('');
  const [sRat, setSRat] = useState(0);
  const [sCom, setSCom] = useState('');
  const [sPho, setSPho] = useState([]);
  const [sHRat, setSHRat] = useState(0);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddresses, setSearchingAddresses] = useState(false);
  const [addressInputFocused, setAddressInputFocused] = useState(false);
  const [sCost, setSCost] = useState('');
  const [sCostAmount, setSCostAmount] = useState('');
  const [sAccessible, setSAccessible] = useState('');
  const [sOpeningHours, setSOpeningHours] = useState('');
  const [sAmenities, setSAmenities] = useState([]);
  const [clRea, setClRea] = useState('');
  const [clCom, setClCom] = useState('');
  const [clRep, setClRep] = useState({});
  const [reportCategory, setReportCategory] = useState('');
  const [reportPhotos, setReportPhotos] = useState([]); 
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [visited, setVisited] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success', duration) => {
    setToast({ message, type });
    const ms = duration ?? (type === 'error' ? 4000 : 3000);
    setTimeout(() => setToast(null), ms);
  };

  const [mapCenter, setMapCenter] = useState(null);  
  const [mapZoom, setMapZoom] = useState(null);
  const addressInputRef = useRef(null);
  const [sCoordinates, setSCoordinates] = useState(null);
  const [sCity, setSCity] = useState(null);
  const [currentFunFact, setCurrentFunFact] = useState('');
  const [mapError, setMapError] = useState(false);

  // Map Bounds für Performance-Optimierung
  const [mapBounds, setMapBounds] = useState(null);
  const MAX_TOILETS_DISPLAY = 150;

  // Stadt-Wechsel bei Zoom in andere Region
  const [suggestedCity, setSuggestedCity] = useState(null);
  const [showCitySwitch, setShowCitySwitch] = useState(false);
  const [lastCitySwitchPrompt, setLastCitySwitchPrompt] = useState(0);

  // Admin-Login States
  const [adminClickCount, setAdminClickCount] = useState(0);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
// eslint-disable-next-line no-unused-vars
const [_adminUser, setAdminUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin-Dashboard States
  const [pendingToilets, setPendingToilets] = useState([]);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [allReports, setAllReports] = useState([]);
  const [adminPreviewToilet, setAdminPreviewToilet] = useState(null);  

  // Admin-Tabs & Bearbeitung
  const [adminTab, setAdminTab] = useState('pending');
  const [pendingCount, setPendingCount] = useState(0);
  const [photosCount, setPhotosCount] = useState(0);
  const [reportsCount, setReportsCount] = useState(0);
  const [commentReports, setCommentReports] = useState([]);
  const [totalToiletsCount, setTotalToiletsCount] = useState(0);
  const [commentReportsCount, setCommentReportsCount] = useState(0);
  const [recentReviews, setRecentReviews] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [editToiletId, setEditToiletId] = useState(null); 
  const [editMode, setEditMode] = useState(false);
  const [profileEditName, setProfileEditName] = useState('');

// States für Bereinigungstool
  const [cleaningResults, setCleaningResults] = useState([]);
  const [selectedCleanIds, setSelectedCleanIds] = useState([]);
  const [loadingCleaning, setLoadingCleaning] = useState(false);
    // Netiquette States
  const [showNetiquetteModal, setShowNetiquetteModal] = useState(false);
  const [netiquetteAccepted, setNetiquetteAccepted] = useState(false);
  const [pendingPhotoFiles, setPendingPhotoFiles] = useState(null);
  const [pendingPhotoContext, setPendingPhotoContext] = useState(null);
  
  // Offline-States
const [networkOnline, setNetworkOnline] = useState(true);
const [offlineQueue, setOfflineQueue] = useState([]);
// 🎉 Achievement-Popup States
const [achievementPopup, setAchievementPopup] = useState(null);
const [achievementQueue, setAchievementQueue] = useState([]);
const [isProcessingAchievement, setIsProcessingAchievement] = useState(false);
const [isFirebaseReady, setIsFirebaseReady] = useState(false);
// 🧭 Navigation Disclaimer States
const [showNavigationDisclaimer, setShowNavigationDisclaimer] = useState(false);
const [pendingNavigationToilet, setPendingNavigationToilet] = useState(null);
const [navigationDisclaimerDisabled, setNavigationDisclaimerDisabled] = useState(false);

// 🌍 Language States
const [showLanguageSelection, setShowLanguageSelection] = useState(false);

// 🎉 April Fools States
const isAprilFools = new Date().getMonth() === 3 && new Date().getDate() === 1;
const aprilFoolsKey = `aprilFools_${new Date().getFullYear()}_seen`;
const [showAprilModal, setShowAprilModal] = useState(() => {
  if (!isAprilFools) return false;
  return !localStorage.getItem(aprilFoolsKey);
});
const [showAprilReveal, setShowAprilReveal] = useState(false);
const [southPoleMode, setSouthPoleMode] = useState(false);
const effectiveAprilFools = isAprilFools;

// Navigation Disclaimer Einstellung aus localStorage laden
useEffect(() => {
  const disclaimerDisabled = localStorage.getItem('navigationDisclaimerDisabled');
  if (disclaimerDisabled === 'true') {
    setNavigationDisclaimerDisabled(true);
  }
}, []);

// 🌍 Language Management - Sprache beim Start laden
useEffect(() => {
  const isNative = Capacitor.isNativePlatform();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (!hasLanguageBeenSet()) {
    setShowLanguageSelection(true);
  } else {
    const savedLang = getCurrentLanguage();
    if (savedLang) {
      setLang(savedLang);
    }
    // appDownload immer zeigen wenn mobiler Browser (nicht native App)
    if (!isNative && isMobile) {
      setScr('appDownload');
    }
  }
}, []);

// 🏅 Beta Tester Badge - Markiere User die vor Release dabei waren
useEffect(() => {
  const releaseDate = new Date('2026-01-22T00:00:00');
  const now = new Date();

  if (now < releaseDate) {
    const alreadyMarked = localStorage.getItem('restroomadvisor_beta_tester');
    if (!alreadyMarked) {
      localStorage.setItem('restroomadvisor_beta_tester', 'true');
    }
 }
}, []);

// 🌍 Language Change Handler
useEffect(() => {
  if (lang) {
    setLanguage(lang);
  }
}, [lang]);

// 🌍 Language Selection Handler
const handleLanguageSelect = (selectedLang) => {
  setLang(selectedLang);
  setLanguage(selectedLang);
  setShowLanguageSelection(false);
  
  const isNative = Capacitor.isNativePlatform();
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  
  if (!isNative && isMobile) {
    setScr('appDownload');
  }
};

// Handler für App-Download-Screen
  const handleSkipAppDownload = () => {
    setScr('main');
    setView('map');
  };

  const handleOpenPlayStore = () => {
    window.open('https://play.google.com/store/apps/details?id=de.restroomadvisor.app', '_blank');
    setScr('main');
    setView('map');
  };

useEffect(() => {
  if (achievementQueue.length === 0 || isProcessingAchievement || achievementPopup !== null) {
      return;
    }
    setIsProcessingAchievement(true);
    
    const nextAchievement = achievementQueue[0];
    setAchievementQueue(prev => prev.slice(1));
    setAchievementPopup(nextAchievement);
    setTimeout(() => {
      setAchievementPopup(null);
      setTimeout(() => {
        setIsProcessingAchievement(false);
      }, 500);
    }, 4000);
    
  }, [achievementQueue, achievementPopup, isProcessingAchievement]);

 // 🎉 Achievement-Check bei allen relevanten Änderungen
useEffect(() => {
  if (!user) return;

  const prevAchievementsStr = localStorage.getItem('restroomadvisor_prev_achievements');
  let previousAchievements = [];
  
  if (prevAchievementsStr) {
    try {
      previousAchievements = JSON.parse(prevAchievementsStr);
    } catch (e) {
      console.error('Fehler beim Parsen der vorherigen Achievements:', e);
    }
  }
  
  // Aktuelle Achievements berechnen
const currentAchievements = getAchievements();
  
  // ALLE neu freigeschalteten Achievements finden 
  const newlyUnlockedList = currentAchievements.filter((ach, idx) => {
    const wasUnlocked = previousAchievements[idx]?.unlocked || false;
    const isNowUnlocked = ach.unlocked;
    return isNowUnlocked && !wasUnlocked;
  });
  
  // Zur Queue hinzufügen 
  if (newlyUnlockedList.length > 0) {
    setAchievementQueue(prev => [...prev, ...newlyUnlockedList]);
  }
  
  localStorage.setItem('restroomadvisor_prev_achievements', JSON.stringify(currentAchievements));
  
}, [checkIns, uRev, user]); // eslint-disable-line react-hooks/exhaustive-deps

// ==========================================
// ANDROID ZURÜCK-BUTTON HANDLER
// ==========================================
useEffect(() => {
  const setupBackButtonListener = async () => {
    try {
      await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        
        // 1. Modals/Overlays zuerst schließen
        if (showNetiquetteModal) {
          setShowNetiquetteModal(false);
          setPendingPhotoFiles(null);
          setPendingPhotoContext(null);
          return;
        }
        
        if (showReportModal) {
          setShowReportModal(false);
          setReportingComment(null);
          setReportReason('');
          return;
        }
        
        if (showAdminLogin) {
          setShowAdminLogin(false);
          setAdminClickCount(0);
          return;
        }
        
        // 2. Achievement-Popup schließen
        if (achievementPopup) {
          setAchievementPopup(null);
          return;
        }
        
        // 3. Dropdowns/Suchvorschläge schließen
        if (showCS) {
          setShowCS(false);
          return;
        }
        
        if (showWS) {
          setShowWS(false);
          return;
        }
        
        // 4. Navigation basierend auf aktuellem Screen
        switch (scr) {
          case 'welcome':
            CapacitorApp.exitApp();
            break;
            
          case 'main':
            setScr('welcome');
            setSq('');
            setSearchQuery('');
            setSel(null);
            break;
            
          case 'detail':
            setSel(null);
            setScr('main');
            break;
            
          case 'review':
            setRRat(0);
            setRCom('');
            setUPho([]);
            setScr('detail');
            break;
            
          case 'suggest':
            if (sNam || sAdd || sPho.length > 0) {
              const confirmLeave = window.confirm(
                translate('backButton.confirmLeaveUnsaved', lang)
              );
              if (confirmLeave) {
                setSNam('');
                setSAdd('');
                setSRat(0);
                setSCom('');
                setSPho([]);
                setSHRat(0);
                setSCost('');
                setSCostAmount('');
                setSAccessible('');
                setSOpeningHours('');
                setSAmenities([]);
                setSCoordinates(null);
                setSCity(null);
                setEditMode(false);
                setEditToiletId(null);
                setScr('main');
              }
            } else {
              setScr('main');
            }
            break;
            
          case 'profile':
            setScr('main');
            break;
            
          case 'admin':
            setScr('profile');
            break;
            
          case 'impressum':
          case 'datenschutz':
          case 'nutzungsbedingungen':
            setScr('welcome');
            break;
            
          default:
            setScr('welcome');
            break;
        }
      });
    } catch (error) {
      console.error('❌ Fehler beim Registrieren des Zurück-Buttons:', error);
    }
  };
  
  // Listener registrieren
  setupBackButtonListener();
  
  // Cleanup beim Unmount - removeAllListeners statt remove()
  return () => {
    CapacitorApp.removeAllListeners('backButton');
  };
}, [
  scr, 
  showNetiquetteModal, 
  showReportModal, 
  showAdminLogin, 
  achievementPopup,
  showCS,
  showWS,
  sNam,
  sAdd,
  sPho,
  lang
]);

// Anonymous Authentication beim App-Start
useEffect(() => {
  const auth = getAuth();

  // Timeout-Schutz: Nach 5 Sekunden App trotzdem starten
  const authTimeout = setTimeout(() => {
    console.warn('⚠️ Firebase Auth Timeout - starte App offline');
    setIsFirebaseReady(true);
  }, 5000);
  
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      try {
        const authPromise = signInAnonymously(auth);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 5000)
        );
        await Promise.race([authPromise, timeoutPromise]);
      } catch (error) {
        console.warn('⚠️ Firebase Auth fehlgeschlagen (offline?):', error.message);
      }
    }
    clearTimeout(authTimeout);
    setIsFirebaseReady(true);
  });
  
  return () => {
    unsubscribe();
    clearTimeout(authTimeout);
  };
}, []);

// Admin-Auth Check beim App-Start
useEffect(() => {
  const unsubscribe = checkAdminAuth(({ isAdmin, user }) => {
      setIsAdmin(isAdmin);
      setAdminUser(user);
      if (isAdmin) {
      }
    });
    
    return () => unsubscribe();
  }, []);

const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('restroomadvisor_user');
    const savedVisited = localStorage.getItem('restroomadvisor_visited');
    const savedReviews = localStorage.getItem('restroomadvisor_reviews');
    const savedCheckIns = localStorage.getItem('restroomadvisor_checkins');
    
    if (savedUser) setUser(JSON.parse(savedUser));
    if (savedVisited) setVisited(JSON.parse(savedVisited));
    if (savedReviews) setURev(JSON.parse(savedReviews));
    if (savedCheckIns) setCheckIns(JSON.parse(savedCheckIns));

    setIsInitialized(true);
  }, []);

  useEffect(() => { if (!isInitialized) return; if (user) localStorage.setItem('restroomadvisor_user', JSON.stringify(user)); }, [user, isInitialized]);
  useEffect(() => { if (!isInitialized) return; localStorage.setItem('restroomadvisor_visited', JSON.stringify(visited)); }, [visited, isInitialized]);
  useEffect(() => { 
    if (!isInitialized) return;
    const reviewsWithoutPhotos = uRev.map(r => ({ ...r, photos: [] }));
    localStorage.setItem('restroomadvisor_reviews', JSON.stringify(reviewsWithoutPhotos)); 
  }, [uRev, isInitialized]);
  useEffect(() => { if (!isInitialized) return; localStorage.setItem('restroomadvisor_checkins', JSON.stringify(checkIns)); }, [checkIns, isInitialized]);
  
  // LETZTE STADT SPEICHERN (für Offline-Modus)
useEffect(() => {
    if (city && city !== '') {
      localStorage.setItem('restroomadvisor_last_city', city);
    }
  }, [city]);

  useEffect(() => {
    cityRef.current = city;
  }, [city]);
  
  // LETZTE STADT BEIM START LADEN (wenn offline)
  useEffect(() => {
    const checkOfflineCity = () => {
      if (!navigator.onLine) {
        const lastCity = localStorage.getItem('restroomadvisor_last_city');
        
        if (lastCity) {

          if (!user || !user.name) {
            const savedUser = localStorage.getItem('restroomadvisor_user');
            if (savedUser) {
              const parsedUser = JSON.parse(savedUser);
              setUser(parsedUser);
            } else {
              const demoUser = {
                id: 'offline_user',
                name: 'Offline Nutzer',
                createdAt: new Date().toISOString().split('T')[0],
                email: null
              };
              setUser(demoUser);
            }
          }

          setScr('main');
          setCity(lastCity);
          setCityIn('');

          setTimeout(() => {
            showToast(translate('toasts.offlineCityLoaded', lang).replace('{city}', lastCity), 'info');
          }, 100);
        }
      }
    };
    
    setTimeout(checkOfflineCity, 200);  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================
  // 🌐 NETWORK STATUS LISTENER
  // ==========================================
  useEffect(() => {
    setThemeColor(scr === 'admin' ? '#dc2626' : '#f59e0b');
  }, [scr, isAdmin]);
 
  // ==========================================
  // 🔥 FIREBASE REALTIME LISTENERS FÜR ADMIN
  // ==========================================
  useEffect(() => {
    if (!isAdmin) return; 

    let unsubscribePending = null;
    let unsubscribeReports = null;
    let unsubscribeCommentReports = null;
    let unsubscribePhotos = null;
    let unsubscribeReviews = null;

    const setupListeners = async () => {
      try {
        const { collection, query, where, onSnapshot } = await import('firebase/firestore');

        // Listener für Pending Toiletten
        const pendingQuery = query(collection(db, 'toilets'), where('isPending', '==', true));
        unsubscribePending = onSnapshot(pendingQuery, (snapshot) => {
          const pending = [];
          snapshot.forEach((doc) => {
            pending.push({ id: doc.id, ...doc.data() });
          });
          setPendingToilets(pending);
          setPendingCount(pending.length);
        });

        // Listener für Pending Photos - OPTIMIERT mit isPendingPhotos Flag
        const photosQuery = query(
          collection(db, 'toilets'),
          where('isPendingPhotos', '==', true) 
        );

        unsubscribePhotos = onSnapshot(photosQuery, (snapshot) => {  
          
          let count = 0;
          snapshot.forEach((doc) => {
            const data = doc.data();

            if (data.images && Array.isArray(data.images)) {
              count += data.images.filter(img => 
                typeof img === 'object' && img.status === 'pending'  
              ).length;
            }
            
            if (data.comments && Array.isArray(data.comments)) {
              data.comments.forEach(comment => {
                if (comment.photos && Array.isArray(comment.photos)) {
                  count += comment.photos.filter(photo => 
                    typeof photo === 'object' && photo.status === 'pending'
                  ).length;
                }
              });
            }
          });
          
          setPhotosCount(count);
        });

        // Listener für Closure Reports
        const reportsQuery = collection(db, 'reports');
        unsubscribeReports = onSnapshot(reportsQuery, (snapshot) => {
          const reports = [];
          snapshot.forEach((doc) => {
            reports.push({ id: doc.id, ...doc.data() });
          });
          setAllReports(reports);
          setReportsCount(reports.length);
        });

        // Listener für Comment Reports
        const commentReportsQuery = collection(db, 'commentReports');
        unsubscribeCommentReports = onSnapshot(commentReportsQuery, (snapshot) => {
          const commentReports = [];
          snapshot.forEach((doc) => {
            commentReports.push({ id: doc.id, ...doc.data() });
          });
          setCommentReports(commentReports);
setCommentReportsCount(commentReports.length);
        });

        // Listener für neueste Bewertungen (Admin-Übersicht)
        const { orderBy, limit } = await import('firebase/firestore');
        const reviewsQuery = query(
          collection(db, 'reviews'),
          orderBy('submittedAt', 'desc'),
          limit(5)
        );
        unsubscribeReviews = onSnapshot(reviewsQuery, async (snapshot) => {
          const reviews = [];
          for (const reviewDoc of snapshot.docs) {
            const data = reviewDoc.data();
            let toiletName = data.toiletId || '—';
            let toiletCity = '';
            let toiletDocId = null;
            try {
              const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
              const tRef = firestoreDoc(db, 'toilets', data.toiletId);
              const tSnap = await getDoc(tRef);
              if (tSnap.exists()) {
                const td = tSnap.data();
                toiletName = td.name || td.address || data.toiletId;
                toiletCity = td.city || '';
                toiletDocId = tSnap.id;
              }
            } catch (_e) {}
            reviews.push({ id: reviewDoc.id, ...data, toiletName, toiletCity, toiletDocId });
          }
          setRecentReviews(reviews);
        });

        // Listener für Gesamt-Toiletten 
        const allToiletsQuery = collection(db, 'toilets');
        onSnapshot(allToiletsQuery, (snapshot) => {
          const approvedCount = snapshot.docs.filter(doc => {
            const data = doc.data();
            return data.status === 'approved' || data.isPending === false || !data.isPending;
          }).length;
          
          setTotalToiletsCount(approvedCount);
        });

      } catch (error) {
        console.error('❌ Fehler beim Einrichten der Realtime Listeners:', error);
      }
    };

    setupListeners();

return () => {
  if (unsubscribePending) unsubscribePending();
  if (unsubscribePhotos) unsubscribePhotos(); 
  if (unsubscribeReports) unsubscribeReports();
  if (unsubscribeCommentReports) unsubscribeCommentReports();
  if (unsubscribeReviews) unsubscribeReviews();
};
  }, [isAdmin]);

  // Koordinaten formatieren
  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return 'Koordinaten fehlen';
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
  };

  // Hilfsfunktion für Kostenanzeige mit automatischem Euro-Zeichen
  const formatCostAmount = (amount) => {
    if (!amount) return '';
    const amountStr = String(amount).trim();
    if (amountStr.includes('€') || amountStr.includes('$') || amountStr.includes('£') || amountStr.includes('¥')) {
      return amountStr;
    }
    return `${amountStr} €`;
  };

// Filtert Toiletten basierend auf Kartenausschnitt und Limit
  const filterToiletsInBounds = (toilets, bounds, userLoc) => {
    if (!toilets || toilets.length === 0) return [];

    if (!bounds) {
      if (userLoc) {
        const sorted = sortToiletsByDistance(toilets, userLoc);
        return sorted.slice(0, MAX_TOILETS_DISPLAY);
      }
      return toilets.slice(0, MAX_TOILETS_DISPLAY);
    }
    
    // Mit Bounds: Filtere nur Toiletten im sichtbaren Bereich
    const filtered = toilets.filter(t => {
      const lat = t.location?.lat || t.lat;
      const lng = t.location?.lng || t.lng;
      
      if (!lat || !lng) return false;
      
      return lat >= bounds.south && 
             lat <= bounds.north && 
             lng >= bounds.west && 
             lng <= bounds.east;
    });
    
    // Limitiere auch gefilterte Liste auf MAX_TOILETS_DISPLAY
    if (filtered.length > MAX_TOILETS_DISPLAY) {
      if (userLoc) {
        const sorted = sortToiletsByDistance(filtered, userLoc);
        return sorted.slice(0, MAX_TOILETS_DISPLAY);
      }
      return filtered.slice(0, MAX_TOILETS_DISPLAY);
    }
    
    return filtered;
  };

  // Toiletten-Namen mit Nummerierung bei Duplikaten
  const getToiletDisplayName = (toilet, allToilets) => {
    if (!toilet || !toilet.name) return 'Unbenannt';
    
    // Alle Toiletten mit gleichem Namen finden
    const sameName = allToilets.filter(t => t.name === toilet.name);
    
    // Wenn mehrere mit gleichem Namen existieren, Nummer hinzufügen
    if (sameName.length > 1) {
      const index = sameName.findIndex(t => t.id === toilet.id);
      return `${toilet.name} #${index + 1}`;
    }
    
    return toilet.name;
  };

// Prüfe ob Text auf Deutsch ist
const isGerman = (text) => {
  if (!text) return true;
  
  const germanWords = ['toilette', 'wc', 'öffentlich', 'bahnhof', 'stadt', 'straße', 'platz', 'der', 'die', 'das', 'am', 'im', 'und', 'bei', 'öffnungszeiten'];
  const englishWords = ['toilet', 'restroom', 'bathroom', 'public', 'open', 'during', 'hours', 'center', 'station', 'at'];
  const otherWords = ['sanitaire', 'toilettes', 'bagno', 'toaleta', 'servizi'];
  
  const lowerText = text.toLowerCase();
  
  const germanMatches = germanWords.filter(w => lowerText.includes(w)).length;
  const englishMatches = englishWords.filter(w => lowerText.includes(w)).length;
  const otherMatches = otherWords.filter(w => lowerText.includes(w)).length;
  
  if ((englishMatches > 0 || otherMatches > 0) && germanMatches === 0) return false;
  
  return true;
};

  // Öffnungszeiten aus Namen extrahieren
  const extractOpeningHours = (name) => {
    if (!name) return null;
    
    const patterns = [
      /Öffnungszeiten?:?\s+([^.]+)/i,
      
      /\(([^()]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7|täglich|Uhr|uhr|h)[^()]*)\)/i,
      
      /\[([^[\]]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7|täglich)[^[\]]*)\]/i,
      
      /\{([^{}]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7)[^{}]*)\}/i
    ];
    
    for (const pattern of patterns) {
      const match = name.match(pattern);
      if (match) {
        let extracted = match[1].trim();
        
        if (extracted.length <= 2) continue;
        
        // ERWEITERTE BLACKLIST 
        const blacklist = [
          /^i\.\s*H\.?$/i,
          /^Stadt\s+\w+$/i,
          /^Café\s+\w+$/i,
          /^Restaurant\s+\w+$/i,
          /^Sanitärcontainer$/i,
          /^Toilette$/i,
          /^WC$/i,
          /^n$/i,
          
          /^(der|die|das|des|den|dem)\s+\w+\s+Open/i, 
          /^(der|die|das|des|den|dem)\s+\w+$/i,       
          
          /^der\s+.*City.*Open/i,
          /^des\s+Centers?$/i,  
          
          /^der\s+Toiletten\s+nur\s+während/i,
          /^während\s+der\s+Betriebszeit/i,
          
          /^Wildpark$/i,
          /^Tourist\s*Info$/i,
          /^Lentia\s*City$/i,
          /^im\s+Kellergeschoss$/i,
          
          /^Behindertentoilette\s+im/i,
          /^Aufzug\s+vorhanden/i,
          /^Zugang\s+mit/i,
        ];
        
        // Prüfe Blacklist
        const isBlacklisted = blacklist.some(regex => regex.test(extracted));
        if (isBlacklisted) continue;

        const hasTimeRelated = 
          /\d/.test(extracted) ||  
          /(Mo|Di|Mi|Do|Fr|Sa|So)/i.test(extracted) || 
          /24\s*\/\s*7/i.test(extracted) ||  
          /(täglich|daily)/i.test(extracted) ||  
          /(Uhr|uhr|h)\b/i.test(extracted);  
        
        if (!hasTimeRelated) continue;
        
        // Wenn es mit "der/des/den" startet UND sehr lang ist UND keine klaren Zeiten hat
        if (/^(der|des|den)\s+/i.test(extracted)) {
          const hasConcreteTime = 
            /\d{1,2}\s*[:.-]\s*\d{2}/.test(extracted) ||  
            /\d{1,2}\s*-\s*\d{1,2}/.test(extracted) ||   
            /(Mo|Di|Mi|Do|Fr|Sa|So)\s*-/.test(extracted); 
          
          if (!hasConcreteTime && extracted.length > 30) {
            continue;  
          }
        }
        
        // Spezialfall: Sehr lange Texte mit Beschreibungen
        if (extracted.length > 100 && /Behindertentoilette|Kellergeschoss|Aufzug|Euroschlüssel/i.test(extracted)) {
          const timeMatch = extracted.match(/(Mo|Di|Mi|Do|Fr|Sa|So)[^.]*\d{1,2}:\d{2}[^.]*/i);
          if (timeMatch) {
            extracted = timeMatch[0].trim();
          } else {
            continue;
          }
        }
        
        return extracted;
      }
    }
    
    return null;
  };

  // Namen von Öffnungszeiten bereinigen
  const cleanNameFromOpeningHours = (name) => {
    if (!name) return name;
    
    let cleaned = name;
    
    // 1. Entferne "Öffnungszeiten:" mit allem danach bis Punkt
    cleaned = cleaned.replace(/Öffnungszeiten?:?\s+[^.]+/gi, '');
    
    // 2. Entferne Klammern mit Zeit-Indikatoren
    cleaned = cleaned.replace(/\s*\(([^()]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7|täglich|Uhr|uhr|h)[^()]*)\)/gi, '');
    
    // 3. Eckige Klammern
    cleaned = cleaned.replace(/\s*\[([^[\]]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7|täglich)[^[\]]*)\]/gi, '');
    
    // 4. Geschweifte Klammern
    cleaned = cleaned.replace(/\s*\{([^{}]*(?:\d|Mo|Di|Mi|Do|Fr|Sa|So|24\/7)[^{}]*)\}/gi, '');
    
    // 5. Entferne "zu den Öffnungszeiten" am Ende
    cleaned = cleaned.replace(/\s+zu\s+(?:den\s+)?Öffnungszeiten\.?$/i, '');
    
    // 6. Entferne "Zu Öffnungszeiten des/der..." am Anfang
    cleaned = cleaned.replace(/^Zu\s+Öffnungszeiten\s+(des|der)\s+\w+:\s*/i, '');
    
    // 7. Entferne "Open during..." Phrasen
    cleaned = cleaned.replace(/Open\s+during\s+open\s+hours\s+of\s+[\w\s]+/gi, '');
    
    // Mehrfache Leerzeichen und Doppelpunkte am Ende entfernen
    cleaned = cleaned.replace(/\s+/g, ' ').replace(/:\s*$/, '').trim();
    
    return cleaned;
  };

  // Öffnungszeiten parsen und Status prüfen
  const parseOpeningHours = (hoursString) => {
    if (!hoursString) return null;
    
    const hours = hoursString.toLowerCase();
    
    // 24/7 Check
    if (hours.includes('24/7') || hours.includes('24 stunden') || hours.includes('durchgehend')) {
      return { type: '24/7', isOpen: true, display: '24/7' };
    }
    
    // Aktuelle Zeit
    const now = new Date();
    const currentDay = now.getDay(); 
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;
    
    // Tage-Mapping
    const dayMap = { 'mo': 1, 'di': 2, 'mi': 3, 'do': 4, 'fr': 5, 'sa': 6, 'so': 0 };
    
    // Zeitmuster: 6-22, 06:00-22:00, etc.
    const timeMatch = hours.match(/(\d{1,2})(?::(\d{2}))?[:\s-]+(\d{1,2})(?::(\d{2}))?/);
    if (!timeMatch) {
      return { type: 'unknown', isOpen: null, display: hoursString };
    }
    
    const startHour = parseInt(timeMatch[1]);
    const startMin = parseInt(timeMatch[2] || '0');
    const endHour = parseInt(timeMatch[3]);
    const endMin = parseInt(timeMatch[4] || '0');
    
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    // Tage prüfen (z.B. "Mo-Fr")
    let isCorrectDay = true;
    const dayRangeMatch = hours.match(/([a-z]{2})-([a-z]{2})/);
    if (dayRangeMatch) {
      const startDay = dayMap[dayRangeMatch[1]];
      const endDay = dayMap[dayRangeMatch[2]];
      
      if (startDay !== undefined && endDay !== undefined) {
        if (startDay <= endDay) {
          isCorrectDay = currentDay >= startDay && currentDay <= endDay;
        } else {
          isCorrectDay = currentDay >= startDay || currentDay <= endDay;
        }
      }
    }
    
    // Zeitfenster prüfen
    const isCorrectTime = currentTime >= startTime && currentTime <= endTime;
    const isOpen = isCorrectDay && isCorrectTime;
    
    // Display-Format
    let display = hoursString;
    if (timeMatch) {
      const startStr = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
      const endStr = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
      display = `${startStr}-${endStr}h`;
      
      if (dayRangeMatch) {
        display = `${dayRangeMatch[1].charAt(0).toUpperCase()}${dayRangeMatch[1].slice(1)}-${dayRangeMatch[2].charAt(0).toUpperCase()}${dayRangeMatch[2].slice(1)} ${display}`;
      }
    }
    
    return { type: 'parsed', isOpen, display };
  };

  // Öffnungsstatus für Toilette abrufen
  const getOpeningStatus = (toilet) => {
    const hoursString = toilet.openingHours || extractOpeningHours(toilet.name);
    return hoursString ? parseOpeningHours(hoursString) : null;
  };

useEffect(() => {
  if (city && scr === 'main' && isFirebaseReady) {
    loadToiletsFromFirebase();
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [city, scr, isFirebaseReady]);
const isSyncing = React.useRef(false);

const syncOfflineQueue = React.useCallback(async (currentCity) => {
  if (isSyncing.current) {
    return;
  }
  isSyncing.current = true;
  const cityToUse = currentCity || cityRef.current;
  const queue = getOfflineQueue();
  if (queue.length === 0) {
    isSyncing.current = false;
    return;
  }

  setOfflineQueue(queue);

  for (const action of queue) {
    try {
      switch (action.type) {
        case 'addToilet':
          await addToilet(action.data);
          break;
        case 'updateToilet':
          await updateToilet(action.toiletId, action.data);
          break;
        case 'addReview': {
          // Fotos (Base64) erst hochladen, dann Review speichern
          let uploadedPhotoURLs = [];
          if (action.photos && action.photos.length > 0) {
            for (const photoData of action.photos) {
              if (typeof photoData === 'string' && photoData.startsWith('data:')) {
                try {
                  const response = await fetch(photoData);
                  const blob = await response.blob();
                  const file = new File([blob], `offline_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
                  const url = await uploadPhoto(file, action.toiletId);
                  uploadedPhotoURLs.push(url);
                } catch (photoErr) {
                  console.error('❌ Foto-Upload beim Sync fehlgeschlagen:', photoErr);
                }
              } else if (typeof photoData === 'string' && photoData.startsWith('http')) {
                uploadedPhotoURLs.push(photoData);
              }
            }
          }

          const reviewData = { ...(action.data || {}) };
          if (uploadedPhotoURLs.length > 0) {
            reviewData.photos = uploadedPhotoURLs.map(url => ({
              url,
              status: 'pending',
              uploadedAt: new Date().toISOString()
            }));
          }

          await addDoc(collection(db, 'reviews'), {
            id: action.reviewId,
            toiletId: action.toiletId,
            ...reviewData,
            status: 'pending',
            submittedAt: action.submittedAt || new Date().toISOString()
          });

          // Toilette aktualisieren: Review in comments Array einfügen
          const toiletRef = doc(db, 'toilets', action.toiletId);
          const toiletSnap = await getDoc(toiletRef);
          if (toiletSnap.exists()) {
            const toiletData = toiletSnap.data();
            const existingComments = toiletData.comments || [];
            const existingIndex = existingComments.findIndex(
              c => c.user === reviewData.user
            );
            let updatedComments;
            let newReviewCount;
            if (existingIndex !== -1) {
              updatedComments = [...existingComments];
              updatedComments[existingIndex] = reviewData;
              newReviewCount = toiletData.reviews || 0;
            } else {
              updatedComments = [reviewData, ...existingComments];
              newReviewCount = (toiletData.reviews || 0) + 1;
            }
            const totalRating = updatedComments.reduce((sum, c) => sum + (c.rating || 0), 0);
            const newRating = updatedComments.length > 0 
              ? Math.round((totalRating / updatedComments.length) * 10) / 10 
              : 0;
            await updateDoc(toiletRef, {
              comments: updatedComments,
              reviews: newReviewCount,
              rating: newRating
            });
          }
          break;
        }
        default:
          console.warn('Unbekannter Action-Type:', action.type);
      }

removeFromOfflineQueue(action.id);
    } catch (error) {
      console.error(`❌ Fehler bei Sync von ${action.type}:`, error.message, error.code);
    }
  }

  setOfflineQueue(getOfflineQueue());

  if (getOfflineQueue().length === 0) {
    setToast({ message: translate('toasts.allChangesSynced', lang), type: 'success' });
    setTimeout(() => setToast(null), 3000);
    // Toiletten neu laden damit Reviews in UI erscheinen
if (cityToUse) {
      try {
        const fetchPromise = getToiletsByCity(cityToUse);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('RELOAD_TIMEOUT')), 8000)
        );
        const fresh = await Promise.race([fetchPromise, timeoutPromise]);
        if (fresh.length > 0) {
          cacheToiletsByCity(city, fresh);
          setTData(prevData => ({
              ...prevData,
              [cityToUse]: fresh
            }));
          // uRev mit Firestore-Comments abgleichen (gelöschte Reviews entfernen)
          setURev(prev => prev.filter(localRev => {
            const toilet = fresh.find(t => t.id === localRev.toiletId);
            if (!toilet) return true;
            return (toilet.comments || []).some(c => c.user === localRev.user && c.date === localRev.date);
          }));
          setVisited(prev => prev.filter(localVis => {
            const toilet = fresh.find(t => t.id === localVis.toiletId);
            if (!toilet) return true;
            return (toilet.comments || []).length > 0;
          }));
          setCheckIns(prev => prev.filter(localCI => {
            const toilet = fresh.find(t => t.id === localCI.toiletId);
            if (!toilet) return true;
            return (toilet.comments || []).length > 0;
          }));
          // sel aktualisieren falls Detailansicht offen ist
          setSel(prevSel => {
            if (!prevSel) return prevSel;
            const updatedToilet = fresh.find(t => t.id === prevSel.id);
            return updatedToilet || prevSel;
          });
        }
      } catch (e) {
        console.error('❌ Reload nach Sync fehlgeschlagen:', e.message);
      }
    }
  }
  isSyncing.current = false;
}, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending Toiletten, Reports und Photos für Admin laden
useEffect(() => {
  if (isAdmin) {
      loadPendingToilets();
      loadAllReports();
      loadCommentReports();
      loadPendingPhotos();
      loadRecentReviews();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [isAdmin]);

    // Toiletten-Anzahl vorladen wenn Stadt-Vorschläge kommen
  useEffect(() => {
    if (citySuggestions.length > 0) {
      preloadToiletCounts(citySuggestions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySuggestions]);

  // Toiletten-Anzahl vorladen für Detail-Vorschläge
  useEffect(() => {
    if (detailCitySuggestions.length > 0) {
      preloadToiletCounts(detailCitySuggestions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailCitySuggestions]);

  useEffect(() => {
    if (isAdmin && adminTab === 'photos') {
      loadPendingPhotos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, isAdmin]);

  // Funktion zum Laden der Toiletten
const loadToiletsFromFirebase = async () => {
  setLoading(true);
  
  try {
    const cached = getCachedToiletsByCity(city);
    
    if (cached && cached.length > 0) {
      setTData(prevData => ({
        ...prevData,
        [city]: cached
      }));
      
      // ✅ Sofort anzeigen, nicht auf Background-Update warten
      setLoading(false);

      if (networkOnline) {
        try {
          const fetchPromise = getToiletsByCity(city);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('TIMEOUT')), 8000)
          );
          const fresh = await Promise.race([fetchPromise, timeoutPromise]);
          if (fresh.length > 0) {
            cacheToiletsByCity(city, fresh);
            setTData(prevData => ({
              ...prevData,
              [city]: fresh
            }));
            setURev(prev => prev.filter(localRev => {
              const toilet = fresh.find(t => t.id === localRev.toiletId);
              if (!toilet) return true;
              return (toilet.comments || []).some(c => c.user === localRev.user && c.date === localRev.date);
            }));
            setVisited(prev => prev.filter(localVis => {
              const toilet = fresh.find(t => t.id === localVis.toiletId);
              if (!toilet) return true;
              return (toilet.comments || []).length > 0;
            }));
            setCheckIns(prev => prev.filter(localCI => {
              const toilet = fresh.find(t => t.id === localCI.toiletId);
              if (!toilet) return true;
              return (toilet.comments || []).length > 0;
            }));
          }
        } catch (bgError) {
          if (bgError.message === 'TIMEOUT') {
            setNetworkOnline(false);
          }
        }
      } else {
        showToast(translate('toasts.offlineCacheLoaded', lang).replace('{count}', cached.length), 'info');
      }
      
    } else {
      if (!networkOnline) {
        throw new Error(translate('toasts.noInternetNoCache', lang));
      }
      
      const fetchPromise = getToiletsByCity(city);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), 8000)
      );
      const toilets = await Promise.race([fetchPromise, timeoutPromise]);

      cacheToiletsByCity(city, toilets);
      
      setTData(prevData => ({
        ...prevData,
        [city]: toilets
      }));
      // uRev mit Firestore-Comments abgleichen (gelöschte Reviews entfernen)
      setURev(prev => prev.filter(localRev => {
        const toilet = toilets.find(t => t.id === localRev.toiletId);
        if (!toilet) return true;
        return (toilet.comments || []).some(c => c.user === localRev.user && c.date === localRev.date);
      }));
      setVisited(prev => prev.filter(localVis => {
        const toilet = toilets.find(t => t.id === localVis.toiletId);
        if (!toilet) return true;
        return (toilet.comments || []).length > 0;
      }));
      setCheckIns(prev => prev.filter(localCI => {
        const toilet = toilets.find(t => t.id === localCI.toiletId);
        if (!toilet) return true;
        return (toilet.comments || []).length > 0;
      }));
    }
    
  } catch (error) {
    console.error('❌ Fehler beim Laden:', error);
    
    // OFFLINE-ERKENNUNG: Bei Netzwerkfehlern
    const isNetworkError = 
      error.constructor.name === 'TypeError' ||
      (error.message && (
        error.message.includes('NetworkError') ||
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('Failed to fetch') ||
        error.message.toLowerCase().includes('cors')
      ));
    
    if (isNetworkError) {
      setNetworkOnline(false);
    }
    
    const errorMsg = !networkOnline
      ? translate('toasts.noInternetNoCache', lang)
      : error.message && error.message.includes('Keine Berechtigung')
      ? translate('toasts.noPermission', lang)
      : translate('toasts.loadError', lang).replace('{city}', city);
   
  showToast(errorMsg, 'error', 5000);
  } finally {
    setLoading(false);
  }
};

  const preloadToiletCounts = async (cities) => {
    const citiesToLoad = cities.filter(cityData => !tData[cityData.name]);
    
    if (citiesToLoad.length === 0) return;

    const loadPromises = citiesToLoad.map(async (cityData) => {
      try {
        const toilets = await getToiletsByCity(cityData.name);
        return { city: cityData.name, toilets };
      } catch (error) {
        console.error(`❌ Fehler beim Laden von ${cityData.name}:`, error);
        return { city: cityData.name, toilets: [] };
      }
    });
    
    const results = await Promise.all(loadPromises);

    setTData(prevData => {
      const newData = { ...prevData };
      results.forEach(({ city, toilets }) => {
        newData[city] = toilets;
      });
      return newData;
    });
  };

  // Funktion zum Laden der Pending Toiletten 
  const loadPendingToilets = async () => {
  try {
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const q = query(collection(db, 'toilets'), where('isPending', '==', true));
    const querySnapshot = await getDocs(q);
    
    const pending = [];
    querySnapshot.forEach((doc) => {
      pending.push({ id: doc.id, ...doc.data() });
    });
    
    setPendingToilets(pending);
    setPendingCount(pending.length); 
} catch (error) {
    console.error('❌ Fehler beim Laden der Pending-Toiletten:', error);
  }
  };

// Letzte 5 Bewertungen für Admin-Übersicht laden
  const loadRecentReviews = async () => {
    try {
      const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
      const { doc, getDoc } = await import('firebase/firestore');
      const q = query(
        collection(db, 'reviews'),
        orderBy('submittedAt', 'desc'),
        limit(5)
      );
      const snapshot = await getDocs(q);
      const reviews = [];
      for (const reviewDoc of snapshot.docs) {
        const data = reviewDoc.data();
        // Toiletten-Name + Stadt nachladen
        let toiletName = data.toiletId || '—';
        let toiletCity = '';
        let toiletDocId = null;
        try {
          // Zuerst per Dokument-ID versuchen
          const tRef = doc(db, 'toilets', data.toiletId);
          const tSnap = await getDoc(tRef);
          if (tSnap.exists()) {
            const td = tSnap.data();
            toiletName = td.name || td.address || data.toiletId;
            toiletCity = td.city || '';
            toiletDocId = tSnap.id;
          }
        } catch (_e) {
          // Toilette nicht gefunden — ID als Fallback
        }
        reviews.push({ 
          id: reviewDoc.id, 
          ...data, 
          toiletName, 
          toiletCity,
          toiletDocId 
        });
      }
      setRecentReviews(reviews);
    } catch (error) {
      console.error('❌ Fehler beim Laden der letzten Bewertungen:', error);
    }
  };

// Foto genehmigen (+ Review wenn Foto-Only)
  const approvePhoto = async (toiletId, photoIndex, fromComment, commentIndex) => {
    setLoadingAdmin(true);
    try {
      // 0. Guard: Prüfen ob Foto noch wirklich pending ist (verhindert Doppelfreigabe)
      const freshToiletRef = doc(db, 'toilets', toiletId);
      const freshSnap = await getDoc(freshToiletRef);
      if (freshSnap.exists()) {
        const freshData = freshSnap.data();
        let currentStatus = null;
        if (fromComment) {
          const comment = (freshData.comments || [])[commentIndex];
          const photo = (comment?.photos || [])[photoIndex];
          currentStatus = typeof photo === 'object' ? photo?.status : null;
        } else {
          const img = (freshData.images || [])[photoIndex];
          currentStatus = typeof img === 'object' ? img?.status : null;
        }
        if (currentStatus === 'approved') {
          showToast('Foto wurde bereits freigegeben', 'error');
          await loadPendingPhotos();
          setLoadingAdmin(false);
          return;
        }
      }

      // 1. Pending-Foto aus DB holen
      const photosQuery = query(
        collection(db, 'pendingPhotos'),
        where('toiletId', '==', toiletId)
      );
      const photosSnapshot = await getDocs(photosQuery);

      let approvedPhotoData = null;
      let reviewId = null;

      // 2. Richtiges Foto finden
      for (const doc of photosSnapshot.docs) {
        const data = doc.data();
        
        if (fromComment) {
          if (data.commentIndex === commentIndex && data.photoIndex === photoIndex) {
            approvedPhotoData = data;
            reviewId = data.reviewId;
            await deleteDoc(doc.ref);
            break;
          }
        } else {
          if (data.photoIndex === photoIndex && !data.fromComment) {
            approvedPhotoData = data;
            reviewId = data.reviewId;
            await deleteDoc(doc.ref);
            break;
          }
        }
      }

      if (!approvedPhotoData) {
      }

      // 3. Foto zu Toilette hinzufügen
      let toiletQuery = query(
        collection(db, 'toilets'),
        where('id', '==', toiletId)
      );
      let toiletSnapshot = await getDocs(toiletQuery);
      
      // Falls nicht gefunden: Alle Toiletten laden
      if (toiletSnapshot.empty) {
        const toiletRef = doc(db, 'toilets', toiletId);
        const toiletDoc = await getDoc(toiletRef);

        if (!toiletDoc.exists()) {
          console.error('❌ Toilette nicht gefunden:', toiletId);
          showToast('Toilette nicht gefunden', 'error');
          setLoadingAdmin(false);
          return;
        }

        const toilet = { id: toiletDoc.id, ...toiletDoc.data() };
        
        if (!toilet) {
          console.error('❌ Toilette nicht gefunden:', toiletId);
          showToast('Toilette nicht gefunden', 'error');
          setLoadingAdmin(false);
          return;
        }
        
        // Direktes Update über updateToilet Helper
        if (fromComment) {
          const comments = toilet.comments || [];
          if (comments[commentIndex]) {
            const comment = comments[commentIndex];
            const photos = comment.photos || [];
            
            if (photos[photoIndex]) {
              photos[photoIndex] = {
                ...photos[photoIndex],
                status: 'approved'
              };
            }
            
            comment.photos = photos;
            comments[commentIndex] = comment;

            const hasPendingInImages = (toilet.images || []).some(img => 
              typeof img === 'object' && img.status === 'pending'
            );
            const hasPendingInComments = (toilet.comments || []).some((c, idx) => 
              idx !== commentIndex && c.photos && c.photos.some(p => 
                typeof p === 'object' && p.status === 'pending'
              )
            );
            
            await updateToilet(toiletId, { 
              comments, 
              isPendingPhotos: hasPendingInImages || hasPendingInComments
            });
          }
        } else {
          const images = toilet.images || [];
          
          if (images[photoIndex]) {
            images[photoIndex] = {
              ...images[photoIndex],
              status: 'approved',
              approvedBy: 'Admin',
              approvedAt: new Date().toISOString()
            };
            
            // Prüfe ob noch pending Photos in anderen Images oder Comments existieren
            const hasPendingInImages = images.some((img, idx) => 
              idx !== photoIndex && typeof img === 'object' && img.status === 'pending'
            );
            const hasPendingInComments = (toilet.comments || []).some(c => 
              c.photos && c.photos.some(p => 
                typeof p === 'object' && p.status === 'pending'
              )
            );
            
            await updateToilet(toiletId, { 
              images, 
              isPendingPhotos: hasPendingInImages || hasPendingInComments
            });
          } else {
            console.error('❌ Foto an Index nicht gefunden:', photoIndex);
          }
        }        
        // Review genehmigen bei Foto-Only
        if (reviewId) {
          try {
            const reviewQuery = query(
              collection(db, 'reviews'),
              where('id', '==', reviewId),
              where('status', '==', 'pending')
            );
            const reviewSnapshot = await getDocs(reviewQuery);
            
            if (!reviewSnapshot.empty) {
              const reviewDoc = reviewSnapshot.docs[0];
              const reviewData = reviewDoc.data();
              
              if (reviewData.isPhotoOnly) {
                await updateDoc(reviewDoc.ref, { status: 'approved' });
              }
            }
          } catch (reviewError) {
            console.error('⚠️ Review konnte nicht genehmigt werden:', reviewError);
          }
        }
        
        showToast('Foto freigegeben!', 'success');
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadPendingPhotos();
        setLoadingAdmin(false);
        return;
      }
      
      if (!toiletSnapshot.empty) {
        const toiletDoc = toiletSnapshot.docs[0];
        const toiletData = toiletDoc.data();
        
        if (fromComment) {
          const comments = toiletData.comments || [];
          if (comments[commentIndex]) {
            const comment = comments[commentIndex];
            const photos = comment.photos || [];
            
            // Status von pending → approved
            if (photos[photoIndex]) {
              photos[photoIndex] = {
                ...photos[photoIndex],
                status: 'approved'
              };
            }
            
            comment.photos = photos;
            comments[commentIndex] = comment;
            
            // Prüfe ob noch pending Photos in Images oder anderen Comments existieren
            const hasPendingInImages = (toiletData.images || []).some(img => 
              typeof img === 'object' && img.status === 'pending'
            );
            const hasPendingInComments = comments.some((c, idx) => 
              idx !== commentIndex && c.photos && c.photos.some(p => 
                typeof p === 'object' && p.status === 'pending'
              )
            );
            
            await updateDoc(toiletDoc.ref, { 
              comments,
              isPendingPhotos: hasPendingInImages || hasPendingInComments
            });
          }
        } else {
          const currentPhotos = toiletData.photos || [];
          // Prüfe ob noch pending Photos existieren
          const hasPendingInImages = (toiletData.images || []).some(img => 
            typeof img === 'object' && img.status === 'pending'
          );
          const hasPendingInComments = (toiletData.comments || []).some(c => 
            c.photos && c.photos.some(p => 
              typeof p === 'object' && p.status === 'pending'
            )
          );
          
          await updateDoc(toiletDoc.ref, {
            photos: [...currentPhotos, {
              url: approvedPhotoData.url,
              uploadedBy: approvedPhotoData.uploadedBy,
              uploadedAt: approvedPhotoData.uploadedAt
            }],
            isPendingPhotos: hasPendingInImages || hasPendingInComments
          });
        }
      }

      // 4. Review auch genehmigen bei Foto-Only Reviews
      if (reviewId) {
        try {
          const reviewQuery = query(
            collection(db, 'reviews'),
            where('id', '==', reviewId),
            where('status', '==', 'pending')
          );
          const reviewSnapshot = await getDocs(reviewQuery);
          
          if (!reviewSnapshot.empty) {
            const reviewDoc = reviewSnapshot.docs[0];
            const reviewData = reviewDoc.data();
            
            // Nur bei Foto-Only Reviews (rating === 0)
            if (reviewData.isPhotoOnly) {
              await updateDoc(reviewDoc.ref, { status: 'approved' });
            }
          }
        } catch (reviewError) {
          console.error('⚠️ Review konnte nicht genehmigt werden:', reviewError);
        }
      }

      showToast('Foto freigegeben!', 'success');
      await loadPendingPhotos();
      
    } catch (error) {
      console.error('❌ Fehler beim Freigeben:', error);
      showToast('Fehler beim Freigeben', 'error');
    }
    setLoadingAdmin(false);
  };

  // Foto ablehnen
  const rejectPhoto = async (toiletId, photoIndex, fromComment, commentIndex) => {
    setLoadingAdmin(true);
    try {
      
      let deletedReviewId = null;
      let foundInPendingPhotos = false;
      
      // 1. VERSUCH: Aus pendingPhotos Collection löschen (NEUES SYSTEM)
      const photosQuery = query(
        collection(db, 'pendingPhotos'),
        where('toiletId', '==', toiletId)
      );
      const photosSnapshot = await getDocs(photosQuery);
      
      for (const doc of photosSnapshot.docs) {
        const data = doc.data();
        
        if (fromComment) {
          if (data.commentIndex === commentIndex && data.photoIndex === photoIndex) {
            deletedReviewId = data.reviewId;
            await deleteDoc(doc.ref);
            foundInPendingPhotos = true;
            break;
          }
        } else {
          if (data.photoIndex === photoIndex && !data.fromComment) {
            deletedReviewId = data.reviewId;
            await deleteDoc(doc.ref);
            foundInPendingPhotos = true;
            break;
          }
        }
      }

      // 2. Foto aus Toilette entfernen (ALTES + NEUES SYSTEM)
      // Versuche zuerst mit 'id' Feld
      let toiletQuery = query(
        collection(db, 'toilets'),
        where('id', '==', toiletId)
      );
      let toiletSnapshot = await getDocs(toiletQuery);
      
      // Falls nicht gefunden: Alle Toiletten laden und filtern
      if (toiletSnapshot.empty) {
        const allToiletsFromDB = await getAllToilets();
        const toilet = allToiletsFromDB.find(t => t.id === toiletId);
        
        if (!toilet) {
          console.error('❌ Toilette nicht gefunden:', toiletId);
          showToast('Toilette nicht gefunden', 'error');
          setLoadingAdmin(false);
          return;
        }
        
        // Direktes Update über updateToilet Helper
        if (fromComment) {
          const comments = toilet.comments || [];
          if (comments[commentIndex]) {
            const comment = comments[commentIndex];
            const photos = comment.photos || [];
            photos.splice(photoIndex, 1);
            comment.photos = photos;
            comments[commentIndex] = comment;
            
            await updateToilet(toiletId, { comments, isPendingPhotos: false });
          }
        } else {
          const images = toilet.images || [];
          
          if (images[photoIndex]) {
            images.splice(photoIndex, 1);
            // Prüfe ob noch andere pending Photos existieren
            const hasPendingPhotos = images.some((img, idx) => 
              idx !== photoIndex && typeof img === 'object' && img.status === 'pending'
            );
            
            await updateToilet(toiletId, { 
              images, 
              isPendingPhotos: hasPendingPhotos 
            });
          }
        }
        
        showToast('Foto abgelehnt', 'success');
        await loadPendingPhotos();
        setLoadingAdmin(false);
        return;
      }
      
      if (!toiletSnapshot.empty) {
        const toiletDoc = toiletSnapshot.docs[0];
        const toiletData = toiletDoc.data();
        
        if (fromComment) {
          // Review-Foto: Aus Comment entfernen
          const comments = toiletData.comments || [];
          if (comments[commentIndex]) {
            const comment = comments[commentIndex];
            const photos = comment.photos || [];
            
            // Foto entfernen
            photos.splice(photoIndex, 1);
            comment.photos = photos;
            comments[commentIndex] = comment;
            
            await updateDoc(toiletDoc.ref, { 
              comments,
              isPendingPhotos: false 
            });
          }
        } else {
          // Haupt-Foto: Aus images entfernen
          const images = toiletData.images || [];

          if (images[photoIndex]) {
            images.splice(photoIndex, 1);
            
            await updateDoc(toiletDoc.ref, { 
              images,
              isPendingPhotos: false 
            });
          }
        }
      }

      // 3. Review auch ablehnen bei Foto-Only Reviews (nur wenn in pendingPhotos gefunden)
      if (deletedReviewId && foundInPendingPhotos) {
        try {
          const reviewQuery = query(
            collection(db, 'reviews'),
            where('id', '==', deletedReviewId),
            where('status', '==', 'pending')
          );
          const reviewSnapshot = await getDocs(reviewQuery);
          
          if (!reviewSnapshot.empty) {
            const reviewDoc = reviewSnapshot.docs[0];
            const reviewData = reviewDoc.data();
            
            if (reviewData.isPhotoOnly) {
              await deleteDoc(reviewDoc.ref);
            }
          }
        } catch (reviewError) {
          console.error('⚠️ Review konnte nicht gelöscht werden:', reviewError);
        }
      }

      showToast('Foto abgelehnt', 'success');
      await loadPendingPhotos();
      
    } catch (error) {
      console.error('❌ Fehler beim Ablehnen:', error);
      showToast('Fehler beim Ablehnen', 'error');
    }
    setLoadingAdmin(false);
  };

const loadPendingPhotos = async () => {
  setLoadingAdmin(true);
  try {
    
    const { getPendingPhotosOnly } = await import('./firebaseHelpers');
    const photosWithContext = await getPendingPhotosOnly();
    
    const processedUrls = new Set();
    const uniquePhotos = photosWithContext.filter(photo => {
      if (processedUrls.has(photo.url)) {
        return false;
      }
      processedUrls.add(photo.url);
      return true;
    });
    
    setPendingPhotos(uniquePhotos);
    setPhotosCount(uniquePhotos.length);
    
  } catch (error) {
    console.error('❌ Fehler beim Laden der pending Fotos:', error);
  showToast('Fehler beim Laden der Fotos', 'error');
  }
  setLoadingAdmin(false);
};

// Funktion zum Laden aller Reports 
const loadAllReports = async () => {
  try {
    const reports = await getAllReports();
    setAllReports(reports);
    setReportsCount(reports.length); 
  } catch (error) {
    console.error('❌ Fehler beim Laden der Reports:', error);
  }
};

// Funktion zum Laden aller Kommentar-Reports 
const loadCommentReports = async () => {
    try {
      const reports = await getAllCommentReports();
      setCommentReports(reports);
      setCommentReportsCount(reports.length);
    } catch (error) {
      console.error('❌ Fehler beim Laden der Kommentar-Reports:', error);
    }
  };

  // Kommentar melden 
  const handleReportComment = (toiletId, commentIndex, comment) => {
    setReportingComment({ toiletId, commentIndex, comment });
    setReportReason('');
    setShowReportModal(true);
  };

  // Report absenden
  const submitCommentReport = async () => {
    if (!reportingComment || !reportReason) return;
    
    setLoading(true);
    try {
      await reportComment(
        reportingComment.toiletId,
        reportingComment.commentIndex,
        reportingComment.comment,
        reportReason,
        user?.name || translate('common.anonymousUser', lang)
      );
      showToast(translate('toasts.commentReportSuccess', lang), 'success');
      setShowReportModal(false);
      setReportingComment(null);
      setReportReason('');
    } catch (error) {
      console.error('❌ Fehler beim Melden:', error);
      showToast(translate('toasts.commentReportError', lang), 'error');
    }
    setLoading(false);
  };

  // Kommentar löschen und Report entfernen
  const handleDeleteReportedComment = async (report) => {
    if (!window.confirm(`Kommentar von "${report.commentUser}" wirklich löschen?`)) return;
    
    setLoadingAdmin(true);
    try {
      // 1. Kommentar aus der Toilette löschen
      await deleteReview(report.toiletId, report.commentIndex, report.commentUser);
      
      // 2. Report löschen
      await resolveCommentReport(report.id);
      
      // 3. Alle weiteren Reports für diesen Kommentar löschen
      await deleteReportsForComment(report.toiletId, report.commentIndex);
      
      // 4. UI aktualisieren
      await loadCommentReports();
      
      showToast('✅ Kommentar gelöscht und Report bearbeitet!', 'success');
    } catch (error) {
      console.error('❌ Fehler:', error);
      showToast('Fehler beim Löschen des Kommentars.', 'error');
    }
    setLoadingAdmin(false);
  };

  // Report ignorieren 
  const handleIgnoreReport = async (report) => {
    if (!window.confirm('Report ignorieren? Der Kommentar bleibt bestehen.')) return;
    
    setLoadingAdmin(true);
    try {
      await resolveCommentReport(report.id);
      await loadCommentReports();
      showToast('✅ Report als unbegründet markiert.', 'success');
    } catch (error) {
      console.error('❌ Fehler:', error);
      showToast('Fehler beim Bearbeiten des Reports.', 'error');
    }
    setLoadingAdmin(false);
  };

  // ==========================================
  // BEREINIGUNGSTOOL-FUNKTIONEN (ALLE STÄDTE)
  // ==========================================
  const loadCleaningPreview = async () => {
  setLoadingCleaning(true);
  try {
    const rejectedIds = JSON.parse(localStorage.getItem('restroomadvisor_rejectedQualityIssues') || '[]');
    
    const allToiletsFromDB = await getAllToilets();
    
    const results = [];
    const citiesFound = new Set();
    
    for (const toilet of allToiletsFromDB) {
      const isApproved = toilet.status === 'approved' || toilet.isPending === false;
      if (!isApproved) continue;
     if (rejectedIds.includes(toilet.id)) {
        continue;
      }
      
      const issues = [];
      let issueType = null;
      let suggestion = null;
      let canAutoFix = false;
      
      const extracted = extractOpeningHours(toilet.name);
      if (extracted) {
        issues.push('🕐 Öffnungszeiten im Titel');
        issueType = 'openingHours';
        suggestion = cleanNameFromOpeningHours(toilet.name);
        canAutoFix = true;
      }

      if (!extracted) {
        if (toilet.name && toilet.name.length > 60) {
          issues.push(`📏 Zu lang (${toilet.name.length} Zeichen)`);
          if (!issueType) issueType = 'tooLong';
        }

        if (toilet.name && !isGerman(toilet.name)) {
          issues.push('🌍 Nicht auf Deutsch');
          if (!issueType) issueType = 'notGerman';
        }

        if (toilet.name) {
          const specialChars = (toilet.name.match(/[^a-zA-ZäöüÄÖÜß0-9\s\-(),/.&]/g) || []).length;
          if (specialChars > 3) {
            issues.push(`🔣 Viele Sonderzeichen (${specialChars})`);
            if (!issueType) issueType = 'specialChars';
          }
        }
      }
      
      if (issues.length > 0) {
        results.push({
          id: toilet.id,
          oldName: toilet.name,
          newName: suggestion || toilet.name,
          openingHours: extracted || '-',
          city: toilet.city || 'Unbekannt',
          issues: issues,
          issueType: issueType,
          issueCount: issues.length,
          canAutoFix: canAutoFix
        });
        citiesFound.add(toilet.city || 'Unbekannt');
      }
    }
    
    results.sort((a, b) => b.issueCount - a.issueCount);
      
    setCleaningResults(results);
    
    showToast(`✅ ${results.length} auffällige Einträge gefunden!`, 'success', 5000);
  } catch (error) {
    console.error('❌ Fehler:', error);
  showToast('Fehler beim Laden: ' + error.message, 'error', 5000);
  }
  setLoadingCleaning(false);
};

  const confirmSingleCleaning = async (toiletId) => {
    try {
      const result = cleaningResults.find(r => r.id === toiletId);
      if (!result) return;
      
      const { extractAndCleanToiletData } = await import('./firebaseHelpers');
      await extractAndCleanToiletData(toiletId, result.oldName);

      setCleaningResults(prev => prev.filter(r => r.id !== toiletId));
      setSelectedCleanIds(prev => prev.filter(id => id !== toiletId));
      
      showToast(`✅ ${result.newName} bereinigt!`, 'success');
    } catch (error) {
      console.error('❌ Fehler:', error);
      showToast('Fehler beim Bereinigen: ' + error.message, 'error');
    }
  };

  const confirmSelectedCleanings = async () => {
    if (selectedCleanIds.length === 0) return;
    
    if (!window.confirm(`${selectedCleanIds.length} Toiletten bereinigen?`)) return;
    
    setLoadingCleaning(true);
    try {
      const { extractAndCleanToiletData } = await import('./firebaseHelpers');
      
      let success = 0;
      let failed = 0;
      
      for (const id of selectedCleanIds) {
        const result = cleaningResults.find(r => r.id === id);
        if (!result) continue;
        
        try {
          await extractAndCleanToiletData(id, result.oldName);
          success++;
        } catch (error) {
          failed++;
          console.error(`❌ Fehler bei ${id}:`, error);
        }
      }
      
      // Bereinigte aus Liste entfernen
      setCleaningResults(prev => prev.filter(r => !selectedCleanIds.includes(r.id)));
      setSelectedCleanIds([]);
      
      showToast(`✅ ${success} bereinigt, ${failed} Fehler`, success > 0 ? 'success' : 'error', 5000);
    } catch (error) {
      console.error('❌ Fehler:', error);
      showToast('Fehler beim Bereinigen: ' + error.message, 'error');
    }
    setLoadingCleaning(false);
  };

  // Alle Bereinigungen bestätigen
  const confirmAllCleanings = async () => {
    if (cleaningResults.length === 0) return;
    
    const citiesCount = new Set(cleaningResults.map(r => r.city)).size;
    
    if (!window.confirm(
      `⚠️ ACHTUNG: Alle ${cleaningResults.length} Toiletten aus ${citiesCount} Städten bereinigen?\n\n` +
      `Dies kann nicht rückgängig gemacht werden!`
    )) return;
    
    setLoadingCleaning(true);
    try {
      const { extractAndCleanToiletData } = await import('./firebaseHelpers');
      
      let success = 0;
      let failed = 0;
      const total = cleaningResults.length;
      
      for (const result of cleaningResults) {
        try {
          await extractAndCleanToiletData(result.id, result.oldName);
          success++;

          if (success % 10 === 0) {
            setToast({ 
              message: `🔄 Bereinige... ${success}/${total} (${Math.round(success/total*100)}%)`, 
              type: 'success' 
            });
          }
        } catch (error) {
          failed++;
          console.error(`❌ Fehler bei ${result.id}:`, error);
        }
      }
      
      setCleaningResults([]);
      setSelectedCleanIds([]);
      
      showToast(`✅ Fertig! ${success} bereinigt, ${failed} Fehler`, success > 0 ? 'success' : 'error', 8000);
    } catch (error) {
      console.error('❌ Fehler:', error);
      showToast('Fehler beim Bereinigen: ' + error.message, 'error');
    }
    setLoadingCleaning(false);
  };

  // ========================================
  // PHASE 2: FEEDBACK-SYSTEM
  // ========================================

  const rejectCleaning = async (toiletId) => {
    try {
     const rejectedIds = JSON.parse(localStorage.getItem('restroomadvisor_rejectedQualityIssues') || '[]');
     if (!rejectedIds.includes(toiletId)) {
       rejectedIds.push(toiletId);
       localStorage.setItem('restroomadvisor_rejectedQualityIssues', JSON.stringify(rejectedIds));
     }
     const result = cleaningResults.find(r => r.id === toiletId);
      if (!result) return;
      if (!result) return;
      
      const feedbackData = {
        toiletId: result.id,
        originalName: result.oldName,
        extractedHours: result.openingHours,
        newName: result.newName,
        city: result.city,
        decision: 'rejected',
        timestamp: new Date().toISOString(),
        pattern: result.openingHours.toLowerCase()
      };

      const { db } = await import('./firebase');
      const { collection, addDoc } = await import('firebase/firestore');
      await addDoc(collection(db, 'cleaning-feedback'), feedbackData);

      // setRejectionFeedback(prev => [...prev, feedbackData]);

      setCleaningResults(prev => prev.filter(r => r.id !== toiletId));
      setSelectedCleanIds(prev => prev.filter(id => id !== toiletId));
      
      showToast('✅ Dauerhaft übersprungen - wird nicht mehr angezeigt', 'success');

    } catch (error) {
      console.error('❌ Fehler beim Speichern:', error);
      showToast('Fehler beim Speichern des Feedbacks', 'error');
      // setRejectedToiletIds(prev => [...prev, toiletId]);
    }
  };

  useEffect(() => {
    const searchCitiesWithDelay = async () => {
      if (scr === 'welcome' && cityIn && cityIn.length >= 3) {
        setSearchingCities(true);
        try {
          const cities = await searchCities(cityIn);
          setCitySuggestions(cities);
        } catch (error) {
          console.error('Fehler bei Städte-Suche:', error);
          setCitySuggestions([]);
        }
        setSearchingCities(false);
      } else {
        setCitySuggestions([]);
      }
    };

    const timer = setTimeout(searchCitiesWithDelay, 500);
    return () => clearTimeout(timer);
  }, [cityIn, scr]); 

// Geocoding-Hook für Detail-Seite Städtesuche
  useEffect(() => {
    const searchDetailCitiesWithDelay = async () => {
      if ((scr === 'main' || scr === 'detail') && searchMode === 'city' && searchQuery && searchQuery.length >= 3) {
        setSearchingDetailCities(true);
        try {
          const cities = await searchCities(sq);
          setDetailCitySuggestions(cities);
        } catch (error) {
          console.error('Fehler bei Detail-Städte-Suche:', error);
          setDetailCitySuggestions([]);
        }
        setSearchingDetailCities(false);
      } else {
        setDetailCitySuggestions([]);
      }
    };
    
    const timer = setTimeout(searchDetailCitiesWithDelay, 500);
    return () => clearTimeout(timer);
}, [searchQuery, searchMode, scr, sq]);

useEffect(() => {
    if (scr === 'welcome') {
      setCurrentFunFact(getRandomFunFact(lang));
    }
  }, [scr, lang]);

  useEffect(() => {
    const searchAddressesWithDelay = async () => {
      if (scr === 'suggest' && sAdd && sAdd.length >= 3 && addressInputFocused) {
        setSearchingAddresses(true);
        try {
          const { searchAddresses } = await import('./geocoding');
          const addresses = await searchAddresses(sAdd, city);
          setAddressSuggestions(addresses);
        } catch (error) {
          console.error('Fehler bei Adress-Suche:', error);
          setAddressSuggestions([]);
        }
        setSearchingAddresses(false);
      } else {
        setAddressSuggestions([]);
        setSearchingAddresses(false);
      }
    };

    const timer = setTimeout(searchAddressesWithDelay, 300);
    return () => clearTimeout(timer);
  }, [sAdd, scr, city, addressInputFocused]);

  useEffect(() => {
    setNetworkOnline(isOnline());

    const cleanup = setupNetworkListeners(
      () => {
        setNetworkOnline(true);
        setToast({ message: translate('toasts.backOnline', lang), type: 'success' });
        
        syncOfflineQueue(city);
       
        setTimeout(() => setToast(null), 3000);
      },
      () => {
        setNetworkOnline(false);
        showToast(translate('toasts.offlineMode', lang), 'warning');
      }
    );

return cleanup;
  }, [lang, syncOfflineQueue, city]);

  // Automatisch auf Stadt-Suche umschalten bei Kartenansicht
  useEffect(() => {
    if (scr === 'main' && window.innerWidth < 640 && view === 'map' && searchMode === 'toilet') {
      setSearchMode('city');
      setShowCS(false);
    }
  }, [view, scr, searchMode]);

// Platzhalter-Bilder für beide Sprachen
  const phDE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23e5e7eb;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23d1d5db;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='400' fill='url(%23grad)'/%3E%3Ctext x='200' y='180' font-size='100' text-anchor='middle' opacity='0.5'%3E📷%3C/text%3E%3Ctext x='200' y='250' font-size='22' text-anchor='middle' fill='%234b5563' font-family='Arial' font-weight='600'%3EKeine Fotos vorhanden%3C/text%3E%3Ctext x='200' y='285' font-size='18' text-anchor='middle' fill='%236b7280' font-family='Arial'%3EZeig's uns! 💩%3C/text%3E%3C/svg%3E";
  
  const phEN = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23e5e7eb;stop-opacity:1' /%3E%3Cstop offset='100%25' style='stop-color:%23d1d5db;stop-opacity:1' /%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='400' height='400' fill='url(%23grad)'/%3E%3Ctext x='200' y='180' font-size='100' text-anchor='middle' opacity='0.5'%3E📷%3C/text%3E%3Ctext x='200' y='250' font-size='22' text-anchor='middle' fill='%234b5563' font-family='Arial' font-weight='600'%3ENo photos available%3C/text%3E%3Ctext x='200' y='285' font-size='18' text-anchor='middle' fill='%236b7280' font-family='Arial'%3ESnap the throne! 👑💩%3C/text%3E%3C/svg%3E";
  
  const ph = lang === 'en' ? phEN : phDE;

// ===== NAVIGATION HANDLER (MIT DISCLAIMER) =====
const handleOpenNavigation = (toilet) => {
  if (!toilet || !toilet.lat || !toilet.lng) {
    showToast(translate('toasts.coordinatesUnavailable', lang), 'error');
    return;
  }

  // Prüfen ob Disclaimer deaktiviert ist
  if (!navigationDisclaimerDisabled) {
    setPendingNavigationToilet(toilet);
    setShowNavigationDisclaimer(true);
    return;
  }

  // Disclaimer wurde deaktiviert - direkt navigieren
  executeNavigation(toilet);
};

// ===== NAVIGATION AUSFÜHREN =====
const executeNavigation = (toilet) => {
  const lat = toilet.lat;
  const lng = toilet.lng;
  const name = encodeURIComponent(toilet.name || 'Toilette');

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  
  // ANDROID
  if (isAndroid) {
    let geoUrl;
    
    if (userLocation) {
      geoUrl = `geo:${userLocation.lat},${userLocation.lng}?q=${lat},${lng}(${name})`;
    } else {
      geoUrl = `geo:${lat},${lng}?q=${lat},${lng}(${name})`;
    }
    
    // Versuche geo: URL zu öffnen
    try {
      window.location.href = geoUrl;
      showToast(translate('toasts.navigationStarted', lang).replace('{name}', toilet.name), 'success');
    } catch (error) {
      console.error('Navigation error:', error);
      // Bei Fehler direkt Google Maps öffnen
      const fallbackUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
      window.open(fallbackUrl, '_blank');
      showToast(translate('toasts.navigationStarted', lang).replace('{name}', toilet.name), 'success');
    }
    
    return;
  }
  
  // iOS
  if (isIOS) {
    const appleMapsUrl = `maps://maps.apple.com/?q=${lat},${lng}&saddr=${userLocation?.lat || ''},${userLocation?.lng || ''}&daddr=${lat},${lng}`;
    
    try {
      window.location.href = appleMapsUrl;
      showToast(translate('toasts.navigationStarted', lang).replace('{name}', toilet.name), 'success');
    } catch (error) {
      console.error('Navigation error:', error);
      // Bei Fehler direkt Google Maps öffnen
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;
      window.open(googleMapsUrl, '_blank');
      showToast(translate('toasts.navigationStarted', lang).replace('{name}', toilet.name), 'success');
    }
    
    return;
  }
  
  // DESKTOP/WEB
  const googleMapsUrl = userLocation
    ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${lat},${lng}&travelmode=walking`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=walking`;

  window.open(googleMapsUrl, '_blank');
  showToast(translate('toasts.navigationStarted', lang).replace('{name}', toilet.name), 'success');
};

// ===== NAVIGATION DISCLAIMER BESTÄTIGEN =====
const confirmNavigation = (dontShowAgain) => {
  if (dontShowAgain) {
    setNavigationDisclaimerDisabled(true);
    localStorage.setItem('navigationDisclaimerDisabled', 'true');
  }
  
  setShowNavigationDisclaimer(false);
  
  if (pendingNavigationToilet) {
    executeNavigation(pendingNavigationToilet);
    setPendingNavigationToilet(null);
  }
};

// ===== NAVIGATION DISCLAIMER ABBRECHEN =====
const cancelNavigation = () => {
  setShowNavigationDisclaimer(false);
  setPendingNavigationToilet(null);
};

  const handlePhUp = async (e) => {
  const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
  
  if (files.length === 0) return;
  
  if (!sel || !sel.id) {
      showToast(translate('toasts.noToiletSelected', lang), 'error');
      return;
    }

  const netiquetteAcceptedBefore = localStorage.getItem('restroomadvisor_netiquette_accepted');
  
  if (!netiquetteAcceptedBefore) {
    setPendingPhotoFiles(files);
    setPendingPhotoContext('review');
    setShowNetiquetteModal(true);
    e.target.value = '';
    return;
  }
  
  setLoading(true);
  try {
    const uploadPromises = files.map(f => uploadPhoto(f, sel.id));
    const photoURLs = await Promise.all(uploadPromises);
    
    setUPho(p => [...p, ...photoURLs]);
    
    const currentCount = parseInt(localStorage.getItem('restroomadvisor_photo_count') || '0');
    localStorage.setItem('restroomadvisor_photo_count', String(currentCount + photoURLs.length));
    
    showToast(
      translate('toasts.uploadSuccess', lang).replace('{count}', photoURLs.length),
      'success'
    );
    
  } catch (error) {
    console.error('❌ Fehler beim Upload:', error);
    showToast(translate('toasts.uploadError', lang), 'error');
  }
  
  setLoading(false);
  e.target.value = '';
};

const handleSubRev = async () => {
    if (rRat === 0 && uPho.length === 0) {
      showToast('Bitte Sterne vergeben oder Fotos hochladen!', 'error');
      return;
    }
    
  if (!sel || !sel.id) {
      showToast(translate('toasts.noToiletSelected', lang), 'error');
      return;
    }
    
    setLoading(true);
      try {
        let uploadedPhotoURLs = [];
        let failedUploads = 0;

        if (uPho && uPho.length > 0) {
        
        for (let i = 0; i < uPho.length; i++) {
          try {
            const photoData = uPho[i];

            if (photoData.startsWith('http')) {
              uploadedPhotoURLs.push(photoData);
              continue;
            }
            
            if (!networkOnline) {
              console.warn(`⚠️ Offline - Foto ${i + 1} wird übersprungen`);
              failedUploads++;
              continue;
            }
           
            const response = await fetch(photoData);
            const blob = await response.blob();
            const file = new File([blob], `review_photo_${i}.jpg`, { type: 'image/jpeg' });

            const uploadPromise = uploadPhoto(file, sel.id);
            const uploadTimeout = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('UPLOAD_TIMEOUT')), 10000)
            );

            const url = await Promise.race([uploadPromise, uploadTimeout]);
            uploadedPhotoURLs.push(url);
            
          } catch (uploadError) {
            console.error(`❌ Foto-Upload ${i + 1} fehlgeschlagen:`, uploadError);
            failedUploads++;
            if (uploadError.message === 'UPLOAD_TIMEOUT') {
              setNetworkOnline(false);
              break; // Weitere Uploads abbrechen, direkt in Queue
            }
          }
        }
        
      if (failedUploads > 0) {
          console.warn(`⚠️ ${failedUploads} von ${uPho.length} Fotos konnten nicht hochgeladen werden`);
        }

      }

      const reviewId = `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const photoObjects = uploadedPhotoURLs.map(url => createPhotoObject(url, {
        status: 'pending',  
        uploadedBy: user?.name || translate('common.demoUser', lang),
        uploadedById: user?.uid || user?.id || 'demo-user',
        uploadedAt: new Date().toISOString(),
        reviewId: reviewId
      }));
      
// Final-Bereinigung des Kommentars vor dem Speichern
      const cleanedComment = sanitizeComment(rCom);
      
// Aktuellen Rank holen (wird bei jedem Submit neu geholt = Update!)
      const currentRank = getSelectedRank();
      
      const nr = { 
        user: user?.name || translate('common.demoUser', lang), 
        userRank: { icon: currentRank.icon, name: currentRank.name }, // Rank speichern/aktualisieren
        rating: rRat,
        text: cleanedComment,
        date: new Date().toISOString().split('T')[0], 
        photos: photoObjects,
        isPhotoOnly: rRat === 0 && photoObjects.length > 0
      };

      // Achievement Tracking
      if (user?.uid || user?.id) {
        const submittedReviews = parseInt(localStorage.getItem(`submittedReviews_${user.uid || user.id}`) || '0');
        localStorage.setItem(`submittedReviews_${user.uid || user.id}`, (submittedReviews + 1).toString());
        setURev(prev => [...prev, { toiletId: sel.id, ...nr }]);
        // checkAchievements(parseInt(localStorage.getItem(`submittedReviews_${user.uid || user.id}`) || '0'));
      }

// Review in Firebase speichern
      if (!networkOnline) {
        // Offline: In Queue speichern (über offlineHelper, korrekter Key)
      addToOfflineQueue({
          type: 'addReview',
          reviewId: reviewId,
          toiletId: sel.id,
          data: nr,
          photos: uPho,
          submittedAt: new Date().toISOString()
        });

        setLoading(false);
        showToast(translate('toasts.reviewSavedOffline', lang), 'info');
        setScr('detail');
        setRRat(0);
        setRCom('');
        setUPho([]);
        return;
        
      } else {
        // Online: Direkt in Firebase schreiben (mit Timeout-Schutz)
        try {
          const writePromise = addDoc(collection(db, 'reviews'), {
            id: reviewId,
            toiletId: sel.id,
            ...nr,
            status: 'pending',
            submittedAt: new Date().toISOString()
          });

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('TIMEOUT')), 8000)
          );

          await Promise.race([writePromise, timeoutPromise]);

        } catch (firebaseError) {
          console.error('❌ Firebase-Speicherung fehlgeschlagen:', firebaseError);
          // Egal ob Timeout oder anderer Fehler: in Offline-Queue retten
          addToOfflineQueue({
            type: 'addReview',
            reviewId: reviewId,
            toiletId: sel.id,
            data: nr,
            photos: uPho,
            submittedAt: new Date().toISOString()
          });
          setNetworkOnline(false);
          showToast(translate('toasts.reviewSavedOffline', lang), 'info');
        }
      }

      const utd = { ...tData };
      const ct = [...(utd[city] || [])];
      const ti = ct.findIndex(t => t.id === sel.id);
      
      if (ti !== -1) {
        const cur = { ...ct[ti] };

        if (!cur.comments || !Array.isArray(cur.comments)) {
          console.warn('⚠️ Keine comments gefunden, erstelle leeres Array');
          cur.comments = [];
        }

        if (!cur.images || !Array.isArray(cur.images)) {
          console.warn('⚠️ Keine images gefunden, erstelle leeres Array');
          cur.images = [];
        }
        
        const eri = cur.comments.findIndex(c => c.user === (user?.name || translate('common.demoUser', lang)));
        let uc, nrc;

        if (eri !== -1) {
          uc = [...cur.comments];
          uc[eri] = nr;
          nrc = cur.reviews;
        } else {
          uc = [nr, ...cur.comments];
          nrc = cur.reviews + 1;
        }

        const ui = [...cur.images];  

        if (photoObjects && photoObjects.length > 0) {
          photoObjects.forEach(photoObj => {
            const urlExists = ui.some(img => {
              const existingUrl = typeof img === 'string' ? img : img.url;
              return existingUrl === photoObj.url;
            });
            
            if (!urlExists) {
              ui.push(photoObj);  
            }
          });
        }

        const commentsWithRating = uc.filter(c => c.rating > 0);
        const tr = commentsWithRating.reduce((s, c) => s + c.rating, 0);
        const nar = commentsWithRating.length > 0 
          ? Math.round((tr / commentsWithRating.length) * 10) / 10 
          : 0;
        
        const updatedToilet = { 
          ...cur, 
          comments: uc, 
          images: ui,  
          reviews: nrc, 
          rating: nar, 
          lastVisited: nr.date 
        };

        // Update mit isPendingPhotos Flag
        const hasNewPendingPhotos = photoObjects && photoObjects.length > 0;
        
        await updateToilet(sel.id, {
          comments: uc,
          images: ui,
          reviews: nrc,
          rating: nar,
          lastVisited: nr.date,
          ...(hasNewPendingPhotos && { isPendingPhotos: true })
        });
        
        if (isAdmin && hasNewPendingPhotos) {
          await loadPendingPhotos();
        }
                
        ct[ti] = updatedToilet;
        utd[city] = ct;
        setSel(updatedToilet);
        setTData(utd);
        
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const isMagicTime = (hours === 11 && minutes === 11) || (hours === 22 && minutes === 22);
        
        if (isMagicTime) {
          const secretUnlocked = localStorage.getItem('restroomadvisor_secret_flusher');
          if (!secretUnlocked) {
            localStorage.setItem('restroomadvisor_secret_flusher', 'true');
          }
        }
        
        const newCheckIn = {
          toiletId: sel.id,
          toiletName: sel.name,
          toiletAddress: sel.address,
          city: city,
          timestamp: new Date().toISOString(),
          date: new Date().toISOString().split('T')[0]
        };
        
        // 🚀 Launch Week Achievement: Speichere erstes Check-In Datum
        if (!localStorage.getItem('restroomadvisor_first_checkin_date')) {
          localStorage.setItem('restroomadvisor_first_checkin_date', new Date().toISOString());
        }
        
        setCheckIns([newCheckIn, ...checkIns]);

        const visitData = {
          toiletId: sel.id, 
          city: city, 
          visitedAt: nr.date, 
          name: sel.name, 
          address: sel.address, 
          rating: updatedToilet.rating 
        };
        
        const existingVisit = visited.findIndex(v => v.toiletId === sel.id);
        if (existingVisit !== -1) {
          const updatedVisited = [...visited];
          updatedVisited[existingVisit] = visitData;
          setVisited(updatedVisited);
        } else {
          setVisited([...visited, visitData]);
        }

        const uri = uRev.findIndex(r => r.toiletId === sel.id);
        const reviewForStorage = { ...nr, photos: [] };
        if (uri !== -1) {
          const uur = [...uRev];
          uur[uri] = { toiletId: sel.id, ...reviewForStorage };
          setURev(uur);
        } else {
          setURev([...uRev, { toiletId: sel.id, ...reviewForStorage }]);
        }
      }
      
      showToast(translate('toasts.reviewSaved', lang), 'success');

      setRRat(0);
      setRCom(''); 
      setUPho([]);
      clearTimeout(window.commentCleanupTimeout);
      setScr('detail');
      
    } catch (error) {
      console.error('❌ Fehler beim Speichern:', error);
      showToast(translate('toasts.reviewSaveError', lang), 'error');
    }
    
    setLoading(false);
  };

  const openRevForm = () => {
    const er = uRev.find(r => r.toiletId === sel.id);
    if (er) { setRRat(er.rating); setRCom(er.text || ''); setUPho(er.photos || []); } 
    else { setRRat(0); setRCom(''); setUPho([]); }
    setScr('review');
  };

const getVis = () => visited.map(v => {
    const allToilets = Object.values(tData).flat();
    const toilet = allToilets.find(t => t.id === v.toiletId);
    // Fallback: Daten direkt aus visited nehmen, auch wenn tData noch nicht geladen
    const base = toilet || {
      id: v.toiletId,
      name: v.name,
      address: v.address,
      lat: v.lat,
      lng: v.lng,
      rating: v.rating,
      images: []
    };
return { ...base, lastVisited: v.visitedAt };
  }).filter(t => t !== null)
    .sort((a, b) => new Date(b.lastVisited) - new Date(a.lastVisited));

const createUser = (name) => {
  const isGuest = name === 'Gast' || name === 'Guest';
  const cleanedName = name
    .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '') 
    .slice(0, 30)
    .trim();
  
  if (!isGuest && cleanedName.length < 2) {
    showToast(translate('toasts.nameTooShort', lang), 'error');
    return;
  }
  
  const savedCreatedAt = localStorage.getItem('restroomadvisor_user_created_at');
  
  const newUser = {
    id: 'user_' + Date.now(),
    name: cleanedName,
    createdAt: savedCreatedAt || new Date().toISOString().split('T')[0], 
    email: null
  };
  setUser(newUser);
  
  if (savedCreatedAt) {
    localStorage.removeItem('restroomadvisor_user_created_at');
  }

    if (city && city !== '') {
      return; 
    }
    
    setScr('welcome');
  };

  const handleMapBoundsChange = (bounds) => {
    setMapBounds(bounds);
  };

  const handleMapCenterChange = async (lat, lng) => {

    try {
      const locationData = await reverseGeocode(lat, lng);

      const newCity = locationData.name || locationData.city;

      if (newCity && newCity !== city && newCity.toLowerCase() !== city.toLowerCase()) {
        setSuggestedCity(newCity);
        

        const dontAskAgain = localStorage.getItem('restroomadvisor_dont_ask_city_switch');
        if (dontAskAgain === 'true') {
          return; 
        }

        const now = Date.now();
        const timeSinceLastPrompt = now - lastCitySwitchPrompt;
        const FIVE_MINUTES = 5 * 60 * 1000;
        
        if (timeSinceLastPrompt > FIVE_MINUTES) {
          setShowCitySwitch(true);
          setLastCitySwitchPrompt(now);
        }

      } else {
        setSuggestedCity(null);
      }
    } catch (error) {
    }
  };
const handleSwitchCity = async () => {
    if (!suggestedCity) return;
    
    setShowCitySwitch(false);
    setLastCitySwitchPrompt(0); 
    setLoading(true);
    
    try {
      const data = await getToiletsByCity(suggestedCity);
      
      if (!data || data.length === 0) {
        showToast(translate('toasts.noToiletsFound', lang).replace('{city}', suggestedCity), 'warning');
        setLoading(false);
        return;
      }

      const newTData = { ...tData, [suggestedCity]: data };
      setTData(newTData);

      // Karte auf neue Stadt zentrieren (aus Geocoding) 
      let cityCoords = null;
      try {
        const cityResult = await searchCities(suggestedCity);
        if (cityResult && cityResult.length > 0) {
          const cityData = cityResult[0];
          if (cityData.lat && cityData.lng) {
            cityCoords = { lat: cityData.lat, lng: cityData.lng };
          }
        }
      } catch (geoError) {
        console.warn('⚠️ Geocoding für Stadt-Wechsel fehlgeschlagen:', geoError);
      }

      setTimeout(() => {
        setUserLocation(null);
        
        // Karte zentrieren falls Koordinaten gefunden
        if (cityCoords) {
          setMapCenter([cityCoords.lat, cityCoords.lng]);
          setMapZoom(12); 
        }
        
        setCity(suggestedCity);
        setScr('main'); 
        setView('map'); 
        setSuggestedCity(null);
        setMapBounds(null); 
        
        showToast(
          translate('toasts.toiletsLoaded', lang)
            .replace('{count}', data.length)
            .replace('{city}', suggestedCity),
          'success'
        );
        
        setLoading(false);
      }, 100);
      
    } catch (error) {
      console.error('❌ Fehler beim Stadt-Wechsel:', error);
      showToast(translate('toasts.error', lang).replace('{message}', error.message), 'error');
      setLoading(false);
    }
};

// 🔧 GPS-Einstellungen öffnen
  const openLocationSettings = async () => {
    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.Location,
        optionIOS: IOSSettings.LocationServices
      });
    } catch (error) {
      console.error('❌ Konnte Einstellungen nicht öffnen:', error);
      showToast(translate('toasts.openSettings', lang), 'info', 5000);
    }
  };

// ===== AUF KARTE ANZEIGEN HANDLER =====
  const handleShowOnMap = (toilet) => {
    
    if (!toilet || !toilet.lat || !toilet.lng) {
      showToast(translate('toasts.coordinatesUnavailable', lang), 'error');
      return;
    }
    
    // Karte auf Toilette zentrieren mit Detail-Zoom
    setMapCenter([toilet.lat, toilet.lng]);
    setMapZoom(17);
    setView('map');
    setScr('main');  
    
    showToast(translate('toasts.mapCentered', lang), 'success');
  };

// GPS-Standort abrufen und Stadt erkennen
  const handleGetLocation = async () => {
    setLoadingGPS(true);
    try {

      const location = await getUserLocation();

      setUserLocation(location);

      setMapCenter([location.lat, location.lng]);
      setMapZoom(16);

      const cityData = await reverseGeocode(location.lat, location.lng);
      
      if (cityData && cityData.name) {

        setCity(cityData.name);
        setCityIn(cityData.name);
        setScr('main');

        showToast(translate('toasts.locationDetected', lang).replace('{city}', cityData.name), 'success');
      } else {
        console.warn('⚠️ Keine Stadt gefunden');

        setNetworkOnline(false);
        
        showToast(translate('toasts.noInternet', lang), 'error');
      }
      
    } catch (error) {
      console.error('❌ GPS-Fehler:', error);
      
      // OFFLINE-ERKENNUNG: Bei Netzwerkfehlern 
      const isNetworkError = 
        error.constructor.name === 'TypeError' ||
        (error.message && (
          error.message.includes('NetworkError') ||
          error.message.includes('fetch') ||
          error.message.includes('network') ||
          error.message.includes('Failed to fetch') ||
          error.message.toLowerCase().includes('cors')
        ));
      
      if (isNetworkError) {
        setNetworkOnline(false);

        showToast(translate('toasts.offlineMode', lang), 'error');
      } else {
        // Prüfe ob Settings-Button angezeigt werden soll
        if (error.canOpenSettings) {
          setToast({ 
            message: error.message || translate('toasts.gpsError', lang),
            type: 'error',
            action: {
              label: translate('toasts.settingsButton', lang),
              onClick: async () => {
                const { openDeviceSettings } = await import('./geolocation');
                await openDeviceSettings();
                setToast(null);
              }
            }
          });
          setTimeout(() => setToast(null), 8000); 
        } else {
        if (error.message && error.message.includes('denied')) {
          setToast({ 
            message: translate('toasts.gpsPermissionDenied', lang), 
            type: 'error',
            action: {
              label: translate('toasts.settingsButton', lang).replace('⚙️ ', ''),
              onClick: openLocationSettings
            }
          });
        } else {
          setToast({ 
            message: error.message || translate('toasts.gpsError', lang), 
            type: 'error' 
          });
        }
        setTimeout(() => setToast(null), 6000);
      }
      }
    }
    
    setLoadingGPS(false);
  };

 // Handler für Netiquette-Modal
  const handleNetiquetteAccept = async () => {
    if (!netiquetteAccepted) {
      showToast(translate('toasts.acceptGuidelines', lang), 'error');
      return;
    }
    
    localStorage.setItem('restroomadvisor_netiquette_accepted', 'true');
    setShowNetiquetteModal(false);
    
    // Upload basierend auf Kontext
    if (pendingPhotoFiles) {
      if (pendingPhotoContext === 'review') {
      setLoading(true);
      try {
        const uploadPromises = pendingPhotoFiles.map(f => uploadPhoto(f, sel.id));
        const photoURLs = await Promise.all(uploadPromises);
        
        setUPho(p => [...p, ...photoURLs]);
        
        const currentCount = parseInt(localStorage.getItem('restroomadvisor_photo_count') || '0');
        localStorage.setItem('restroomadvisor_photo_count', String(currentCount + photoURLs.length));
        
        showToast(
          translate('toasts.photosUploaded', lang).replace('{count}', photoURLs.length), 
          'success'
        );
      } catch (error) {
        console.error('❌ Fehler beim Upload:', error);
        showToast(translate('toasts.photoUploadError', lang), 'error');
      }
      setLoading(false);
      } else if (pendingPhotoContext === 'suggest') {
        pendingPhotoFiles.forEach(f => {
          const r = new FileReader();
          r.onload = (ev) => setSPho(p => [...p, ev.target.result]);
          r.readAsDataURL(f);
        });
      }
      setPendingPhotoFiles(null);
      setPendingPhotoContext(null);
    }
    
    setNetiquetteAccepted(false);
  };

const getLevel = () => {
  const uniqueToilets = new Set(checkIns.map(c => c.toiletId)).size;
  if (uniqueToilets >= 200) return { level: 7, key: 'legend', next: 200, progress: 100 };
  if (uniqueToilets >= 100) return { level: 6, key: 'diamond', next: 200, progress: ((uniqueToilets - 100) / 100) * 100 };
  if (uniqueToilets >= 50) return { level: 5, key: 'platinum', next: 100, progress: ((uniqueToilets - 50) / 50) * 100 };
  if (uniqueToilets >= 25) return { level: 4, key: 'gold', next: 50, progress: ((uniqueToilets - 25) / 25) * 100 };
  if (uniqueToilets >= 10) return { level: 3, key: 'silver', next: 25, progress: ((uniqueToilets - 10) / 15) * 100 };
  if (uniqueToilets >= 5) return { level: 2, key: 'bronze', next: 10, progress: ((uniqueToilets - 5) / 5) * 100 };
  return { level: 1, key: 'newbie', next: 5, progress: (uniqueToilets / 5) * 100 };
};

const getAchievements = (overrideToiletsSuggested = null) => {
  const totalCheckins = checkIns.length;
  const uniqueToilets = new Set(checkIns.map(c => c.toiletId)).size;
  const uniqueCities = new Set(checkIns.map(c => c.city)).size;
  
  // 📷 Foto-Counter aus localStorage
  const photoCount = parseInt(localStorage.getItem('restroomadvisor_photo_count') || '0');
  
  // 💬 Bewertungen (Reviews)
  const reviewCount = uRev.length;
  
  // 🏘️ Neighborhood Expert - 10 Toiletten in EINER Stadt
  const cityCounts = {};
  checkIns.forEach(c => {
    cityCounts[c.city] = (cityCounts[c.city] || 0) + 1;
  });
  const maxToiletsInOneCity = Math.max(0, ...Object.values(cityCounts));
  
  // 🚂 Transit Explorer - Bahnhofs-Toiletten (Name-basiert)
  const transitVisits = checkIns.filter(c => {
    const savedName = c.toiletName?.toLowerCase() || '';
    if (savedName.includes('bahnhof') || savedName.includes('hbf') || 
        savedName.includes('hauptbahnhof') || savedName.includes('station') || 
        savedName.includes('bhf')) {
      return true;
    }
    
    // Fallback: Suche in tData (falls Namen aktualisiert wurden)
    const allToilets = Object.values(tData).flat();
    const toilet = allToilets.find(t => t.id === c.toiletId);
    if (toilet && toilet.name) {
      const name = toilet.name.toLowerCase();
      return name.includes('bahnhof') || name.includes('hbf') || 
             name.includes('hauptbahnhof') || name.includes('station') || 
             name.includes('bhf');
    }
    
    return false;
  }).length;

  const toiletsSuggested = overrideToiletsSuggested !== null 
    ? overrideToiletsSuggested 
    : parseInt(localStorage.getItem('restroomadvisor_toilets_suggested') || '0');
  
  return [
    { id: 1, key: 'firstFlush', icon: '🎉', unlocked: totalCheckins >= 1 },
    { id: 2, key: 'gettingStarted', icon: '🌟', unlocked: uniqueToilets >= 5 },
    { id: 3, key: 'dedicatedUser', icon: '🏆', unlocked: uniqueToilets >= 10 },
    { id: 4, key: 'committedExplorer', icon: '💪', unlocked: uniqueToilets >= 25 },
    { id: 5, key: 'hardcoreFlusher', icon: '🔥', unlocked: uniqueToilets >= 50 },
    { id: 6, key: 'cityHopper', icon: '🗺️', unlocked: uniqueCities >= 3 },
    { id: 7, key: 'countryWide', icon: '🌍', unlocked: uniqueCities >= 5 },
    { id: 8, key: 'picturePerfect', icon: '📷', unlocked: photoCount >= 5 },
    { id: 9, key: 'firstOpinion', icon: '💬', unlocked: reviewCount >= 1 },
    { id: 10, key: 'neighborhoodExpert', icon: '🏘️', unlocked: maxToiletsInOneCity >= 10 },
    { id: 11, key: 'constructor', icon: '🏗️', unlocked: toiletsSuggested >= 1 },
    { id: 12, key: 'transitExplorer', icon: '🚂', unlocked: transitVisits >= 5 },
    { id: 13, key: 'secretFlusher', icon: '🕵️', unlocked: localStorage.getItem('restroomadvisor_secret_flusher') === 'true', secret: true },
    { 
      id: 14, 
      key: 'earlyAdopter', 
      icon: '🚀', 
      unlocked: (() => {
        const launchDate = new Date('2026-01-22T00:00:00');
        const launchWeekEnd = new Date('2026-01-29T23:59:59');
        const firstCheckInDate = localStorage.getItem('restroomadvisor_first_checkin_date');
        
        if (!firstCheckInDate) return false;
        
        const userFirstCheckIn = new Date(firstCheckInDate);
        return userFirstCheckIn >= launchDate && userFirstCheckIn <= launchWeekEnd;
      })(),
      secret: true,
      limitedTime: true
    }
  ];
};

// ==========================================
// RANK SYSTEM - Mehrdimensionale Ranks
// ==========================================

// 💬 Review-basierte Ranks
const getReviewRanks = () => {
  const reviewCount = uRev.length;
  const ranks = [];
  
  if (reviewCount >= 50) ranks.push({ id: 'master_reviewer', icon: '🏅', name: 'Master Reviewer', category: 'review', unlocked: true, requirement: '50 reviews' });
  if (reviewCount >= 25) ranks.push({ id: 'review_expert', icon: '📝', name: 'Review Expert', category: 'review', unlocked: true, requirement: '25 reviews' });
  if (reviewCount >= 10) ranks.push({ id: 'active_critic', icon: '💬', name: 'Active Critic', category: 'review', unlocked: true, requirement: '10 reviews' });
  
  // Locked ranks
  if (reviewCount < 10) ranks.push({ id: 'active_critic', icon: '🔒', name: 'Active Critic', category: 'review', unlocked: false, requirement: '10 reviews' });
  if (reviewCount < 25) ranks.push({ id: 'review_expert', icon: '🔒', name: 'Review Expert', category: 'review', unlocked: false, requirement: '25 reviews' });
  if (reviewCount < 50) ranks.push({ id: 'master_reviewer', icon: '🔒', name: 'Master Reviewer', category: 'review', unlocked: false, requirement: '50 reviews' });
  
  return ranks;
};

// 🏆 Achievement-basierte Ranks
const getAchievementRanks = () => {
  const achievements = getAchievements();
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const ranks = [];
  
  if (unlockedCount >= 13) ranks.push({ id: 'perfect_legend', icon: '💎', name: 'Perfect Legend', category: 'achievement', unlocked: true, requirement: 'All achievements' });
  if (unlockedCount >= 10) ranks.push({ id: 'ultimate_achiever', icon: '👑', name: 'Ultimate Achiever', category: 'achievement', unlocked: true, requirement: '10 achievements' });
  if (unlockedCount >= 7) ranks.push({ id: 'completionist', icon: '🏆', name: 'Completionist', category: 'achievement', unlocked: true, requirement: '7 achievements' });
  if (unlockedCount >= 3) ranks.push({ id: 'achievement_hunter', icon: '🎯', name: 'Achievement Hunter', category: 'achievement', unlocked: true, requirement: '3 achievements' });
  
  // Locked ranks
  if (unlockedCount < 3) ranks.push({ id: 'achievement_hunter', icon: '🔒', name: 'Achievement Hunter', category: 'achievement', unlocked: false, requirement: '3 achievements' });
  if (unlockedCount < 7) ranks.push({ id: 'completionist', icon: '🔒', name: 'Completionist', category: 'achievement', unlocked: false, requirement: '7 achievements' });
  if (unlockedCount < 10) ranks.push({ id: 'ultimate_achiever', icon: '🔒', name: 'Ultimate Achiever', category: 'achievement', unlocked: false, requirement: '10 achievements' });
  if (unlockedCount < 13) ranks.push({ id: 'perfect_legend', icon: '🔒', name: 'Perfect Legend', category: 'achievement', unlocked: false, requirement: 'All achievements' });
  
  return ranks;
};

// 🎖️ Spezial-Ranks
const getSpecialRanks = () => {
  const ranks = [];
  
  // Admin Rank
  if (isAdmin) {
    ranks.push({ id: 'admin', icon: '👑', name: 'Admin', category: 'special', unlocked: true, requirement: 'Admin status' });
  }
  
  // Beta Legend (nutzt bestehendes localStorage System)
  if (localStorage.getItem('restroomadvisor_beta_tester') === 'true') {
    ranks.push({ id: 'beta_legend', icon: '🏅', name: 'Beta Legend', category: 'special', unlocked: true, requirement: 'Early adopter' });
  } else {
    ranks.push({ id: 'beta_legend', icon: '🔒', name: 'Beta Legend', category: 'special', unlocked: false, requirement: 'Early adopter' });
  }
  
  // Contributor (vorgeschlagene Toiletten)
  const toiletsSuggested = parseInt(localStorage.getItem('restroomadvisor_toilets_suggested') || '0');
  if (toiletsSuggested >= 3) {
    ranks.push({ id: 'contributor', icon: '🎖️', name: 'Contributor', category: 'special', unlocked: true, requirement: '3 suggested toilets' });
  } else {
    ranks.push({ id: 'contributor', icon: '🔒', name: 'Contributor', category: 'special', unlocked: false, requirement: '3 suggested toilets' });
  }
  
  // Photo Pro
  const photoCount = parseInt(localStorage.getItem('restroomadvisor_photo_count') || '0');
  if (photoCount >= 10) {
    ranks.push({ id: 'photo_pro', icon: '📸', name: 'Photo Pro', category: 'special', unlocked: true, requirement: '10 photos uploaded' });
  } else {
    ranks.push({ id: 'photo_pro', icon: '🔒', name: 'Photo Pro', category: 'special', unlocked: false, requirement: '10 photos uploaded' });
  }
  
  return ranks;
};

// 🎯 Check-In Ranks (alle erreichten Stufen)
const getCheckInRanks = () => {
  const uniqueToilets = new Set(checkIns.map(c => c.toiletId)).size;
  const ranks = [];
  
  // Freigeschaltete Ranks
  if (uniqueToilets >= 200) ranks.push({ id: 'legend', icon: '👑', name: 'Legend', category: 'checkin', unlocked: true, requirement: '200 toilets' });
  if (uniqueToilets >= 100) ranks.push({ id: 'diamond', icon: '💎', name: 'Diamond Flusher', category: 'checkin', unlocked: true, requirement: '100 toilets' });
  if (uniqueToilets >= 50) ranks.push({ id: 'platinum', icon: '🏆', name: 'Platinum Flusher', category: 'checkin', unlocked: true, requirement: '50 toilets' });
  if (uniqueToilets >= 25) ranks.push({ id: 'gold', icon: '🥇', name: 'Gold Flusher', category: 'checkin', unlocked: true, requirement: '25 toilets' });
  if (uniqueToilets >= 10) ranks.push({ id: 'silver', icon: '🥈', name: 'Silver Flusher', category: 'checkin', unlocked: true, requirement: '10 toilets' });
  if (uniqueToilets >= 5) ranks.push({ id: 'bronze', icon: '🥉', name: 'Bronze Flusher', category: 'checkin', unlocked: true, requirement: '5 toilets' });
  ranks.push({ id: 'newbie', icon: '🐣', name: 'Newbie', category: 'checkin', unlocked: true, requirement: 'Start' });
  
  // Gesperrte Ranks
  if (uniqueToilets < 5) ranks.push({ id: 'bronze', icon: '🔒', name: 'Bronze Flusher', category: 'checkin', unlocked: false, requirement: '5 toilets' });
  if (uniqueToilets < 10) ranks.push({ id: 'silver', icon: '🔒', name: 'Silver Flusher', category: 'checkin', unlocked: false, requirement: '10 toilets' });
  if (uniqueToilets < 25) ranks.push({ id: 'gold', icon: '🔒', name: 'Gold Flusher', category: 'checkin', unlocked: false, requirement: '25 toilets' });
  if (uniqueToilets < 50) ranks.push({ id: 'platinum', icon: '🔒', name: 'Platinum Flusher', category: 'checkin', unlocked: false, requirement: '50 toilets' });
  if (uniqueToilets < 100) ranks.push({ id: 'diamond', icon: '🔒', name: 'Diamond Flusher', category: 'checkin', unlocked: false, requirement: '100 toilets' });
  if (uniqueToilets < 200) ranks.push({ id: 'legend', icon: '🔒', name: 'Legend', category: 'checkin', unlocked: false, requirement: '200 toilets' });
  
  return ranks;
};

// 📊 Alle verfügbaren Ranks sammeln
const getAllAvailableRanks = () => {
  return {
    checkin: getCheckInRanks(),
    review: getReviewRanks(),
    achievement: getAchievementRanks(),
    special: getSpecialRanks()
  };
};

// 🎖️ Aktuell ausgewählten Rank holen
const getSelectedRank = () => {
  const selectedId = localStorage.getItem('restroomadvisor_selected_rank');
  
  if (!selectedId) {
    // Default: Newbie
    return { id: 'newbie', icon: '🐣', name: 'Newbie' };
  }
  
  const allRanks = getAllAvailableRanks();
  const allFlat = [
    ...allRanks.checkin.filter(r => r.unlocked),
    ...allRanks.review.filter(r => r.unlocked),
    ...allRanks.achievement.filter(r => r.unlocked),
    ...allRanks.special.filter(r => r.unlocked)
  ];
  
  const found = allFlat.find(r => r.id === selectedId);
  return found || { id: 'newbie', icon: '🐣', name: 'Newbie' };
};

  const getCheckInCount = (toiletId) => {
    return checkIns.filter(c => c.toiletId === toiletId).length;
  };

const handleSuggest = async (ntd) => {
  setLoading(true);
  try {
    
    let lat = 0;
    let lng = 0;
    
    // Koordinaten verwenden 
    if (sCoordinates && sCoordinates.lat && sCoordinates.lng) {
      lat = sCoordinates.lat;
      lng = sCoordinates.lng;
    } else {
      const { geocodeAddress } = await import('./geocoding');
      const geoResult = await geocodeAddress(ntd.address, city);
      
      if (geoResult) {
      lat = geoResult.lat;
      lng = geoResult.lng;
      } else {
      console.error('❌ FEHLER: Keine Koordinaten gefunden!');
      showToast(translate('toasts.noAddressFound', lang), 'error', 6000);
      setLoading(false);
      return; 
   }
    }
    
    // Fotos zuerst zu Firebase Storage hochladen
    let uploadedPhotoURLs = [];
    let failedUploads = 0;
    
    if (ntd.photos && ntd.photos.length > 0) {
      const tempId = 'temp_' + Date.now();
      
      for (let i = 0; i < ntd.photos.length; i++) {
        try {
          const photoData = ntd.photos[i];

          if (!networkOnline) {
            failedUploads++;
            continue;
          }

          const response = await fetch(photoData);
          const blob = await response.blob();
          const file = new File([blob], `photo_${i}.jpg`, { type: 'image/jpeg' });

          const url = await uploadPhoto(file, tempId);
          uploadedPhotoURLs.push(url);
          
        } catch (uploadError) {
          console.error(`❌ Foto-Upload ${i + 1} fehlgeschlagen:`, uploadError);
          failedUploads++;
        }
      }
      
      if (failedUploads > 0) {
        console.warn(`⚠️ ${failedUploads} von ${ntd.photos.length} Fotos konnten nicht hochgeladen werden`);
        showToast(`⚠️ ${failedUploads} Foto(s) konnten nicht hochgeladen werden. Toilette wird ohne diese Fotos gespeichert.`, 'warning', 5000);
      }
    }
    
    // const extractedHours = extractOpeningHours(ntd.name);
    // const cleanedName = cleanNameFromOpeningHours(ntd.name);

    const currentUserId = user?.uid || user?.id || 'demo-user';
    const currentUserName = user?.name || 'Demo Nutzer';
    const isAdminUser = isAdmin;

    // Toiletten-Objekt erstellen (mit Storage-URLs statt Base64!)
const nt = {
  name: ntd.name, 
  address: ntd.address, 
  city: sCity || city,  
  rating: ntd.initialRating || 0,
  reviews: ntd.initialRating ? 1 : 0, 
  lat: lat,
  lng: lng,
  isPendingPhotos: uploadedPhotoURLs.length > 0,
  images: uploadedPhotoURLs.map(url => createPhotoObject(url, {
    status: isAdminUser ? 'pending' : 'pending',  
    uploadedBy: currentUserName,
    uploadedById: currentUserId,
    uploadedAt: new Date().toISOString()
  })),
  cost: ntd.cost || '', 
  costAmount: ntd.costAmount || '',
  openingHours: ntd.openingHours || '', 
  amenities: ntd.amenities || [],
  lastVisited: null, 
  comments: ntd.initialRating ? [{
    user: currentUserName, 
    rating: ntd.initialRating, 
    text: ntd.initialComment || '',
    date: new Date().toISOString().split('T')[0], 
    photos: uploadedPhotoURLs.map(url => createPhotoObject(url, {
      status: 'pending',
      uploadedBy: currentUserName,
      uploadedById: currentUserId,
      uploadedAt: new Date().toISOString()
    }))
      }] : [], 
      isPending: true,  
      suggestedBy: currentUserName,
      suggestedById: currentUserId,  
      suggestedByAdmin: isAdminUser,  
      suggestedDate: new Date().toISOString().split('T')[0],
      approvedBy: null,  
      approvedById: null,  
      approvedDate: null 
    };

    if (ntd.accessible !== undefined) {
      nt.accessible = ntd.accessible;
    }
      
    const newId = await addToilet(nt);
    nt.id = newId;  
    
    // 🏗️ VORSCHLAG-COUNTER ERHÖHEN
    const currentSuggestions = parseInt(localStorage.getItem('restroomadvisor_toilets_suggested') || '0');
    localStorage.setItem('restroomadvisor_toilets_suggested', String(currentSuggestions + 1));
    
    // Lokale Daten aktualisieren 
    const toiletCity = sCity || city;
    const ud = { ...tData };
    if (!ud[toiletCity]) ud[toiletCity] = [];
    ud[toiletCity] = [...ud[toiletCity], nt];
    setTData(ud);
    
    showToast(translate('toasts.suggestionSuccess', lang), 'success');
    
    // Koordinaten und Stadt zurücksetzen
    setSCoordinates(null);
    setSCity(null);
    
setScr('main');
    
    // ✨ ACHIEVEMENT-CHECK MANUELL mit NEUEM Wert ausführen
    setTimeout(() => {
      const newCount = currentSuggestions + 1; 
      
      // Vorherige Achievements aus localStorage laden
      const prevAchievementsStr = localStorage.getItem('restroomadvisor_prev_achievements');
      let previousAchievements = [];
      
      if (prevAchievementsStr) {
        try {
          previousAchievements = JSON.parse(prevAchievementsStr);
        } catch (e) {
          console.error('Fehler beim Parsen der vorherigen Achievements:', e);
        }
      }
      
      // Aktuelle Achievements berechnen MIT NEUEM WERT
      const currentAchievements = getAchievements(newCount);
      
      // ALLE neuen Achievements finden
      const newlyUnlockedList = currentAchievements.filter((ach, idx) => {
        const wasUnlocked = previousAchievements[idx]?.unlocked || false;
        const isNowUnlocked = ach.unlocked;
        return isNowUnlocked && !wasUnlocked;
      });
      
      // Zur Queue hinzufügen
      if (newlyUnlockedList.length > 0) {
        setAchievementQueue(prev => [...prev, ...newlyUnlockedList]);
      }
      
      // Aktuellen Stand speichern
      localStorage.setItem('restroomadvisor_prev_achievements', JSON.stringify(currentAchievements));
    }, 150);
    
  } catch (error) {
    console.error('❌ Fehler beim Vorschlagen:', error);
    showToast(translate('toasts.suggestionError', lang), 'error');
  }
  
  setLoading(false);
};

  const handleRepClo = async (category, rea, com, photos = []) => {
    setLoading(true);
    try {
      
      const nrep = {
        user: user?.name || 'Demo Nutzer', 
        category: category, 
        reason: rea, 
        comment: com,
        photos: photos,
        date: new Date().toISOString().split('T')[0],
        toiletId: sel.id, 
        toiletName: sel.name, 
        toiletAddress: sel.address, 
        city: city
      };

      await reportClosure(sel.id, nrep);

      const rk = `${city}-${sel.id}`;
      const er = clRep[rk] || { count: 0, reports: [] };
      setClRep({ ...clRep, [rk]: { count: er.count + 1, reports: [...er.reports, nrep] }});
      
      showToast(translate('toasts.reportSuccess', lang), 'success');

      setClRea('');
      setClCom(''); 
      setReportCategory('');
      setReportPhotos([]);
      setScr('detail');
      
    } catch (error) {
      console.error('❌ Fehler beim Melden:', error);
      showToast(translate('toasts.reportError', lang), 'error');
    }
    
    setLoading(false);
  };

// 🎉 ACHIEVEMENT POPUP - GLOBAL (über allen Screens!)
const AchievementPopup = () => {
  if (!achievementPopup) {
    return null;
  }
  
  return (
    <div 
      className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[99999] animate-slideDown px-4"
      style={{ animation: 'slideDown 0.5s ease-out' }}
    >
      <div className="bg-gradient-to-r from-yellow-400 via-white to-yellow-400 rounded-2xl shadow-2xl p-4 min-w-[300px] max-w-[350px] border-4 border-yellow-500 relative">
        <div className="flex items-center gap-4 mb-3">
          <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center shadow-lg">
            <span className="text-5xl leading-none flex items-center justify-center animate-bounce">
              {achievementPopup.icon}
            </span>
          </div>
          <div className="flex-1">
            <div className="text-amber-900 font-bold text-sm uppercase tracking-wide mb-1">
              {translate('achievementPopup.unlocked', lang)}
            </div>
            <div className="text-gray-900 font-bold text-lg">
              {achievementPopup.name}
            </div>
            <div className="text-gray-700 text-sm">
              {achievementPopup.desc}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => {
            setAchievementPopup(null);
            setScr('profile');
          }}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-md"
        >
          {translate('achievementPopup.viewAll', lang)}
        </button>
      </div>
    </div>
  );
};

// Loading-Overlay anzeigen während Firebase-Operationen
if (loading) {
    return (
      <>
        <AchievementPopup />
        <OfflineBanner 
          isOnline={networkOnline}
          onRetry={() => {
            showToast(translate('toasts.checkingConnection', lang), 'info', 1000);
            setTimeout(() => {
              setNetworkOnline(navigator.onLine);
              if (navigator.onLine) {
                syncOfflineQueue(city);
              }
            }, 1000);
          }}
          queueCount={offlineQueue.length}
          style={{ isolation: 'isolate' }}
        />
        <div className="fixed inset-0 bg-gradient-to-b from-amber-100 to-white flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
            <div className="animate-spin text-6xl mb-4">💩</div>
            <p className="text-xl font-bold text-gray-800">{translate('loading.title', lang)}</p>
            <p className="text-sm text-gray-600 mt-2">{translate('loading.pleaseWait', lang)}</p>
          </div>
        </div>
        
        <style>{`
          @keyframes slideDown {
            0% {
              transform: translate(-50%, -100px);
              opacity: 0;
            }
            100% {
              transform: translate(-50%, 0);
              opacity: 1;
            }
          }
        `}</style>
      </>
    );
  }

  // Netiquette-Modal
  if (showNetiquetteModal) {
    return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <OfflineBanner 
          isOnline={networkOnline}
          onRetry={() => {
            showToast(translate('toasts.checkingConnection', lang), 'info', 1000);
            setTimeout(() => {
              setNetworkOnline(navigator.onLine);
              if (navigator.onLine) {
                syncOfflineQueue(city);
              }
            }, 1000);
          }}
          queueCount={offlineQueue.length}
          style={{ isolation: 'isolate' }}
        />
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-amber-600">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">📸</span>
                {translate('netiquette.title', lang)}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-900 font-semibold mb-2">
                  {translate('netiquette.importantNotice', lang)}
                </p>
                <p className="text-blue-800 text-sm">
                  {translate('netiquette.importantDescription', lang)}
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <h3 className="font-bold text-gray-900 text-lg mb-3">
                  {translate('netiquette.rulesHeading', lang)}
                </h3>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                    <span className="text-2xl flex-shrink-0">🚫</span>
                    <div>
                      <p className="font-semibold text-red-900">{translate('netiquette.noPeople', lang)}</p>
                      <p className="text-sm text-red-800" dangerouslySetInnerHTML={{ __html: translate('netiquette.noPeopleDesc', lang) }} />
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-amber-50 border-2 border-amber-200 rounded-lg">
                    <span className="text-2xl flex-shrink-0">👤</span>
                    <div>
                      <p className="font-semibold text-amber-900">{translate('netiquette.privacyRights', lang)}</p>
                      <p className="text-sm text-amber-800">
                        {translate('netiquette.privacyRightsDesc', lang)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                    <span className="text-2xl flex-shrink-0">🚽</span>
                    <div>
                      <p className="font-semibold text-green-900">{translate('netiquette.onlyFacilities', lang)}</p>
                      <p className="text-sm text-green-800">
                        {translate('netiquette.onlyFacilitiesDesc', lang)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={netiquetteAccepted}
                    onChange={(e) => setNetiquetteAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <span className="text-gray-900 text-sm" dangerouslySetInnerHTML={{ __html: translate('netiquette.confirmText', lang) }} />
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowNetiquetteModal(false);
                    setPendingPhotoFiles(null);
                    setPendingPhotoContext(null);
                    setNetiquetteAccepted(false);
                  }}
                  className="flex-1 py-3 px-6 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  {translate('netiquette.cancelButton', lang)}
                </button>
                <button
                  onClick={handleNetiquetteAccept}
                  disabled={!netiquetteAccepted}
                  className={`flex-1 py-3 px-6 rounded-lg font-semibold text-white transition-colors ${
                    netiquetteAccepted
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  {translate('netiquette.acceptButton', lang)}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                {translate('netiquette.oneTimeNotice', lang)}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ===== NAVIGATION DISCLAIMER MODAL =====
  if (showNavigationDisclaimer && pendingNavigationToilet) {
    return (
      <>
        <OfflineBanner isOnline={networkOnline} />
        
        {/* Achievement Popup */}
        {achievementPopup && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] w-11/12 max-w-md animate-slideDown">
            <div className="bg-gradient-to-r from-yellow-400 via-white to-yellow-400 rounded-xl p-5 shadow-2xl border-4 border-yellow-500 relative">
              <div className="absolute top-2 left-2 text-2xl">🎊</div>
              <div className="absolute top-2 right-2 text-2xl">🎊</div>
              
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-white border-4 border-yellow-400 flex items-center justify-center">
                  <span className="text-4xl leading-none flex items-center justify-center">{achievementPopup.icon}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-black text-xl text-gray-900 mb-1 leading-tight">Achievement freigeschaltet!</h3>
                  <p className="font-bold text-lg text-yellow-700 leading-tight">{achievementPopup.name}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-700 mb-3 leading-snug">{achievementPopup.description}</p>
              
              <button
                onClick={() => {
                  setAchievementPopup(null);
                  setScr('profile');
                }}
                className="w-full py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-bold rounded-lg shadow-md text-sm"
              >
                Alle Achievements anzeigen
              </button>
            </div>
          </div>
        )}
        
        {/* Navigation Disclaimer Modal */}
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center gap-3">
                <Navigation className="text-white" size={28} />
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    {translate('detail.navigationModal.title', lang)}
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    {translate('detail.navigationModal.externalAppRequired', lang)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Toiletten-Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <MapPin className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="font-bold text-gray-900 mb-1">{pendingNavigationToilet.name}</p>
                    <p className="text-sm text-gray-600">{pendingNavigationToilet.address}</p>
                  </div>
                </div>
              </div>
              
              {/* Hinweis */}
              <div className="mb-4">
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  {translate('detail.navigationModal.description', lang)}
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-xs flex items-start gap-2">
                    <span className="text-lg flex-shrink-0">ℹ️</span>
                    <span>{translate('detail.navigationModal.reminder', lang)}</span>
                  </p>
                </div>
              </div>
              
              {/* Checkbox - Nicht mehr anzeigen */}
              <label className="flex items-start gap-3 cursor-pointer mb-6 select-none">
                <input
                  type="checkbox"
                  id="dontShowAgain"
                  className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 flex-1">
                  {translate('detail.navigationModal.dontShowAgain', lang)}
                </span>
              </label>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={cancelNavigation}
                  className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition-colors"
                >
                  {translate('detail.navigationModal.cancel', lang)}
                </button>
                <button
                  onClick={() => {
                    const dontShowAgain = document.getElementById('dontShowAgain').checked;
                    confirmNavigation(dontShowAgain);
                  }}
                  className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Navigation size={18} />
                  {translate('detail.navigationModal.startNavigation', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

   // REPORT-MODAL FÜR KOMMENTARE 
  if (showReportModal && reportingComment) {
    return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🚩 {translate('reportComment.title', lang)}</h3>
            
            {/* Vorschau des gemeldeten Kommentars */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 border">
              <p className="text-sm text-gray-600 mb-1">{translate('reportComment.commentBy', lang)} <strong>{reportingComment.comment.user}</strong>:</p>
              <p className="text-gray-800 text-sm italic">
                "{reportingComment.comment.text || translate('reportComment.noText', lang)}"
              </p>
            </div>
            
            {/* Grund auswählen */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translate('reportComment.selectReason', lang)}
              </label>
              <div className="space-y-2">
                {[
                  translate('reportComment.reasons.hate', lang),
                  translate('reportComment.reasons.spam', lang),
                  translate('reportComment.reasons.misinformation', lang),
                  translate('reportComment.reasons.inappropriate', lang),
                  translate('reportComment.reasons.other', lang)
                ].map((reason) => (
                  <label key={reason} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason}
                      checked={reportReason === reason}
                      onChange={(e) => setReportReason(e.target.value)}
                      className="w-4 h-4 text-amber-500"
                    />
                    <span className="text-gray-700">{reason}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportingComment(null);
                  setReportReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg"
              >
                {translate('common.cancel', lang)}
              </button>
              <button
                onClick={submitCommentReport}
                disabled={!reportReason || loading}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? translate('common.sending', lang) : '🚩 ' + translate('common.report', lang)}
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              {translate('reportComment.warningText', lang)}
            </p>
          </div>
        </div>
      </>
    );
  }

  // LANGUAGE SELECTION SCREEN 
  if (showLanguageSelection) {
    return (
      <>
        <OfflineBanner isOnline={networkOnline} />
        
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-cyan-500">
              
              {/* Header */}
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">🌍</div>
                <h1 className="text-3xl font-black text-gray-800 mb-2">
                  Choose Language
                </h1>
                <p className="text-gray-600">
                  Sprache wählen
                </p>
              </div>

              {/* Language Options */}
              <div className="space-y-4">
                {/* Deutsch */}
                <button
                  onClick={() => handleLanguageSelect('de')}
                  className="w-full py-6 px-6 bg-gradient-to-r from-red-500 via-yellow-500 to-black hover:from-red-600 hover:via-yellow-600 hover:to-gray-900 text-white font-bold rounded-xl shadow-lg transform hover:scale-[1.02] transition-all flex items-center justify-between"
                >
                  <span className="text-xl">🇩🇪 Deutsch</span>
                  <span className="text-2xl">→</span>
                </button>

                {/* English */}
                <button
                  onClick={() => handleLanguageSelect('en')}
                  className="w-full py-6 px-6 bg-gradient-to-r from-blue-600 via-white to-red-600 hover:from-blue-700 hover:via-gray-100 hover:to-red-700 text-gray-800 font-bold rounded-xl shadow-lg transform hover:scale-[1.02] transition-all flex items-center justify-between border-2 border-gray-200"
                >
                  <span className="text-xl">🇬🇧 English</span>
                  <span className="text-2xl">→</span>
                </button>
              </div>

              {/* Info Text */}
              <p className="text-xs text-gray-500 text-center mt-6">
                You can change the language later in your profile.<br />
                Du kannst die Sprache später in deinem Profil ändern.
              </p>
            </div>
          </div>
        </div>
</>
    );
  }

  // App Download Screen (nur im Browser)
if (scr === 'appDownload') {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {/* Toast-Benachrichtigungen */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Offline-Banner */}
      <OfflineBanner
        isOnline={networkOnline}
        onRetry={() => {
          setToast({ message: translate('toasts.checkingConnection', lang), type: 'info' });
          setTimeout(() => {
            setNetworkOnline(navigator.onLine);
            if (navigator.onLine) {
              syncOfflineQueue(city);
            }
            setToast(null);
          }, 1000);
        }}
        queueCount={offlineQueue.length}
        style={{ isolation: 'isolate' }}
      />
      
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          {/* App Logo */}
          <div className="mb-6">
            <div className="w-32 h-32 mx-auto bg-amber-600 rounded-full shadow-xl flex items-center justify-center">
              <img 
                src="/transparent_logo.png" 
                alt="Restroom Advisor Logo" 
                className="w-28 h-28 object-contain"
              />
            </div>
          </div>
            
            {/* Title */}
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Restroom Advisor
          </h2>
            
            {/* Android Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-full mb-4">
            <span className="text-2xl">📱</span>
            <span className="text-sm font-bold text-orange-700">
              {translate('appDownload.androidOnly', lang)}
              </span>
            </div>
            
            {/* Description */}
            <p className="text-gray-600 mb-6 leading-relaxed">
              {translate('appDownload.description', lang)}
            </p>
            
            {/* Benefits List */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 mb-6 text-left">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="text-xl">💾</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">
                  {translate('appDownload.benefit1Title', lang)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {lang === 'de' 
                      ? 'Alle deine Bewertungen, Check-ins und Favoriten werden lokal gespeichert - ohne Cloud-Upload' 
                      : 'All your reviews, check-ins and favorites are stored locally - no cloud upload'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-xl">⚡</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">
                    {translate('appDownload.benefit2Title', lang)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {translate('appDownload.benefit2Text', lang)}
                  </p>
                </div>
              </div>
            </div>
          </div>
            
            {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleOpenPlayStore}
              className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-xl shadow-lg transform transition-all hover:scale-105 flex items-center justify-center gap-3"
            >
              <span className="text-2xl">📱</span>
              <span>{translate('appDownload.downloadButton', lang)}</span>
            </button>
            
            <button
              onClick={handleSkipAppDownload}
              className="w-full py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all"
            >
              {translate('appDownload.continueWeb', lang)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

  if (!user) {
    return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {/* Toast-Benachrichtigungen */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        
        {/* Offline-Banner - TEST */}
        <OfflineBanner
          isOnline={networkOnline}
          onRetry={() => {
            showToast(translate('toasts.checkingConnection', lang), 'info', 1000);
            setTimeout(() => {
              setNetworkOnline(navigator.onLine);
              if (navigator.onLine) {
                syncOfflineQueue(city);
              }
            }, 1000);
          }}
          queueCount={offlineQueue.length}
          style={{ isolation: 'isolate' }}
        />
        
        <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div 
            className="mb-6 cursor-pointer select-none" 
            onClick={() => {
              const newCount = adminClickCount + 1;
              setAdminClickCount(newCount);
              if (newCount >= 5) {
                setShowAdminLogin(true);
                setAdminClickCount(0);
              }
            }}
          >
            <div className="w-52 h-52 mx-auto bg-amber-600 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform duration-300">
              <img 
                src="/transparent_logo.png" 
                alt="Restroom Advisor Logo" 
                className="w-44 h-44 object-contain"
              />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-amber-900 mb-2">{translate('welcome.title', lang)}</h1>
          <p className="text-amber-700 mb-10">{translate('welcome.subtitle', lang)}</p>
          <div className="bg-white p-8 rounded-2xl shadow-lg">
            <label className="block text-left text-amber-900 font-semibold mb-3">{translate('welcome.nameLabel', lang)}</label>
            <input 
              type="text" 
              value={userName} 
              onChange={(e) => {
                const value = e.target.value;
                
                // Erlaube temporär alle Zeichen während des Tippens
                if (value.length <= 30) {
                  setUserName(value);
                }
                
                // Bereinigung mit Verzögerung (besseres Mobile-Erlebnis)
                clearTimeout(window.userNameCleanupTimeout);
                window.userNameCleanupTimeout = setTimeout(() => {
                  const cleaned = value
                    .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '')
                    .slice(0, 30);
                  setUserName(cleaned);
                }, 500); 
              }}
              onBlur={(e) => {
                // Sofortige Bereinigung wenn User fertig ist
                clearTimeout(window.userNameCleanupTimeout);
                const cleaned = e.target.value
                  .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '')
                  .slice(0, 30);
                setUserName(cleaned);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  clearTimeout(window.userNameCleanupTimeout);
                  const cleaned = userName
                    .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '')
                    .slice(0, 30)
                    .trim();
                  if (cleaned.length >= 2) {
                    createUser(cleaned);
                  }
                }
              }}
              placeholder={translate('welcome.namePlaceholder', lang)} 
              maxLength={30}
              className="w-full p-4 border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none mb-4" 
            />
            <p className="text-xs text-gray-500 mb-4">
              {translate('welcome.charactersCount', lang).replace('{count}', userName.length)}
            </p>
            <button 
              onClick={() => userName.trim().length >= 2 && createUser(userName.trim())}
              disabled={!userName.trim() || userName.trim().length < 2}
              className={`w-full py-4 px-6 rounded-lg font-bold text-white ${
                userName.trim() && userName.trim().length >= 2
                  ? 'bg-amber-600 hover:bg-amber-700' 
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {translate('welcome.startButton', lang)}
            </button>
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {lang === 'de' ? 'oder' : 'or'}
              </span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>
            <button
              onClick={() => createUser(lang === 'de' ? 'Gast' : 'Guest')}
            className="w-full py-3 px-6 rounded-lg font-medium text-amber-600 hover:text-amber-700 border-2 border-amber-600 hover:border-amber-700 bg-transparent hover:bg-amber-50 transition-all flex items-center justify-center gap-2"
            >
              <span>👤</span>
              {translate('welcome.guestButton', lang)}
            </button>
            {/* Geheimer Admin-Login */}
            {showAdminLogin && (
              <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <p className="text-sm text-gray-600 mb-3">{translate('welcome.adminLogin', lang)}</p>
                <input 
                  type="email" 
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder={translate('welcome.adminEmail', lang)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg mb-2 text-sm"
                />
                <input 
                  type="password" 
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && adminEmail && adminPassword) {
                      setLoading(true);
                      const result = await loginAdmin(adminEmail, adminPassword);
                      setLoading(false);
                      
                      if (result.success) {
                        setIsAdmin(true);
                        setAdminUser(result.user);
                        setShowAdminLogin(false);
                        showToast('✅ Admin-Login erfolgreich!', 'success');
                      } else {
                        showToast(`❌ Login fehlgeschlagen: ${result.error}`, 'error');
                      }
                    }
                  }}
                  placeholder={translate('welcome.adminPassword', lang)} 
                  className="w-full p-3 border-2 border-gray-300 rounded-lg mb-2 text-sm"
                />
                <button 
                  onClick={async () => {
                    if (!adminEmail || !adminPassword) return;
                    
                    setLoading(true);
                    const result = await loginAdmin(adminEmail, adminPassword);
                    setLoading(false);
                    
                    if (result.success) {
                      setIsAdmin(true);
                      setAdminUser(result.user);
                      setShowAdminLogin(false);
                      showToast('✅ Admin-Login erfolgreich!', 'success');
                    } else {
                      showToast(`❌ Login fehlgeschlagen: ${result.error}`, 'error');
                    }
                  }}
                  disabled={!adminEmail || !adminPassword}
                  className={`w-full py-2 px-4 rounded-lg text-sm font-bold text-white ${
                    adminEmail && adminPassword 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  Admin Login
                </button>
                <button 
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminClickCount(0);
                  }}
                  className="w-full mt-2 py-2 px-4 text-sm text-gray-600 hover:text-gray-800"
                >
                  Abbrechen
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4">{translate('welcome.nameStoredLocally', lang)}</p>
          </div>
      </div>
      </div>
      </>
    );
  }

if (scr === 'welcome') {
      
    // Stadt auswählen
    const handleCitySelect = async (cityData) => {
      // 🎉 Easter Egg Check (mit aktueller Sprache)
      const easterEgg = getEasterEgg(cityData.name, lang);
      if (easterEgg) {
        setCityIn(cityData.name);
        setShowWS(false);
        return; 
      }
      
      // City Alias Normalisierung
      const normalizedCityName = normalizeCityName(cityData.name);
      
      setCityIn(normalizedCityName);
      setShowWS(false);
      
      // GPS-Zentrierung deaktivieren (User hat manuell Stadt gewählt)
      setUserLocation(null);
      
      // Karte auf Stadtzentrum fokussieren
      if (cityData.lat && cityData.lng) {
        const newCenter = [cityData.lat, cityData.lng];
        const newZoom = 12;
        
        setMapCenter(newCenter);
        setMapZoom(newZoom);
      }
      
      setTimeout(() => {
        setCity(normalizedCityName);
        setScr('main');
      }, 100);
    };
    
        return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {/* Toast-Benachrichtigungen */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        
        {/* Offline-Banner */}
        <OfflineBanner
          isOnline={networkOnline} 
          onRetry={() => {
            showToast(translate('toasts.checkingConnection', lang), 'info', 1000);
            setTimeout(() => {
              setNetworkOnline(navigator.onLine);
              if (navigator.onLine) {
                syncOfflineQueue(city);
              }
            }, 1000);
          }}
        queueCount={offlineQueue.length}
        style={{ isolation: 'isolate' }}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-white via-amber-50 to-white flex items-center justify-center p-4 sm:p-6">
        
        {/* 🔥 Firebase Loading Overlay - Zeigt während Initialisierung */}
        {!isFirebaseReady && (
          <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="animate-spin text-6xl mb-4">🔄</div>
              <p className="text-amber-900 font-semibold text-lg">Initialisiere App...</p>
              <p className="text-amber-700 text-sm mt-2">Einen Moment bitte</p>
            </div>
          </div>
        )}
        
      <div className="text-center w-full max-w-md px-4">
        {/* Fun Fact über dem Logo */}
        {currentFunFact && (
          <div className="bg-white border border-amber-200 rounded-lg px-4 sm:px-5 py-3 mb-4 sm:mb-6 shadow-sm">
            <p className="text-amber-900 text-sm sm:text-base font-medium italic">
            {currentFunFact}
            </p>
            </div>
          )}
          <div 
            className="mb-2 cursor-pointer select-none" 
            onClick={() => {
              const newCount = adminClickCount + 1;
              setAdminClickCount(newCount);
              if (newCount >= 5) {
                setShowAdminLogin(true);
                setAdminClickCount(0);
              }
            }}
          >
            <div className="w-52 h-52 mx-auto bg-amber-600 rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform duration-300">
              <img 
                src="/transparent_logo.png" 
                alt="Restroom Advisor Logo" 
                className="w-44 h-44 object-contain"
              />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-amber-900 mb-8">Restroom Advisor</h1>
                  
          <div className="bg-white p-4 sm:p-8 rounded-2xl shadow-lg">
            <label className="block text-left text-amber-900 font-semibold mb-3 text-sm sm:text-base">{translate('welcome.cityLabel', lang)}</label>
            
            {/* GPS-Button */}
            <button 
              onClick={handleGetLocation}
              disabled={loadingGPS}
              className={`w-full mb-4 py-4 px-6 rounded-xl font-bold text-white flex items-center justify-center gap-3 transition-all shadow-lg min-h-[56px] ${
                loadingGPS 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 hover:shadow-xl transform hover:scale-[1.02]'
              }`}
            >
              {loadingGPS ? (
                <>
                  <div className="animate-spin text-xl">🔄</div>
                  <span>{translate('welcome.locationDetecting', lang)}</span>
                </>
              ) : (
                <>
                  <Navigation size={22} />
                  <span>{translate('welcome.locationButton', lang)}</span>
                </>
              )}
            </button>
            
            <div className="relative mb-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="text-sm text-gray-500">{translate('welcome.orDivider', lang)}</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
            </div>
            
            <div className="relative">
              <input 
                type="text" 
                value={cityIn} 
                onChange={(e) => { setCityIn(e.target.value); setShowWS(true); }}
                onFocus={() => setShowWS(true)} 
                placeholder={translate('welcome.cityPlaceholder', lang)}
                className="w-full p-4 text-base border-2 border-amber-200 rounded-lg focus:border-amber-500 focus:outline-none mb-4" 
              />
              
              {/* Autocomplete-Dropdown mit Geocoding-Ergebnissen */}
              {showWS && citySuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-[-16px] mb-4 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                  {citySuggestions.map((cityData, index) => {
                    const toiletCount = tData[cityData.name] ? tData[cityData.name].length : 0;
                    
                    return (
                      <button 
                        key={`${cityData.name}-${index}`} 
                        onClick={() => handleCitySelect(cityData)}
                        className="w-full text-left p-3 hover:bg-amber-50 border-b last:border-b-0 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <span className="font-semibold text-gray-800 block">{cityData.name}</span>
                            {cityData.state && (
                              <span className="text-xs text-gray-500 block">{cityData.state}</span>
                            )}
                          </div>
                          <div className="text-right ml-3">
                          <span className="text-xs text-gray-600 block">
                            {toiletCount > 0 ? `${toiletCount} ${toiletCount === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)}` : translate('main.results', lang)}
                          </span>
                          {cityData.population && (
                            <span className="text-xs text-gray-400 block">
                                {(cityData.population / 1000).toFixed(0)}k {translate('main.populationUnit', lang)}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Lade-Anzeige */}
              {searchingCities && (
                <div className="absolute top-full left-0 right-0 mt-[-16px] mb-4 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 p-4 text-center">
                  <div className="text-amber-600 animate-pulse">🔍 Suche Städte...</div>
                </div>
              )}
              
              {/* Keine Ergebnisse - mit Easter Eggs! 🎉 */}
              {!searchingCities && cityIn.length >= 3 && citySuggestions.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-[-16px] mb-4 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 p-4 text-center">
                  {getEasterEgg(cityIn, lang) ? (
                    <>
                      <div className="text-2xl mb-2">{getEasterEgg(cityIn, lang).split(' ')[0]}</div>
                      <div className="text-gray-700 text-sm font-medium">{getEasterEgg(cityIn, lang).substring(3)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-gray-500 text-sm">
                        {translate('welcome.noCitiesFound', lang)} "{cityIn}"
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {translate('welcome.tryDifferentSpelling', lang)}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Geheimer Admin-Login */}
            {showAdminLogin && (
              <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <p className="text-sm text-gray-600 mb-3">🔐 Admin-Login</p>
                <input 
                  type="email" 
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="Admin E-Mail" 
                  className="w-full p-3 border-2 border-gray-300 rounded-lg mb-2 text-sm"
                />
                <input 
                  type="password" 
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && adminEmail && adminPassword) {
                      setLoading(true);
                      const result = await loginAdmin(adminEmail, adminPassword);
                      setLoading(false);
                      
                      if (result.success) {
                        setIsAdmin(true);
                        setAdminUser(result.user);
                        setShowAdminLogin(false);
                        showToast(translate('toasts.adminLoginSuccess', lang), 'success');
                      } else {
                        showToast(translate('toasts.loginFailed', lang).replace('{error}', result.error), 'error');
                      }
                    }
                  }}
                  placeholder="Admin Passwort" 
                  className="w-full p-3 border-2 border-gray-300 rounded-lg mb-2 text-sm"
                />
                <button 
                  onClick={async () => {
                    if (!adminEmail || !adminPassword) return;
                    
                    setLoading(true);
                    const result = await loginAdmin(adminEmail, adminPassword);
                    setLoading(false);
                    
                    if (result.success) {
                      setIsAdmin(true);
                      setAdminUser(result.user);
                      setShowAdminLogin(false);
                      showToast('✅ Admin-Login erfolgreich!', 'success');
                    } else {
                      showToast(`❌ Login fehlgeschlagen: ${result.error}`, 'error');
                    }
                  }}
                  disabled={!adminEmail || !adminPassword}
                  className={`w-full py-2 px-4 rounded-lg text-sm font-bold text-white ${
                    adminEmail && adminPassword 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  {translate('welcome.adminLoginButton', lang)}
                </button>
                <button 
                  onClick={() => {
                    setShowAdminLogin(false);
                    setAdminClickCount(0);
                  }}
                  className="w-full mt-2 py-2 px-4 text-sm text-gray-600 hover:text-gray-800"
                >
                  {translate('welcome.cancel', lang)}
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4">{translate('welcome.minCharsHint', lang)}</p>
            <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-4 mt-6 px-4">
            <button onClick={() => setScr('impressum')} className="text-xs text-gray-400 hover:text-gray-600 underline decoration-10 underline-offset-2 whitespace-nowrap">{translate('welcome.impressum', lang)}</button>
            <span className="text-xs text-gray-300">•</span>
            <button onClick={() => setScr('datenschutz')} className="text-xs text-gray-400 hover:text-gray-600 underline decoration-10 underline-offset-2 whitespace-nowrap">{translate('welcome.datenschutz', lang)}</button>
            <span className="text-xs text-gray-300">•</span>
            <button onClick={() => setScr('nutzungsbedingungen')} className="text-xs text-gray-400 hover:text-gray-600 underline decoration-10 underline-offset-2 whitespace-nowrap">{translate('welcome.nutzungsbedingungen', lang)}</button>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

if (scr === 'main') {
  let ts = tData[city] || [];
  
  // 🔍 Smart-Search: Toiletten filtern
  if (searchMode === 'toilet' && searchQuery && searchQuery.length >= 2) {
    const searchLower = searchQuery.toLowerCase();
    ts = ts.filter(t => {
      const name = (t.name || '').toLowerCase();
      const address = (t.address || '').toLowerCase();
      return name.includes(searchLower) || address.includes(searchLower);
    });
  }
  
  // 🔍 Toiletten filtern basierend auf Suche
  if (searchQuery && searchQuery.length >= 2 && searchMode === 'toilet') {
  const searchLower = searchQuery.toLowerCase();
    ts = ts.filter(t => {
      const name = (t.name || '').toLowerCase();
      const address = (t.address || '').toLowerCase();
      return name.includes(searchLower) || address.includes(searchLower);
    });
  }
  
  // ♿ 💰 Filter für Barrierefreiheit und Kostenlos
  if (filterAccessible) {
    ts = ts.filter(t => t.accessible === true);
  }
  if (filterFree) {
    ts = ts.filter(t => t.cost === 'kostenlos');
  }
    
    // Toiletten nach Entfernung sortieren, wenn GPS-Position vorhanden
    if (userLocation && ts.length > 0) {
      ts = sortToiletsByDistance(ts, userLocation.lat, userLocation.lng);
    }
    // Stadt wechseln Handler mit Geocoding
    const handleDetailCitySelect = async (cityData) => {
      
      setSq('');
      setShowCS(false);
      
      // GPS-Zentrierung deaktivieren 
      setUserLocation(null);
      
      // MapBounds zurücksetzen damit alle neuen Toiletten sichtbar sind
      setMapBounds(null);
      
      setTimeout(() => {
        setCity(cityData.name);
        
        // 🗺️ Karte auf neue Stadt zentrieren 
        setTimeout(() => {
          if (cityData.lat && cityData.lng) {
            setMapCenter([cityData.lat, cityData.lng]);
            setMapZoom(13); 
          }
        }, 150);
      }, 100);
    };
    
return (
  <>
{/* 🎉 APRIL FOOLS MODAL */}
    {showAprilModal && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[99999] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="text-5xl mb-3">🚽</div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">
            {translate('aprilFools.reviewTitle', lang)}
          </h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 my-4 text-left">
            <p className="text-gray-700 italic text-sm leading-relaxed">
              {translate('aprilFools.reviewText', lang)}
            </p>
            <p className="text-gray-500 text-xs mt-2 text-right">
              {translate('aprilFools.reviewAuthor', lang)}
            </p>
          </div>
          <button
            onClick={() => {
              localStorage.setItem(aprilFoolsKey, 'true');
              setShowAprilModal(false);
              setShowAprilReveal(true);
            }}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-6 rounded-xl transition-colors text-lg"
          >
            {translate('aprilFools.reviewButton', lang)}
          </button>
        </div>
      </div>
    )}

        {/* 🎉 APRIL FOOLS REVEAL MODAL */}
    {showAprilReveal && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[99999] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            {translate('aprilFools.revealTitle', lang)}
          </h2>
          <p className="text-gray-600 mb-6">
            {translate('aprilFools.revealText', lang)}
          </p>
          <button
            onClick={() => setShowAprilReveal(false)}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition-colors text-lg"
          >
            {translate('aprilFools.revealButton', lang)}
          </button>
        </div>
      </div>
    )}

    {/* 🎉 ACHIEVEMENT POPUP */}
    <AchievementPopup />
    
    {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    <div className="min-h-screen bg-gray-50 pb-20 sm:pb-0">
    <div className="bg-white shadow-sm p-2 sm:p-4 sticky top-0 z-[9998]">
      <div className="flex items-center justify-between max-w-6xl mx-auto mb-2 sm:mb-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => { setScr('welcome'); setSq(''); }} className="p-2 min-h-[44px] min-w-[44px] hover:bg-gray-100 rounded-lg flex items-center justify-center"><ArrowLeft size={20} className="sm:w-6 sm:h-6" /></button>
          <span className="text-2xl sm:text-3xl">💩</span>
          <div>
            <h1 className="text-base sm:text-xl font-bold text-amber-900 leading-tight">Restroom Advisor</h1>
            <p className="text-xs sm:text-sm text-gray-600 truncate max-w-[120px] sm:max-w-none">{city}</p>
          </div>
        </div>
        
        {/* Desktop Navigation - versteckt auf Mobile */}
        <div className="hidden sm:flex gap-2">
          <button onClick={() => setView(view === 'map' ? 'list' : 'map')} className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-gray-100 flex items-center justify-center">{view === 'map' ? <List size={24} /> : <Map size={24} />}</button>
          <button onClick={() => setScr('suggest')} className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-gray-100 flex items-center justify-center"><Edit2 size={24} /></button>
          <button onClick={() => setScr('profile')} className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-gray-100 flex items-center justify-center"><User size={24} /></button>
          
        </div>
      </div>
      <div className="max-w-6xl mx-auto relative">
          {/* 🔍 SEARCH mit Toggle + Filter - Mobile Karte: kein Toggle (automatisch Stadtsuche) */}
          {(window.innerWidth >= 640 || view === 'list') && (
          <div className="mb-3 flex gap-2 items-center overflow-x-auto bg-white rounded-lg p-1 border-2 border-gray-200">
            {/* Toiletten Button */}
            <button
              onClick={() => {
                setSearchMode('toilet');
                setShowCS(false);
                setFilterAccessible(false);
                setFilterFree(false);
              }}
              className={`px-2.5 py-2 rounded-md font-semibold text-sm transition-all ${
                searchMode === 'toilet'
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {translate('main.searchToilet', lang)}
            </button>

            {/* Städte Button */}
            <button
              onClick={() => {
                setSearchMode('city');
                setShowCS(false);
                setFilterAccessible(false);
                setFilterFree(false);
              }}
              className={`px-2.5 py-2 rounded-md font-semibold text-sm transition-all ${
                searchMode === 'city'
                  ? 'bg-amber-500 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {translate('main.searchCity', lang)}
            </button>

            {/* Barrierefrei Button */}
            <button
              onClick={() => {
                setFilterAccessible(!filterAccessible);
                if (!filterAccessible) {
                  setSearchQuery('');
                  setSq('');
                  setShowCS(false);
                }
              }}
              className={`px-2.5 py-2 rounded-md font-semibold text-sm transition-all ${
                filterAccessible
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {translate('main.filterAccessible', lang)}
            </button>

            {/* Kostenlos Button */}
            <button
              onClick={() => {
                setFilterFree(!filterFree);
                if (!filterFree) {
                  setSearchQuery('');
                  setSq('');
                  setShowCS(false);
                }
              }}
              className={`px-2.5 py-2 rounded-md font-semibold text-sm transition-all ${
                filterFree
                  ? 'bg-green-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {translate('main.filterFree', lang)}
            </button>

            {/* Reset Button - nur wenn Filter aktiv */}
            {(filterAccessible || filterFree) && (
              <button
                onClick={() => {
                  setFilterAccessible(false);
                  setFilterFree(false);
                }}
                className="px-3 py-2 rounded-md font-semibold text-sm bg-gray-600 text-white hover:bg-gray-700 transition-all"
              >
                ✕
              </button>
            )}
          </div>
          )}
          
          {/* Suchfeld - auf Mobile-Karte immer sichtbar */}
          {((window.innerWidth < 640 && view === 'map') || (!filterAccessible && !filterFree)) && (
          <div className="flex gap-2">
          <input 
            type="text"
            value={searchQuery} 
            onChange={(e) => { 
              const value = e.target.value;
              setSearchQuery(value);
              
              // Stadt-Modus: sq aktualisieren und Dropdown zeigen
              if (searchMode === 'city') {
                setSq(value);
                if (value.length >= 3) {
                  setShowCS(true);
                } else {
                  setShowCS(false);
                }
              } else {
                // Toiletten-Modus: Dropdown aus
                setShowCS(false);
              }
            }}
            onFocus={() => {
              if (searchQuery.length >= 3) {
                const currentToilets = tData[city] || [];
                if (currentToilets.length <= 2) {
                  setShowCS(true);
                }
              }
            }}
            placeholder={
              window.innerWidth < 640 && view === 'map' 
                ? translate('main.searchCityPlaceholder', lang)
                : searchMode === 'city' 
                  ? translate('main.searchCityPlaceholder', lang)
                  : translate('main.searchToiletPlaceholder', lang)
            }
            className="flex-1 p-3 text-base border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
          />
          
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSq('');
                setShowCS(false);
                setSearchMode('toilet');
              }}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-lg transition-colors"
            >
              ✕
            </button>
          )}
        </div>
          )}
            
            {/* Autocomplete-Dropdown für Städte */}
            {showCS && detailCitySuggestions.length > 0 && searchMode === 'city' && (
              <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-[9999] max-h-96 overflow-y-auto">
                {detailCitySuggestions.map((cityData, index) => {
                  const toiletCount = tData[cityData.name] ? tData[cityData.name].length : 0;
                  
                  return (
                    <button 
                      key={`${cityData.name}-${index}`} 
                      onClick={() => handleDetailCitySelect(cityData)}
                      className="w-full text-left p-3 hover:bg-amber-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <span className="font-semibold text-gray-800 block">{cityData.name}</span>
                          {cityData.state && (
                            <span className="text-xs text-gray-500 block">{cityData.state}</span>
                          )}
                        </div>
                        <div className="text-right ml-3">
                          <span className="text-xs text-gray-600 block">
                            {toiletCount > 0 ? `${toiletCount} ${toiletCount === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)}` : translate('main.results', lang)}
                          </span>
                          {cityData.population && (
                            <span className="text-xs text-gray-400 block">
                              {(cityData.population / 1000).toFixed(0)}k Einw.
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Lade-Anzeige für Städte */}
            {searchingDetailCities && searchMode === 'city' && (
              <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-[9999] p-4 text-center">
                <div className="text-amber-600 animate-pulse">🔍 Suche Städte...</div>
              </div>
            )}
            
            {/* Keine Städte gefunden */}
            {!searchingDetailCities && searchQuery.length >= 3 && detailCitySuggestions.length === 0 && searchMode === 'city' && (
              <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-[9999] p-4 text-center">
                {getEasterEgg(searchQuery, lang) ? (
                  <>
                    <div className="text-2xl mb-2">{getEasterEgg(searchQuery, lang).split(' ')[0]}</div>
                    <div className="text-gray-700 text-sm font-medium">{getEasterEgg(searchQuery, lang).substring(3)}</div>
                  </>
                ) : (
                  <>
                    <div className="text-gray-500 text-sm">
                      {translate('welcome.noCitiesFound', lang)} "{searchQuery}"
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {translate('welcome.tryDifferentSpelling', lang)}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
         </div> 
        <div className="flex-1 max-w-6xl w-full mx-auto p-4">

        {/* TOILETTEN-ANZAHL - vor Karte/Liste */}
          <div className="mb-4">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
            {(() => {
              const total = tData[city] ? tData[city].length : 0;
              const filtered = ts.length;
              const isMapVisible = window.innerWidth >= 640 || view === 'map';
              const visible = isMapVisible ? filterToiletsInBounds(ts, mapBounds, userLocation).length : filtered;
              const hasActiveFilters = filterAccessible || filterFree;
              
              if (searchMode === 'toilet' && searchQuery && searchQuery.length >= 2) {
                return (
                  <span>
                    🔍 {filtered} {filtered !== 1 ? translate('main.resultsForPlural', lang) : translate('main.resultsFor', lang)} "{searchQuery}"
                    <span className="text-xs sm:text-sm font-normal text-gray-600 ml-2">
                      ({translate('main.of', lang)} {total} {translate('main.total', lang)})
                    </span>
                  </span>
                );
              }

              if (hasActiveFilters) {
                const filterText = [];
                if (filterAccessible) filterText.push('♿');
                if (filterFree) filterText.push('💰');
                
                if (isMapVisible && mapBounds && visible < filtered) {
                  return (
                    <span>
                      {visible} / {filtered} {visible === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)}
                      <span className="text-xs sm:text-sm font-normal text-gray-600 ml-2">
                        ({filterText.join(' ')} {translate('main.visible', lang)})
                      </span>
                    </span>
                  );
                }
                
                return (
                  <span>
                    {filtered} / {total} {filtered === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)}
                    <span className="text-xs sm:text-sm font-normal text-gray-600 ml-2">
                      ({filterText.join(' ')})
                    </span>
                  </span>
                );
              }

              if (isMapVisible && mapBounds && visible < filtered) {
                return (
                  <span>
                    {visible} / {total} {visible === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)} 
                    <span className="text-xs sm:text-sm font-normal text-gray-600 ml-2">
                      ({translate('main.imSichtbarenBereich', lang)})
                    </span>
                  </span>
                );
              }

               return `${filtered} ${filtered === 1 ? translate('main.toiletsFoundSingular', lang) : translate('main.toiletsFoundPlural', lang)}`;
            })()}
            </h2>
          </div>

          {/* Keine Toiletten gefunden */}
        {scr === 'list' && city && (!ts || ts.length === 0) && !loading && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🚽❌</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Keine Toiletten in {city}
            </h2>
            <p className="text-gray-600 mb-6">
              Für diese Stadt sind noch keine Toiletten in der Datenbank.
            </p>
            <button
              onClick={() => setScr('welcome')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg"
            >
              ← Zurück zur Stadtauswahl
            </button>
          </div>
        )}

        {/* KARTEN-ANSICHT: Mobile nur wenn view='map', ab Tablet immer sichtbar */}
        {(view === 'map' || window.innerWidth >= 640) && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4 relative h-[calc(100dvh-380px)] sm:h-[500px]">
          <MapView
            key={`map-${city}-${ts.length}`}
            toilets={filterToiletsInBounds(ts, mapBounds, userLocation)}
            allToilets={ts}
            onToiletClick={(toilet) => {
              setHoveredToilet(toilet);
              
              if (toilet.lat && toilet.lng) {
                setMapCenter([toilet.lat, toilet.lng]);
                setMapZoom(17); 
              }
            }}
            userLocation={userLocation}
            onLocationRequest={handleGetLocation}
            onBoundsChange={handleMapBoundsChange}
            onCenterChange={handleMapCenterChange}
            maxToiletsDisplay={MAX_TOILETS_DISPLAY}
            currentCity={city}
            center={mapCenter}
            zoom={mapZoom}
            southPoleMode={effectiveAprilFools ? southPoleMode : false}
            onToggleSouthPole={() => setSouthPoleMode(prev => !prev)}
            isAprilFools={effectiveAprilFools}
            lang={lang}
            infoBadgeOf={translate('main.infoBadgeOf', lang)}
            infoBadgeShown={translate('main.infoBadgeShown', lang)}
            infoBadgeMove={translate('main.infoBadgeMove', lang)}
                    />

          {/* Preview-Card bei Marker-Klick */}
          {hoveredToilet && (
            <>
              {/* Klick auf Karte zum Schließen */}
              <div 
                className="absolute inset-0 z-[999]"
                onClick={() => setHoveredToilet(null)}
              />
              
              <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl shadow-2xl overflow-hidden z-[1000] border-2 border-amber-500 max-w-sm mx-auto">
                {/* Bild mit X oben rechts */}
                <div className="relative w-full h-48 bg-gray-100">
                  {(() => {
                    const allImages = hoveredToilet.images || [];
                    const approvedImages = getApprovedPhotos(allImages);
                    const previewImage = approvedImages.length > 0 ? getPhotoUrl(approvedImages[0]) : ph;
                    return (
                      <img 
                        src={previewImage}
                        alt={hoveredToilet.name || 'Toilette'} 
                        className="w-full h-full object-contain"
                      />
                    );
                  })()}
                  
                  {/* X Button oben rechts */}
                  <button
                    onClick={() => setHoveredToilet(null)}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors"
                    title="Schließen"
                  >
                    ✕
                  </button>
                </div>

                {/* Info-Bereich */}
                <div className="p-4">
                  {/* Name + Navigation nebeneinander */}
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-bold text-gray-800 text-base line-clamp-2 flex-1">
                      {hoveredToilet.name}
                    </h3>
                    
                    {/* Navigation Button (kompakt, rechts) */}
                    <button
                      onClick={() => {
                        handleOpenNavigation(hoveredToilet);
                        setHoveredToilet(null);
                      }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-1.5 text-sm flex-shrink-0"
                      title="Navigation starten"
                    >
                      <Navigation size={16} />
                      Nav
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-1">
                    📍 {hoveredToilet.address}
                  </p>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-amber-500 text-lg">
                        {i < Math.floor(hoveredToilet.rating || 0) ? '⭐' : '☆'}
                      </span>
                    ))}
                    <span className="text-sm font-semibold ml-1">
                      {(() => {
                        if (hoveredToilet.rating !== undefined && hoveredToilet.rating !== null && hoveredToilet.rating > 0) {
                          return hoveredToilet.rating.toFixed(1);
                        }
                        
                        const addedDate = hoveredToilet.approvedDate || hoveredToilet.suggestedDate;
                        if (addedDate) {
                          const daysSinceAdded = Math.floor((Date.now() - new Date(addedDate).getTime()) / (1000 * 60 * 60 * 24));
                        if (daysSinceAdded < 30) {
                          return lang === 'en' ? '🆕 New' : '🆕 Neu';
                        }
                      }
                        
                        return '';
                      })()}
                    </span>
                  </div>

                  {/* Details Button (volle Breite) */}
                  <button
                    onClick={() => {
                      setSel(hoveredToilet);
                      setSelectedImageIndex(0);
                      setScr('detail');
                      setHoveredToilet(null);
                    }}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
                  >
                    📋 {translate('detail.showDetails', lang)}
                  </button>
                </div>
              </div>
            </>
          )}
      </div>
    )}

    {/* Stadt-Wechsel Vorschlag */}
          {showCitySwitch && suggestedCity && view === 'map' && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1001]">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-6 rounded-2xl shadow-2xl max-w-lg w-[90vw]">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">🏙️</span>
                  <div>
                    <h3 className="font-bold text-lg">Neue Stadt erkannt!</h3>
                    <p className="text-sm opacity-90">Du schaust gerade nach <strong>{suggestedCity}</strong></p>
                  </div>
                </div>
                
                <div className="flex gap-3 mb-3">
                  <button
                    onClick={handleSwitchCity}
                    className="flex-1 bg-white text-blue-600 font-bold py-3 px-6 rounded-xl hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
                  >
                    ✓ Stadt wechseln
                  </button>
                  <button
                    onClick={() => {
                      setShowCitySwitch(false);
                    }}
                    className="flex-1 bg-white/20 text-white font-bold py-3 px-6 rounded-xl hover:bg-white/30 transition-all hover:scale-105 border-2 border-white/30"
                  >
                    ✕ Bleiben
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    localStorage.setItem('restroomadvisor_dont_ask_city_switch', 'true');
                    setShowCitySwitch(false);
                    showToast(translate('toasts.citySwitchDisabledToast', lang), 'info');
                  }}
                  className="w-full text-xs text-white/70 hover:text-white underline"
                >
                  Nicht mehr automatisch fragen
                </button>
              </div>
            </div>
          )}
                    
          {/* LISTEN-ANSICHT: Mobile nur wenn view='list', ab Tablet immer sichtbar */}
          {(view === 'list' || window.innerWidth >= 640) && (
          <div className="space-y-4">
        
            {[...ts].sort((a, b) => {
              if (hoveredToilet) {
                if (a.id === hoveredToilet.id) return -1;
                if (b.id === hoveredToilet.id) return 1;
              }
              return 0;
            }).map(t => {
              const isHighlighted = hoveredToilet && t.id === hoveredToilet.id;
              
              return (
              <div key={t.id} onClick={() => { 
                setSel(t); 
                setSelectedImageIndex(0);
                setHoveredToilet(null); 
                
                if (t.lat && t.lng) {
                  setMapCenter([t.lat, t.lng]);
                  setMapZoom(16); 
                }
                
                setScr('detail'); 
              }} className={`bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-lg active:scale-[0.98] transition-transform ${
                isHighlighted ? 'ring-4 ring-amber-500 ring-opacity-50' : ''
              }`}>
                <div className="flex flex-col sm:flex-row">
                <img 
                  src={(() => {
                    if (t.images && t.images.length > 0) {
                      const approvedImages = getApprovedPhotos(t.images);
                      if (approvedImages.length > 0) {
                        return getPhotoUrl(approvedImages[0]);
                      }
                    }
                    return ph;
                  })()} 
                  alt={t.name ? `Foto der Toilette ${t.name}` : 'Foto einer öffentlichen Toilette'}
                  className="w-full h-48 sm:w-32 sm:h-32 object-cover" 
                />
                <div className="flex-1 p-3 sm:p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-bold text-base sm:text-lg text-gray-800 line-clamp-1">{getToiletDisplayName(t, ts)}</h3>
                      {t.isClosed && (
                        <span className="bg-red-600 text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                          🔒 {translate('listView.closed', lang)}
                        </span>
                      )}
                      {userLocation && t.distance !== undefined && (
                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                          <Navigation size={12} />
                          {formatDistance(t.distance)}
                        </span>
                      )}
                     {t.isPending && <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-1 rounded">{translate('listView.suggestion', lang)}</span>}
                      {clRep[`${city}-${t.id}`] && <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded">⚠ {clRep[`${city}-${t.id}`].count}{translate('listView.timesReported', lang)}</span>} 

                    {(() => {
                      const openStatus = getOpeningStatus(t);
                      if (!openStatus || openStatus.type === 'unknown') return null;
                      
                      return (
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          openStatus.isOpen === true ? 'bg-green-100 text-green-800' :
                          openStatus.isOpen === false ? 'bg-red-100 text-red-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {openStatus.isOpen === true ? `🟢 ${translate('listView.open', lang)}` :
                          openStatus.isOpen === false ? `🔴 ${translate('listView.closed', lang)}` :
                          '⏰'}
                        </span>
                      );
                    })()}
                    </div>
                    <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                      <MapPin size={14} />
                      {t.address && !t.address.includes('Unbekannt') 
                        ? t.address 
                        : formatCoordinates(t.lat, t.lng)}
                    </p>
                   <div className="flex items-center gap-2 mb-2">
                      <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={16} className={i < Math.floor(t.rating) ? 'fill-amber-500 text-amber-500' : 'text-gray-300'} />)}</div>
                      <span className="text-sm font-semibold">{t.rating}</span>
                      <span className="text-sm text-gray-500">
                        ({(t.comments || []).length} {(t.comments || []).length === 1 
                          ? translate('listView.review', lang)
                          : translate('listView.reviews', lang)})
                      </span>
                    </div>
                    {t.lastVisited && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Clock size={12} />
                        {translate('listView.lastVisited', lang)} {new Date(t.lastVisited).toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE')}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {t.cost === 'kostenlos' && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          💰 {translate('listView.free', lang)}
                        </span>
                      )}
                      {t.cost === 'kostenpflichtig' && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                          💰 {t.costAmount ? formatCostAmount(t.costAmount) : translate('listView.paid', lang)}
                        </span>
                      )}
                      {t.accessible && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          ♿ {translate('listView.accessible', lang)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              ); 
            })}
          </div>
          )}
        </div>
        
        {/* BOTTOM NAVIGATION - Nur auf Mobile sichtbar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 sm:hidden z-[9999] safe-area-bottom">
          <div className="flex justify-around items-center px-2 py-3">
            <button 
              onClick={() => setView('map')} 
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-[64px] transition-colors ${
                view === 'map' ? 'text-amber-600 bg-amber-50' : 'text-gray-600'
              }`}
            >
              <Map size={24} />
              <span className="text-xs mt-1 font-medium">{translate('main.navMap', lang)}</span>
            </button>
            
            <button 
              onClick={() => setView('list')} 
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-[64px] transition-colors ${
                view === 'list' ? 'text-amber-600 bg-amber-50' : 'text-gray-600'
              }`}
            >
              <List size={24} />
              <span className="text-xs mt-1 font-medium">{translate('main.navList', lang)}</span>
            </button>
            
            <button 
              onClick={() => setScr('suggest')} 
              className="flex flex-col items-center justify-center p-2 rounded-lg min-w-[64px] text-gray-600 transition-colors hover:text-amber-600"
            >
              <div className="bg-amber-600 text-white rounded-full p-2 mb-1">
                <Edit2 size={20} />
              </div>
              <span className="text-xs font-medium">{translate('main.navSuggest', lang)}</span>
            </button>
            
            <button 
              onClick={() => setScr('profile')} 
              className={`flex flex-col items-center justify-center p-2 rounded-lg min-w-[64px] transition-colors ${
                scr === 'profile' ? 'text-amber-600 bg-amber-50' : 'text-gray-600'
              }`}
            >
              <User size={24} />
              <span className="text-xs mt-1 font-medium">{translate('main.navProfile', lang)}</span>
            </button>
          </div>
        </div>
        
      </div>
      </>
    );
  }

  if (scr === 'detail' && sel) {
  const allImages = sel.images || [];
  const approvedImages = getApprovedPhotos(allImages);
  
  // URLs extrahieren
  const approvedUrls = approvedImages.map(img => getPhotoUrl(img));
  const allUrls = allImages.map(img => getPhotoUrl(img));
  
  // Fallback auf Placeholder wenn keine Fotos
  const images = approvedUrls.length > 0 ? approvedUrls : [ph];
  const imagesForAdmin = allUrls.length > 0 ? allUrls : [ph];
  
  // Admin sieht ALLE Fotos (approved + pending), normale User nur approved
  const displayImages = isAdmin ? imagesForAdmin : images;
    
    return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="min-h-screen bg-gray-50 pb-20 sm:pb-0">
        <div className="bg-white shadow-sm p-2 sm:p-4 sticky top-0 z-[9998]">
          <div className="flex items-center gap-2 sm:gap-3 max-w-6xl mx-auto">
            <button onClick={() => setScr('main')} className="p-2 min-h-[44px] min-w-[44px] hover:bg-gray-100 rounded-lg flex items-center justify-center">
              <ArrowLeft size={20} className="sm:w-6 sm:h-6" />
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800 flex-1">{translate('detail.details', lang)}</h1>
          </div>
        </div>

        <div className="max-w-6xl mx-auto p-3 sm:p-4">

          {/* Geschlossen-Warnung */}
          {sel.isClosed && (
            <div className="bg-red-600 text-white border-2 border-red-700 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 font-bold text-lg mb-2">
                🔒 {translate('detail.closed', lang)}
              </div>
              <p className="mb-2">
                <strong>{translate('detail.reason', lang)}:</strong> {sel.closedReason || translate('detail.notSpecified', lang)}
              </p>
              {sel.closedUntil && (
                <p className="mb-2">
                  <strong>{translate('detail.until', lang)}:</strong> {new Date(sel.closedUntil).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}
                </p>
              )}
              <p className="text-sm text-red-100">
                {translate('detail.closedAt', lang)} {sel.closedAt ? new Date(sel.closedAt.seconds * 1000).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US') : translate('common.unknown', lang)}
              </p>
              
              {/* Admin kann wieder öffnen */}
              {isAdmin && (
                <button
                  onClick={async () => {
                    if (!window.confirm('Toilette wieder als geöffnet markieren?')) return;
                    
                    setLoading(true);
                    try {
                      await reopenToilet(sel.id);
                      
                      const updatedToilet = { ...sel, isClosed: false, closedReason: null };
                      setSel(updatedToilet);
                      
                      const updatedTData = { ...tData };
                      const cityToilets = updatedTData[city] || [];
                      const toiletIndex = cityToilets.findIndex(t => t.id === sel.id);
                      if (toiletIndex !== -1) {
                        cityToilets[toiletIndex] = updatedToilet;
                        updatedTData[city] = cityToilets;
                        setTData(updatedTData);
                      }
                      
                      showToast(translate('toasts.toiletReopened', lang), 'success');
                    } catch (error) {
                      console.error('❌ Fehler:', error);
                      showToast(translate('toasts.toiletReopenError', lang), 'error');
                    }
                    setLoading(false);
                  }}
                  className="mt-3 w-full bg-white text-red-600 font-bold py-2 px-4 rounded-lg hover:bg-red-50"
                >
                  🔓 Toilette wieder öffnen (Admin)
                </button>
              )}
            </div>
          )}

          {clRep[`${city}-${sel.id}`] && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
               ⚠ {translate('detail.reportedClosedTimes', lang).replace('{count}', clRep[`${city}-${sel.id}`].count)}
              </div>
              <p className="text-sm text-red-700">
                {translate('detail.lastReport', lang)} {clRep[`${city}-${sel.id}`].reports[clRep[`${city}-${sel.id}`].reports.length - 1].reason}
              </p>
            </div>
          )}

          {sel.isPending && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2">
                💡 {translate('detail.suggested', lang)}
              </div>
              <p className="text-sm text-amber-700">
                {translate('detail.suggestedBy', lang)} {sel.suggestedBy} {lang === 'de' ? 'am' : 'on'} {new Date(sel.suggestedDate).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}. {translate('detail.waitingForApproval', lang)}.
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-4">
            <div className="relative w-full h-[50vh] sm:h-[500px] bg-gray-100">
              <img
                src={displayImages[selectedImageIndex]}
                alt={sel.name ? `Toiletten-Foto ${selectedImageIndex + 1} von ${displayImages.length} - ${sel.name}` : `Toiletten-Foto ${selectedImageIndex + 1} von ${displayImages.length}`}
                className="w-full h-full object-contain bg-gray-50"
              />
              
              {/* Admin: Foto löschen */}
              {isAdmin && (sel.images || []).length > 0 && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    
                    const photoUrl = displayImages[selectedImageIndex];
                    if (!window.confirm('Dieses Foto wirklich löschen?')) return;
                    
                    setLoading(true);
                   try {
                    await deletePhoto(sel.id, photoUrl);
                    
                    // Stadt neu laden für frische Daten
                    const freshToilets = await getToiletsByCity(city);
                    const freshToilet = freshToilets.find(t => t.id === sel.id);
                    
                    if (freshToilet) {
                      setSel(freshToilet);
                      
                      const updatedTData = { ...tData };
                      updatedTData[city] = freshToilets;
                      setTData(updatedTData);
                      
                      setSelectedImageIndex(0);
                    }
                    
                    showToast(translate('toasts.photoDeleted', lang), 'success');
                  } catch (error) {
                    console.error('❌ Fehler:', error);
                    showToast(translate('toasts.photoDeleteError', lang), 'error');
                  }
                  setLoading(false);
                  }}
                  className="absolute top-4 right-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg shadow-lg flex items-center gap-2 transition-colors z-10"
                >
                  🗑️ Foto löschen
                </button>
              )}
              
              {displayImages.length > 0 && (
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                  {selectedImageIndex + 1} / {displayImages.length}
                </div>
              )}
              
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImageIndex(selectedImageIndex === 0 ? displayImages.length - 1 : selectedImageIndex - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setSelectedImageIndex(selectedImageIndex === displayImages.length - 1 ? 0 : selectedImageIndex + 1)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full shadow-lg transition-all"
                  >
                    →
                  </button>
                </>
              )}
            </div>

            {displayImages.length > 0 && (
              <div className="p-4 bg-gray-50">
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {displayImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden transition-all ${
                        selectedImageIndex === index 
                          ? 'ring-4 ring-amber-500 scale-105' 
                          : 'ring-2 ring-gray-200 hover:ring-amber-300 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Vorschaubild ${index + 1} von ${displayImages.length} der Toilette`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          {/* Name + Karten-Button nebeneinander */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 className="text-2xl font-bold text-gray-800 flex-1">
              {getToiletDisplayName(sel, tData[city] || [])}
            </h2>
            
            {/* Karten-Button */}
            <button
              onClick={() => handleShowOnMap(sel)}
              className="px-3 py-2 min-h-[44px] bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2 text-sm flex-shrink-0"
              title={translate('detail.showOnMap', lang)}
            >
              <Map size={18} />
              <span className="hidden sm:inline">{translate('detail.showOnMap', lang)}</span>
              <span className="sm:hidden">{lang === 'en' ? 'Map' : 'Karte'}</span>
            </button>
          </div>
          
          {/* Adresse + Navigation nebeneinander */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="text-gray-600 flex-1">
              <div className="flex items-start gap-2">
                <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                <div>
                  {(() => {
                    if (!sel.address || sel.address.includes('Unbekannt')) {
                      return <span>{formatCoordinates(sel.lat, sel.lng)}</span>;
                    }
                    
                    const parts = sel.address.split(',').map(p => p.trim());
                    
                    if (parts.length >= 2) {
                      return (
                        <>
                          <div>{parts[0]}</div>
                          <div>{parts[1]}</div>
                        </>
                      );
                    } else {
                      return <span>{sel.address}</span>;
                    }
                  })()}
                </div>
              </div>
            </div>
            
            {/* Kompakter Navigation-Button */}
            <button
              onClick={() => handleOpenNavigation(sel)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-1.5 text-sm flex-shrink-0"
              title={translate('detail.startNavigation', lang)}
            >
              <Navigation size={16} />
              {translate('detail.navigation', lang)}
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4 pb-4 border-b">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={24}
                  className={i < Math.floor(sel.rating) ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}
                />
              ))}
            </div>
            <span className="text-2xl font-bold">{sel.rating}</span>
            <span className="text-gray-600">
              ({(sel.comments || []).length} {(sel.comments || []).length === 1 
                ? lang === 'en' ? 'review' : 'Bewertung'
                : lang === 'en' ? 'reviews' : 'Bewertungen'})
            </span>
          </div>

          {sel.lastVisited && (
            <div className="bg-green-50 p-3 rounded-lg mb-4 flex items-center gap-2">
              <Clock size={18} className="text-green-600" />
              <span className="text-green-800">
                {translate('detail.lastVisited', lang)}: {new Date(sel.lastVisited).toLocaleDateString(lang === 'en' ? 'en-US' : 'de-DE')}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b">
            <div className={`p-3 rounded-lg ${sel.cost === 'kostenlos' ? 'bg-green-50 border-2 border-green-200' : 'bg-amber-50 border-2 border-amber-200'}`}>
              <div className="text-2xl mb-1">💰</div>
              <div className="font-semibold text-gray-800">
                {sel.cost === 'kostenlos' 
                  ? translate('detail.free', lang)
                  : (sel.costAmount ? formatCostAmount(sel.costAmount) : translate('detail.paid', lang))}
              </div>
              <div className="text-xs text-gray-600">{translate('detail.cost', lang)}</div>
            </div>
            
            {sel.accessible !== undefined && (
              <div className={`p-3 rounded-lg ${sel.accessible ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 border-2 border-gray-200'}`}>
                <div className="text-2xl mb-1">♿</div>
                <div className="font-semibold text-gray-800">
                  {sel.accessible ? translate('detail.accessible', lang) : translate('detail.notAccessible', lang)}
                </div>
                <div className="text-xs text-gray-600">{translate('detail.accessibility', lang)}</div>
              </div>
            )}

            {(() => {
              const openStatus = getOpeningStatus(sel);
              if (!openStatus || openStatus.type === 'unknown') return null;
              
              return (
                <div className={`p-3 rounded-lg ${
                  openStatus.isOpen === true ? 'bg-green-50 border-2 border-green-200' :
                  openStatus.isOpen === false ? 'bg-red-50 border-2 border-red-200' :
                  'bg-purple-50 border-2 border-purple-200'
                }`}>
                  <div className="text-2xl mb-1">
                    {openStatus.isOpen === true ? '🟢' :
                     openStatus.isOpen === false ? '🔴' :
                     '🕐'}
                  </div>
                  <div className="font-semibold text-gray-800">
                    {openStatus.isOpen === true ? translate('detail.nowOpen', lang) :
                     openStatus.isOpen === false ? translate('detail.currentlyClosed', lang) :
                     translate('detail.openingHours', lang)}
                  </div>
                  <div className="text-sm text-gray-600">{openStatus.display}</div>
                </div>
              );
            })()}

            {sel.amenities && sel.amenities.length > 0 && (
              <div className="p-3 rounded-lg bg-indigo-50 border-2 border-indigo-200">
                <div className="text-2xl mb-1">🚿</div>
                <div className="font-semibold text-gray-800">
                  {sel.amenities.map(a => translateAmenity(a, lang)).join(', ')}
                </div>
                <div className="text-xs text-gray-600">{translate('detail.amenities', lang)}</div>
              </div>
            )}
          </div>

          <button 
            onClick={openRevForm}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg mb-2 transition-colors flex items-center justify-center gap-2"
          >
            <Star size={20} />
            {uRev.find(r => r.toiletId === sel.id) 
              ? translate('detail.editReview', lang)
              : translate('detail.rateButton', lang)}
          </button>

          <button 
            onClick={() => setScr('report-closure')}
            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg mb-2 transition-colors flex items-center justify-center gap-2"
          >
            ⚠ {translate('detail.reportChange', lang)}
          </button>

          {/* 🔒 Admin-Only Buttons */}
          {isAdmin && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              {/* Bearbeiten Button */}
              <button 
                onClick={() => {
                  setEditMode(true);
                  setEditToiletId(sel.id);

                  setSNam(sel.name || '');
                  setSAdd(sel.address || '');
                  setSCity(sel.city || city);
                  setSCoordinates({ lat: sel.lat, lng: sel.lng });
                  setSCost(sel.cost || '');
                  setSCostAmount(sel.costAmount || '');
                  setSAccessible(sel.accessible === true ? 'ja' : sel.accessible === false ? 'nein' : '');
                  setSOpeningHours(sel.openingHours || '');
                  setSAmenities(sel.amenities || []);

                  setScr('suggest');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {translate('common.edit', lang)}
              </button>

              {/* Löschen Button */}
              <button 
                onClick={async () => {
                  if (!window.confirm(`"${sel.name}" wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) return;
                  
                  setLoading(true);
                  try {
                    await deleteToilet(sel.id);
                    
                    const updatedTData = { ...tData };
                    const cityToilets = updatedTData[city] || [];
                    const filteredToilets = cityToilets.filter(t => t.id !== sel.id);
                    updatedTData[city] = filteredToilets;
                    setTData(updatedTData);
                    
                    showToast(translate('toasts.toiletDeleted', lang), 'success');
                    
                    setScr('main');
                  } catch (error) {
                    console.error('❌ Fehler beim Löschen:', error);
                    showToast(translate('toasts.toiletDeleteError', lang), 'error');
                  }
                  setLoading(false);
                }}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                🗑️ {translate('common.delete', lang)}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{translate('detail.reviews', lang)} ({(sel.comments || []).length})</h3>
          {(sel.comments || []).length === 0 ? (
            <p className="text-gray-500 text-center py-8">{translate('detail.noReviews', lang)}</p>
          ) : (
            <div className="space-y-4">
              {(sel.comments || []).map((c, i) => (
                <div key={i} className="border-b pb-4 last:border-b-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{c.user}</span>
                      {c.userRank && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">
                          {c.userRank.icon} {c.userRank.name}
                        </span>
                      )}
                      <span className="text-sm text-gray-500">
                        {new Date(c.date).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                    
                    {/* Admin: Bewertung löschen */}
                    {isAdmin && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Bewertung von "${c.user}" wirklich löschen?`)) return;
                          
                          setLoading(true);
                          try {
                            const result = await deleteReview(sel.id, i, c.user);

                            const updatedComments = (sel.comments || []).filter((_, index) => index !== i);
                            const updatedToilet = {
                              ...sel,
                              comments: updatedComments,
                              rating: result.newRating,
                              reviews: result.newReviewCount
                            };
                            setSel(updatedToilet);

                            const updatedTData = { ...tData };
                            const cityToilets = updatedTData[city] || [];
                            const toiletIndex = cityToilets.findIndex(t => t.id === sel.id);
                            if (toiletIndex !== -1) {
                              cityToilets[toiletIndex] = updatedToilet;
                              updatedTData[city] = cityToilets;
                              setTData(updatedTData);
                            }
                            
                            showToast(translate('toasts.reviewDeleted', lang), 'success');
                          } catch (error) {
                            console.error('❌ Fehler:', error);
                            showToast(translate('toasts.reviewDeleteError', lang), 'error');
                          }
                          setLoading(false);
                        }}
                        className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-semibold rounded-lg flex items-center gap-1 transition-colors"
                      >
                        🗑️ Löschen
                      </button>
                    )}
                    
                    {/* 🚩 Melden-Button für alle Nutzer */}
                    <button
                      onClick={() => handleReportComment(sel.id, i, c)}
                      className="px-2 py-1 text-gray-500 hover:text-red-600 hover:bg-red-50 text-sm rounded-lg flex items-center gap-1 transition-colors"
                      title="Kommentar melden"
                    >
                      🚩
                    </button>
                  </div>
                  {/* Sterne nur anzeigen wenn Rating > 0 */}
                  {c.rating > 0 ? (
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          size={14}
                          className={j < c.rating ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                  ) : c.isPhotoOnly || (c.photos && c.photos.length > 0 && !c.text) ? (
                    <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                      <Camera size={12} />
                      <span>Foto-Beitrag</span>
                    </div>
                  ) : null}
                  {c.text && <p className="text-gray-700 mb-2">{c.text}</p>}
                  {c.photos && c.photos.length > 0 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto">
                      {getApprovedPhotos(c.photos).map((p, pi) => (
                        <div key={pi} className="relative">
                          <img
                            src={getPhotoUrl(p)}
                            alt={`Bewertungs-Foto ${pi + 1} von ${c.user || 'Unbekannt'} für ${sel.name || 'diese Toilette'}`}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                          {/* Admin: Zeige Status-Badge */}
                          {isAdmin && typeof p === 'object' && (
                            <div className={`absolute top-1 right-1 px-2 py-0.5 rounded text-xs font-bold ${
                              p.status === 'approved' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-black'
                            }`}>
                              {p.status === 'approved' ? '✓' : '⏳'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

if (scr === 'review' && sel) {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => setScr('detail')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
         <h1 className="text-xl font-bold text-gray-800">{uRev.find(r => r.toiletId === sel.id) ? translate('detail.editReview', lang) : translate('detail.rateButton', lang)}</h1>
        </div>
      </div>
      <div className="max-w-6xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <div className="flex items-center gap-4">
           <div className="flex-shrink-0">
              <img 
                src={(sel.images || []).length > 0 ? getPhotoUrl(sel.images[0]) : ph}
                alt={sel.name ? `Vorschaubild der Toilette ${sel.name}` : 'Vorschaubild einer Toilette'} 
                className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200 shadow-sm" 
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-800 mb-1">{sel.name}</h2>
              <p className="text-sm text-gray-600">{sel.address}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('review.rateYourVisit', lang)}</h3>
          <div className="flex items-center justify-center gap-2 py-6">
            {[1,2,3,4,5].map((s) => (
              <button key={s} onClick={() => setRRat(s)} onMouseEnter={() => setHRat(s)} onMouseLeave={() => setHRat(0)} className="transition-transform hover:scale-110">
                <Star size={48} className={s <= (hRat || rRat) ? 'fill-amber-500 text-amber-500' : 'text-gray-300'} />
              </button>
            ))}
          </div>
          <p className="text-center text-gray-600">
            {rRat === 0 && translate('review.tapToRate', lang)}
            {rRat === 1 && `⭐ ${translate('review.veryBad', lang)}`}
            {rRat === 2 && `⭐⭐ ${translate('review.bad', lang)}`}
            {rRat === 3 && `⭐⭐⭐ ${translate('review.okay', lang)}`}
            {rRat === 4 && `⭐⭐⭐⭐ ${translate('review.good', lang)}`}
            {rRat === 5 && `⭐⭐⭐⭐⭐ ${translate('review.excellent', lang)}`}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('review.commentLabel', lang)}</h3>
          <textarea
            value={rCom}
            onChange={(e) => {
              setRCom(e.target.value.slice(0, 300));
            }}
            onBlur={() => {
              const cleaned = sanitizeComment(rCom);
              setRCom(cleaned);
            }}
            placeholder={translate('review.commentPlaceholder', lang)}
            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none resize-none" 
            rows="5" 
            maxLength={300}
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-gray-500">
              {translate('validation.allowedCharsComment', lang)}
            </p>
            <p className={`text-sm font-semibold ${rCom.length >= 280 ? 'text-red-600' : 'text-gray-500'}`}>
              {rCom.length}/300 {translate('common.characters', lang)}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('review.addPhotos', lang)}</h3>
          
          {/* Netiquette-Hinweis (immer sichtbar) */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-3">
            <span className="text-xl flex-shrink-0">⚠️</span>
            <div className="text-sm">
              <p className="text-amber-900 font-semibold mb-1">
                {translate('review.importantReminder', lang)}
              </p>
              <p className="text-amber-800">
                {translate('review.photoReminderText', lang)}
              </p>
            </div>
          </div>

          <label className="block w-full cursor-pointer">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-amber-500">
              <Camera size={48} className="mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600 font-semibold mb-1">{translate('review.uploadPhotos', lang)}</p>
              <p className="text-sm text-gray-500">{translate('review.uploadHint', lang)}</p>
            </div>
            <input type="file" accept="image/*" multiple onChange={handlePhUp} className="hidden" />
          </label>
          {uPho.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">{translate('review.photosSelected', lang).replace('{count}', uPho.length)}</p>
              <div className="grid grid-cols-3 gap-3">
                {uPho.map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p} alt="Foto von Toilette" className="w-full h-24 object-cover rounded-lg" />
                    <button onClick={() => setUPho(uPho.filter((_, idx) => idx !== i))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100">×</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Bestätigungs-Hinweis */}
          {uPho.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-900">
                {translate('review.confirmationText', lang)}
              </p>
            </div>
          )}
        <button onClick={handleSubRev} disabled={rRat === 0 && uPho.length === 0}
          className={`w-full py-4 px-6 rounded-lg font-bold text-white text-lg ${
            rRat === 0 && uPho.length === 0 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-amber-600 hover:bg-amber-700'
          }`}>
          {rRat === 0 && uPho.length === 0 
            ? translate('review.submitDisabled', lang)
            : rRat === 0 && uPho.length > 0
              ? translate('review.submitPhotosOnly', lang).replace('{count}', uPho.length)
              : uRev.find(r => r.toiletId === sel.id) 
                ? translate('review.updateReview', lang)
                : translate('review.submitReview', lang)}
        </button>
      </div>
    </main>
    </>
  );
}

if (scr === 'suggest') {
  const handleSPhUp = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    
    if (files.length === 0) return;
    
    // Prüfe Netiquette
    const netiquetteAcceptedBefore = localStorage.getItem('restroomadvisor_netiquette_accepted');
    
    if (!netiquetteAcceptedBefore) {
      setPendingPhotoFiles(files);
      setPendingPhotoContext('suggest');
      setShowNetiquetteModal(true);
      e.target.value = '';
      return;
    }
    
    // Netiquette akzeptiert - Fotos laden
    files.forEach(f => {
      const r = new FileReader();
      r.onload = (ev) => setSPho(p => [...p, ev.target.result]);
      r.readAsDataURL(f);
    });
    e.target.value = '';
  };
  const handleSubSug = async () => {
    // Validierung
    if (!sNam || (!sAdd && !sCoordinates) || !sCost) { 
      showToast(translate('suggest.validation.requiredFields', lang), 'error');
     return; 
    }
    if (sCost === 'kostenpflichtig' && !sCostAmount) {
      showToast(translate('suggest.validation.priceRequired', lang), 'error');
      return;
    }
    
    // Koordinaten-Validation: Prüfen ob Koordinaten in der gewählten Stadt liegen
    if (sCoordinates && sCity && city && sCity.toLowerCase() !== city.toLowerCase()) {
      const confirmDifferentCity = window.confirm(
        translate('suggest.validation.confirmDifferentCity', lang)
          .replace('{sCity}', sCity)
          .replace('{city}', city)
      );
      
      if (!confirmDifferentCity) {
        return;
      }
    }
    
    // Toilette aktualisieren
    if (editMode && editToiletId) {
      setLoading(true);
      try {
        let uploadedPhotoURLs = [];
        if (sPho.length > 0) {
          
          for (let i = 0; i < sPho.length; i++) {
            const photoData = sPho[i];

            const response = await fetch(photoData);
            const blob = await response.blob();
            const file = new File([blob], `photo_${i}.jpg`, { type: 'image/jpeg' });

            const url = await uploadPhoto(file, editToiletId);
            uploadedPhotoURLs.push(url);
          }
        }

        const existingToilet = tData[city]?.find(t => t.id === editToiletId);
const existingImages = existingToilet?.images || [];
const newPhotoObjects = uploadedPhotoURLs.map(url => createPhotoObject(url, {
  status: 'pending',
  uploadedBy: user?.name || 'Demo Nutzer',
  uploadedById: user?.uid || user?.id || 'demo-user',
  uploadedAt: new Date().toISOString()
}));

const allImages = [...existingImages, ...newPhotoObjects];

// Final-Bereinigung aller Text-Eingaben vor dem Speichern
const cleanedName = sanitizeName(sNam);
const cleanedAddress = sanitizeAddress(sAdd);
const cleanedPrice = sanitizePrice(sCostAmount || '');
const cleanedHours = sanitizeOpeningHours(sOpeningHours || '');

const updateData = {
  name: cleanedName,
  address: cleanedAddress,
  cost: sCost,
  costAmount: cleanedPrice || null,
  accessible: sAccessible === 'ja' ? true : sAccessible === 'nein' ? false : undefined,
  openingHours: cleanedHours || null,
  amenities: sAmenities || [],
  lat: sCoordinates?.lat || null,
  lng: sCoordinates?.lng || null,
  city: sCity || city,
  images: allImages
};

if (newPhotoObjects.length > 0) {
  updateData.isPendingPhotos = true;
}

await updateToilet(editToiletId, updateData);

        const updatedTData = { ...tData };
        const cityToilets = updatedTData[city] || [];
        const toiletIndex = cityToilets.findIndex(t => t.id === editToiletId);
        if (toiletIndex !== -1) {
          cityToilets[toiletIndex] = { ...cityToilets[toiletIndex], ...updateData };
          updatedTData[city] = cityToilets;
          setTData(updatedTData);
        }
        
        showToast(translate('toasts.toiletUpdated', lang), 'success');

        setEditMode(false);
        setEditToiletId(null);
        setSNam(''); setSAdd(''); setSRat(0); setSCom(''); setSPho([]);
        setSCost(''); setSCostAmount(''); setSAccessible(''); setSOpeningHours(''); setSAmenities([]);
        setSCoordinates(null); setSCity(null);

        setScr('main');
      } catch (error) {
        console.error('❌ Fehler beim Aktualisieren:', error);
        showToast(translate('toasts.toiletUpdateError', lang), 'error');
      }
      setLoading(false);
      return;
}

    // Final-Bereinigung aller Text-Eingaben vor dem Speichern
    const cleanedName = sanitizeName(sNam);
    const cleanedAddress = sanitizeAddress(sAdd);
    const cleanedComment = sanitizeComment(sCom);
    const cleanedPrice = sanitizePrice(sCostAmount || '');
    const cleanedHours = sanitizeOpeningHours(sOpeningHours || '');

    handleSuggest({ 
      name: cleanedName, 
      address: cleanedAddress, 
      initialRating: sRat, 
      initialComment: cleanedComment, 
      photos: sPho,
      cost: sCost, 
      costAmount: cleanedPrice,
      accessible: sAccessible === 'ja' ? true : sAccessible === 'nein' ? false : undefined,
      openingHours: cleanedHours,
      amenities: sAmenities
    });
    setSNam(''); setSAdd(''); setSRat(0); setSCom(''); setSPho([]);
    setSCost(''); setSCostAmount(''); setSAccessible(''); setSOpeningHours(''); setSAmenities([]);
  };
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => {
            if (editMode) {
              setEditMode(false);
              setEditToiletId(null);
              setSNam(''); setSAdd(''); setSRat(0); setSCom(''); setSPho([]);
              setSCost(''); setSCostAmount(''); setSAccessible(''); setSOpeningHours(''); setSAmenities([]);
              setSCoordinates(null); setSCity(null);
            }
            setScr('main');
          }} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold text-gray-800">
            {editMode ? translate('suggest.editRestroom', lang) : translate('suggest.title', lang)}
          </h1>
        </div>
      </div>
      <div className="max-w-6xl mx-auto p-4">
        {!editMode && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-800">💡 {translate('suggest.infoText', lang)}</p>
          </div>
        )}
        {editMode && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-blue-800">✏️ {translate('suggest.editingInfo', lang)}</p>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('suggest.mandatoryFields', lang)}</h3>
          <label className="block mb-4">
            <span className="text-gray-700 font-semibold mb-2 block">{translate('suggest.nameLabel', lang)} *</span>
            <input 
              type="text" 
              value={sNam} 
              onChange={(e) => {
                setSNam(e.target.value.slice(0, 100));
              }}
              onBlur={() => {
                setSNam(sanitizeName(sNam));
              }}
              placeholder={translate('suggest.namePlaceholder', lang)}
              maxLength={100}
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
            />
            <p className="text-xs text-gray-500 mt-1">
              {sNam.length}/100 {translate('common.characters', lang)}
            </p>
          </label>
         <label className="block">
            <span className="text-gray-700 font-semibold mb-2 block">{translate('suggest.addressLabel', lang)} *</span>
            <div className="relative">
              <input
                ref={addressInputRef}
                type="text" 
                value={sAdd} 
                onChange={(e) => {
                  const value = e.target.value.slice(0, 150);
                  setSAdd(value);
                  setSCoordinates(null);
                  setSCity(null);
                }}
                onFocus={() => setAddressInputFocused(true)}
                onBlur={() => {
                  setSAdd(sanitizeAddress(sAdd));
                  setTimeout(() => {
                    setAddressInputFocused(false);
                  }, 200);
                }}
                placeholder={translate('suggest.addressPlaceholder', lang)}
                maxLength={150}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
              />
              <p className="text-xs text-gray-500 mt-1">
                {sAdd.length}/150 {translate('common.characters', lang)}
              </p>
              
              {/* Autocomplete mit echten Adressen */}
              {addressSuggestions.length > 0 && sAdd.length >= 3 && addressInputFocused && (
                <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                  {addressSuggestions.map((addr, i) => (
                    <button 
                      key={i} 
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        setAddressSuggestions([]);
                        setSearchingAddresses(false);
                        
                        setSAdd(addr.address);
                        setSCoordinates({
                          lat: addr.lat,
                          lng: addr.lng
                        });
                        
                        const extractedCity = addr.city || addr.town || addr.municipality || null;
                        setSCity(extractedCity);
                        
                        if (addressInputRef.current) {
                          addressInputRef.current.blur();
                        }
                      }}
                      className="w-full text-left p-3 hover:bg-amber-50 border-b last:border-b-0 flex items-start gap-2"
                    >
                      <MapPin size={16} className="text-amber-600 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium block">{addr.address}</span>
                        {addr.name && addr.name !== addr.address && (
                          <span className="text-xs text-gray-500">{addr.name}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Lade-Anzeige */}
              {searchingAddresses && addressInputFocused && (
                <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 p-3 text-center">
                  <div className="text-amber-600 animate-pulse text-sm">🔍 {translate('suggest.searchingAddresses', lang)}</div>
                </div>
              )}

              {!searchingAddresses && sAdd.length >= 3 && addressSuggestions.length === 0 && addressInputFocused && (
                <div className="absolute top-full mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg z-20 p-3 text-center">
                  <div className="text-gray-500 text-sm">{translate('suggest.noAddressesFound', lang).replace('{query}', sAdd)}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    💡 {translate('suggest.addressTips', lang)}<br/>
                    {translate('suggest.addressExample', lang)}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">💡 {translate('suggest.addressHint', lang)}</p>
            
            {/* Button für aktuellen Standort */}
            <button
              type="button"
              onClick={async () => {
                setLoadingGPS(true);
                try {
                  const location = await getUserLocation();

                  setSCoordinates({
                    lat: location.lat,
                    lng: location.lng
                  });

                  const cityData = await reverseGeocode(location.lat, location.lng);
                  
                  if (cityData && cityData.address) {
                    setSAdd(cityData.address);
                    setSCity(cityData.name || city);
                    
                    showToast(translate('toasts.locationTaken', lang).replace('{address}', cityData.address), 'success');
                  } else {
                    showToast(translate('toasts.coordinatesTakenNoAddress', lang), 'info');
                  }
                } catch (error) {
                  console.error('GPS-Fehler:', error);
                  
                  if (error.message && error.message.includes('denied')) {
                    setToast({ 
                      message: 'GPS-Zugriff verweigert. Bitte aktiviere die Standortberechtigung in den Einstellungen.', 
                      type: 'error',
                      action: {
                        label: 'Einstellungen öffnen',
                        onClick: openLocationSettings
                      }
                    });
                    setTimeout(() => setToast(null), 6000);
                  } else {
                    showToast(error.message || 'Standort konnte nicht ermittelt werden', 'error');
                  }
                }
                setLoadingGPS(false);
              }}
              className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              disabled={loadingGPS}
            >
              <Navigation size={18} />
              {loadingGPS ? translate('toasts.locationDetecting', lang) : '📍 ' + translate('suggest.useCurrentLocation', lang)}
            </button>
          </label>

          {/* Interaktive Karte zur Koordinaten-Festlegung */}
          <div className="mt-6">
            <h4 className="text-gray-700 font-semibold mb-3">
              {translate('suggest.selectOnMap', lang)}
            </h4>

            {/* Koordinaten-Anzeige */}
            {sCoordinates && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 mb-3 flex items-center gap-2">
                <span className="text-green-700 text-sm">
                  ✅ {translate('suggest.selectedLocation', lang)}: <strong>{sCoordinates.lat.toFixed(6)}</strong>, <strong>{sCoordinates.lng.toFixed(6)}</strong>
                </span>
              </div>
            )}

            {/* Interaktive Karte */}
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-3 border-b border-gray-200">
                <p className="text-sm font-semibold text-gray-700">
                  🖱️ {translate('suggest.tapToSelect', lang)}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {translate('suggest.markerCanBeMoved', lang)}
                </p>
              </div>
  
  {!mapError ? (
    <div style={{ height: '400px' }}>
      <MapView
        toilets={sCoordinates ? [{
          id: 'suggest-marker',
          name: sNam || translate('suggest.newLocation', lang),
          lat: sCoordinates.lat,
          lng: sCoordinates.lng,
          address: sAdd || translate('suggest.detectingLocation', lang)
        }] : []}
        onMarkerClick={() => {}}
        center={sCoordinates ? [sCoordinates.lat, sCoordinates.lng] :
                (city && tData[city] && tData[city].length > 0)
                  ? [tData[city][0].lat, tData[city][0].lng]
                  : [51.1657, 10.4515]}
        zoom={sCoordinates ? 16 : (city ? 13 : 6)}
        onClick={async (lat, lng) => {
         
          setSCoordinates({ lat, lng });
         
          setToast({
            message: translate('toasts.locationMarked', lang),
            type: 'success'
          });
         
          try {
            const addressData = await reverseGeocode(lat, lng);
           
            if (addressData && addressData.address) {
              setSAdd(addressData.address);
             
              const extractedCity = addressData.city || addressData.town || addressData.municipality || null;
              setSCity(extractedCity);
             
              setToast({
                message: translate('toasts.addressDetected', lang).replace('{address}', addressData.address),
                type: 'success'
              });
            } else {
              setToast({
                message: translate('toasts.addressDetectionError', lang),
                type: 'warning'
              });
            }
          } catch (error) {
            console.error('❌ Reverse Geocoding Fehler:', error);
            setToast({
              message: translate('toasts.addressDetectionFailed', lang),
              type: 'warning'
            });
          }
         
          setTimeout(() => setToast(null), 4000);
        }}
        onError={() => {
          console.error('❌ Karte konnte nicht geladen werden');
          setMapError(true);
          showToast(translate('toasts.mapNotAvailable', lang), 'error');
        }}
      />
    </div>
) : (
    <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4">
      <p className="text-yellow-800 font-semibold mb-2">⚠️ {translate('toasts.mapErrorTitle', lang)}</p>
      <p className="text-yellow-700 text-sm mb-3">
        {translate('toasts.mapErrorDescription', lang)}
      </p>
      <button
        onClick={() => {
          setMapError(false);
        showToast(translate('toasts.mapRetryLoading', lang), 'info', 2000);
        }}
        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
      >
        🔄 {translate('toasts.mapRetryButton', lang)}
      </button>
    </div>
  )}
</div>
            
            {/* Hinweis-Box */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mt-3">
              <p className="text-sm text-blue-800">
                💡 <strong>{translate('common.tip', lang)}</strong> {translate('suggest.mapTip', lang)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('suggest.additionalRequired', lang)}</h3>
          
          <label className="block mb-4">
            <span className="text-gray-700 font-semibold mb-2 block">{translate('suggest.costLabel', lang)} *</span>
            <select value={sCost} onChange={(e) => setSCost(e.target.value)}
            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none">
            <option value="">{translate('common.pleaseSelect', lang)}</option>
            <option value="kostenlos">💰 {translate('suggest.costFree', lang)}</option>
            <option value="kostenpflichtig">💳 {translate('suggest.costPaid', lang)}</option>
          </select>
        </label>

          {sCost === 'kostenpflichtig' && (
            <label className="block mb-4">
              <span className="text-gray-700 font-semibold mb-2 block">{translate('suggest.costAmount', lang)}</span>
              <input 
                type="text" 
                value={sCostAmount} 
                onChange={(e) => {
                  setSCostAmount(e.target.value.slice(0, 20));
                }}
                onBlur={() => {
                  setSCostAmount(sanitizePrice(sCostAmount));
                }}
                placeholder={translate('suggest.costAmountPlaceholder', lang)}
                maxLength={20}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
              />
              <p className="text-xs text-gray-500 mt-1">
                {sCostAmount.length}/20 {translate('common.characters', lang)}
              </p>
            </label>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {translate('common.optional', lang)}: {translate('suggest.additionalInfo', lang)}
          </h3>
          
          <label className="block mb-4">
            <span className="text-gray-700 font-semibold mb-2 block">{translate('detail.accessibility', lang)}</span>
            <select value={sAccessible} onChange={(e) => setSAccessible(e.target.value)}
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none">
              <option value="">{translate('suggest.accessibilityUnknown', lang)}</option>
              <option value="ja">♿ {translate('common.yes', lang)}, {translate('detail.accessible', lang)}</option>
              <option value="nein">❌ {translate('common.no', lang)}, {translate('detail.notAccessible', lang)}</option>
            </select>
          </label>
          
          <label className="block mb-4">
            <span className="text-gray-700 font-semibold mb-2 block">
              {translate('suggest.openingHoursLabel', lang)}
            </span>
            <input 
              type="text" 
              value={sOpeningHours} 
              onChange={(e) => {
                setSOpeningHours(e.target.value.slice(0, 100));
              }}
              onBlur={() => {
                setSOpeningHours(sanitizeOpeningHours(sOpeningHours));
              }}
              placeholder={translate('suggest.openingHoursPlaceholder', lang)}
              maxLength={100}
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
            />
            <p className="text-xs text-gray-500 mt-1">
              {sOpeningHours.length}/100 {translate('common.characters', lang)}
            </p>
          </label>
          
          <label className="block">
            <span className="text-gray-700 font-semibold mb-2 block">
              {translate('detail.amenities', lang)} ({translate('common.optional', lang)})
            </span>
            <div className="space-y-2">
              {availableAmenities.map(amenity => (
                <label key={amenity} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="checkbox"
                    checked={sAmenities.includes(amenity)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSAmenities([...sAmenities, amenity]);
                      } else {
                        setSAmenities(sAmenities.filter(a => a !== amenity));
                      }
                    }}
                    className="mr-3" />
                  <span className="text-gray-700">{translateAmenity(amenity, lang)}</span>
                </label>
              ))}
            </div>
          </label>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('common.optional', lang)}: {translate('suggest.initialReview', lang)}</h3>
          <div className="flex items-center justify-center gap-2 py-4 mb-4">
            {[1,2,3,4,5].map((s) => (
              <button key={s} onClick={() => setSRat(s)} onMouseEnter={() => setSHRat(s)} onMouseLeave={() => setSHRat(0)} className="transition-transform hover:scale-110">
                <Star size={40} className={s <= (sHRat || sRat) ? 'fill-amber-500 text-amber-500' : 'text-gray-300'} />
              </button>
            ))}
          </div>
          <label className="block">
            <span className="text-gray-700 font-semibold mb-2 block">{translate('review.commentLabel', lang)}</span>
            <textarea 
              value={sCom} 
              onChange={(e) => {
                setSCom(e.target.value.slice(0, 300));
              }}
              onBlur={() => {
                setSCom(sanitizeComment(sCom));
              }}
              placeholder={translate('review.commentPlaceholder', lang)}
              maxLength={300}
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none resize-none" 
              rows="4" 
            />
            <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-gray-500">
              {translate('validation.allowedCharsComment', lang)}
            </p>
            <p className={`text-sm font-semibold ${sCom.length >= 280 ? 'text-red-600' : 'text-gray-500'}`}>
              {sCom.length}/300 {translate('common.characters', lang)}
            </p>
          </div>
          </label>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4">{translate('suggest.photosLabel', lang)}</h3>

          {/* Netiquette-Hinweis */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-3">
            <span className="text-xl flex-shrink-0">⚠️</span>
            <div className="text-sm">
              <p className="text-amber-900 font-semibold mb-1">
                {translate('review.importantReminder', lang)}
              </p>
              <p className="text-amber-800">
                {translate('review.photoReminderText', lang)}
              </p>
            </div>
          </div>

          <label className="block w-full cursor-pointer">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500">
              <Camera size={40} className="mx-auto mb-2 text-gray-400" />
              <p className="text-gray-600 font-semibold">{translate('suggest.selectPhoto', lang)}</p>
            </div>
            <input type="file" accept="image/*" multiple onChange={handleSPhUp} className="hidden" />
          </label>
          {sPho.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3">
              {sPho.map((p, i) => (
                <div key={i} className="relative group">
                  <img src={p} alt={`Vorschau ${i + 1} des hochgeladenen Toiletten-Fotos`} className="w-full h-24 object-cover rounded-lg" />
                  <button onClick={() => setSPho(sPho.filter((_, idx) => idx !== i))}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Bestätigungs-Hinweis */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-900">
            {translate('review.confirmationText', lang)}
          </p>
        </div>
        <button
          onClick={handleSubSug} 
          className={`w-full font-bold py-4 px-6 rounded-lg text-white ${
            editMode 
              ? 'bg-blue-600 hover:bg-blue-700' 
              : 'bg-amber-600 hover:bg-amber-700'
          }`}
        >
          {editMode ? translate('suggest.editButton', lang) : translate('suggest.submitButton', lang)}
        </button>
      </div>
    </main>
    </>
  );
}

if (scr === 'report-closure' && sel) {
  // Kategorien für Meldungen
  const categories = [
    { id: 'closed', label: translate('reportClosure.categories.closed', lang), icon: '🔒' },
    { id: 'address', label: translate('reportClosure.categories.address', lang), icon: '📍' },
    { id: 'hours', label: translate('reportClosure.categories.hours', lang), icon: '🕐' },
    { id: 'accessibility', label: translate('reportClosure.categories.accessibility', lang), icon: '♿' },
    { id: 'cost', label: translate('reportClosure.categories.cost', lang), icon: '💶' },
    { id: 'other', label: translate('reportClosure.categories.other', lang), icon: '📝' }
  ];

// Gründe je nach Kategorie
  const reasonsByCategory = {
    closed: [
      translate('reportClosure.reasons.closed.temporary', lang),
      translate('reportClosure.reasons.closed.permanent', lang),
      translate('reportClosure.reasons.closed.notFound', lang),
      translate('reportClosure.reasons.closed.renovation', lang),
      translate('reportClosure.reasons.closed.other', lang)
    ],
    address: [
      translate('reportClosure.reasons.address.wrong', lang),
      translate('reportClosure.reasons.address.entrance', lang),
      translate('reportClosure.reasons.address.notExists', lang),
      translate('reportClosure.reasons.address.other', lang)
    ],
    hours: [
      translate('reportClosure.reasons.hours.wrong', lang),
      translate('reportClosure.reasons.hours.different', lang),
      translate('reportClosure.reasons.hours.alwaysOpen', lang),
      translate('reportClosure.reasons.hours.alwaysClosed', lang),
      translate('reportClosure.reasons.hours.other', lang)
    ],
    accessibility: [
      translate('reportClosure.reasons.accessibility.notAccessible', lang),
      translate('reportClosure.reasons.accessibility.accessible', lang),
      translate('reportClosure.reasons.accessibility.wheelchairAccessible', lang),
      translate('reportClosure.reasons.accessibility.other', lang)
    ],
    cost: [
      translate('reportClosure.reasons.cost.wrongAmount', lang),
      translate('reportClosure.reasons.cost.freeInsteadPaid', lang),
      translate('reportClosure.reasons.cost.paidInsteadFree', lang),
      translate('reportClosure.reasons.cost.other', lang)
    ],
    other: [
      translate('reportClosure.reasons.other.inappropriatePhoto', lang),
      translate('reportClosure.reasons.other.wrongName', lang),
      translate('reportClosure.reasons.other.other', lang)
    ]
  };

  const handleSubRep = () => {
    if (!reportCategory) {
      showToast(translate('reportClosure.selectCategoryError', lang), 'error');
      return;
    }
    if (!clRea) { 
      showToast(translate('reportClosure.selectReasonError', lang), 'error');
      return; 
    }
    
    // Final-Bereinigung des Kommentars vor dem Speichern
    const cleanedComment = sanitizeComment(clCom);
    
    handleRepClo(reportCategory, clRea, cleanedComment, reportPhotos);
    setClRea(''); 
    setClCom(''); 
    setReportCategory('');
    setReportPhotos([]);
    clearTimeout(window.closureCommentCleanupTimeout);
    setScr('detail');
  };

  // Vorschaubild aus genehmigten Fotos holen
  const allImages = sel.images || [];
  const approvedImages = getApprovedPhotos(allImages);
  const previewImage = approvedImages.length > 0 ? getPhotoUrl(approvedImages[0]) : ph;

return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3 max-w-6xl mx-auto">
            <button onClick={() => { setScr('detail'); setReportCategory(''); setClRea(''); setClCom(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">{translate('reportClosure.title', lang)}</h1>
          </div>
        </div>
        
        <div className="max-w-6xl mx-auto p-4">
          {/* Toiletten-Info */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
            <div className="flex items-center gap-4 mb-4 pb-4 border-b">
              <img 
                src={previewImage} 
                alt={`Vorschaubild der zu meldenden Toilette ${sel.name || 'ohne Namen'}`} 
                className="w-16 h-16 object-cover rounded-lg" 
              />
              <div>
                <h2 className="font-bold text-gray-800">{sel.name}</h2>
                <p className="text-sm text-gray-600">{sel.address}</p>
              </div>
            </div>

            {/* Kategorie-Auswahl */}
            <h3 className="font-bold text-gray-800 mb-3">{translate('reportClosure.selectCategory', lang)} *</h3>
            <div className="grid grid-cols-2 gap-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setReportCategory(cat.id);
                    setClRea(''); 
                  }}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    reportCategory === cat.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <span className="text-lg mr-2">{cat.icon}</span>
                  <span className="text-sm font-semibold text-gray-700">{cat.label.replace(/^.{1,3} /, '')}</span>
                </button>
              ))}
            </div>

            {/* Grund-Auswahl (nur wenn Kategorie gewählt) */}
            {reportCategory && (
              <>
                <h3 className="font-bold text-gray-800 mb-3">{translate('reportClosure.selectReason', lang)} *</h3>
                <div className="space-y-2 mb-4">
                  {reasonsByCategory[reportCategory].map((r) => (
                    <label 
                      key={r} 
                      className="flex items-center p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-amber-500 transition-colors"
                    >
                      <input 
                        type="radio" 
                        name="reason" 
                        value={r} 
                        checked={clRea === r} 
                        onChange={(e) => setClRea(e.target.value)} 
                        className="mr-3" 
                      />
                      <span className="text-gray-700">{r}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {/* Kommentar-Feld */}
            <label className="block">
              <span className="text-gray-700 font-semibold mb-2 block">
                {translate('reportClosure.additionalInfo', lang)}
              </span>
              <textarea 
                value={clCom} 
                onChange={(e) => {
                  setClCom(e.target.value.slice(0, 300));
                }}
                onBlur={() => {
                  setClCom(sanitizeComment(clCom));
                }}
                placeholder={translate('reportClosure.additionalInfoPlaceholder', lang)}
                maxLength={300}
                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none resize-none" 
                rows="4" 
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">
                ✓ Max. 300 {translate('common.characters', lang)}
              </p>
              <p className={`text-sm font-semibold ${clCom.length >= 280 ? 'text-red-600' : 'text-gray-500'}`}>
                {clCom.length}/300 {translate('common.characters', lang)}
              </p>
              </div>
            </label>
          </div>

          {/* Info-Box */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-blue-800">
              {translate('reportClosure.infoMessage', lang)}
            </p>
          </div>

          {/* Submit-Button */}
          <button 
            onClick={handleSubRep} 
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-4 px-6 rounded-lg transition-colors"
          >
            {translate('reportClosure.submitReport', lang)}
          </button>
        </div>
      </main>
    </>
  );
}

if (scr === 'impressum') {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => setScr('welcome')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold text-gray-800">{translate('impressum.title', lang)}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{translate('impressum.heading', lang)}</h2>
          <div className="space-y-6 text-gray-700">
            
            {/* Angaben gemäß TMG */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📋</span>
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">{translate('impressum.tmg.title', lang)}</h3>
                  <p className="text-sm text-blue-800">
                    <strong>{translate('impressum.tmg.name', lang)}</strong><br/>
                    Jonas Ziethen<br/>
                    Merkurstraße 16<br/>
                    40223 Düsseldorf<br/>
                    Deutschland
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    📧 <strong>{translate('impressum.tmg.contact', lang)}:</strong> restroomadvisor@gmx.de
                  </p>
                </div>
              </div>
            </div>

            {/* Verantwortlich für den Inhalt */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">✍️</span>
                <div>
                  <h3 className="font-bold text-purple-900 mb-2">{translate('impressum.responsible.title', lang)}</h3>
                  <p className="text-sm text-purple-800">
                    <strong>{translate('impressum.responsible.subtitle', lang)}</strong>
                  </p>
                  <p className="text-sm text-purple-800 mt-1">
                    Jonas Ziethen<br/>
                    Merkurstraße 16<br/>
                    40223 Düsseldorf
                  </p>
                </div>
              </div>
            </div>

            {/* Kartendaten & Lizenzen */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🗺️</span>
                <div>
                  <h3 className="font-bold text-green-900 mb-2">{translate('impressum.maps.title', lang)}</h3>
                  <p className="text-sm text-green-800 mb-2">
                    {translate('impressum.maps.description', lang)}
                  </p>
                  <p className="text-sm text-green-800 mb-1">
                    <strong>{translate('impressum.maps.mapData', lang)}:</strong> © OpenStreetMap contributors
                  </p>
                  <p className="text-sm text-green-800 mb-1">
                    <strong>{translate('impressum.maps.license', lang)}:</strong> {translate('impressum.maps.odbl', lang)}
                  </p>
                  <p className="text-xs text-green-700">
                    📄 <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-900">openstreetmap.org/copyright</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Haftungsausschluss */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚠️</span>
                <div>
                  <h3 className="font-bold text-amber-900 mb-2">{translate('impressum.disclaimer.title', lang)}</h3>
                  <p className="text-sm text-amber-800 mb-2">
                    {translate('impressum.disclaimer.description', lang)}
                  </p>
                  <p className="text-xs text-amber-700">
                    ℹ️ {translate('impressum.disclaimer.externalLinks', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Link zur vollständigen Version */}
            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📄</span>
                <div>
                  <h3 className="font-bold text-cyan-900 mb-2">{translate('impressum.fullVersion.title', lang)}</h3>
                  <p className="text-sm text-cyan-800 mb-2">
                    {translate('impressum.fullVersion.description', lang)}
                  </p>
                  <a href="https://restroom-advisor.web.app/impressum.html" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-cyan-600 hover:text-cyan-700 font-medium text-sm underline break-all">
                    restroom-advisor.web.app/impressum.html
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Zurück zur Hauptseite */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button 
              onClick={() => setScr('welcome')} 
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              {translate('impressum.backButton', lang)}
            </button>
          </div>
        </div>
      </div>
    </main>
    </>
  );
}

if (scr === 'nutzungsbedingungen') {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => setScr('welcome')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold text-gray-800">{translate('terms.title', lang)}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 legal-screen-padding">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{translate('terms.heading', lang)}</h2>
          
          <div className="space-y-6 text-gray-700">
            
            {/* Was ist RestroomAdvisor */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">ℹ️</span>
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">{translate('terms.what.title', lang)}</h3>
                  <p className="text-sm text-blue-800 mb-2">
                    {translate('terms.what.description', lang)}
                  </p>
                  <p className="text-xs text-blue-700">
                    ✅ {translate('terms.what.free', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Öffentliche Toiletten - Fokus */}
            <div className="bg-cyan-50 border-2 border-cyan-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🚻</span>
                <div>
                  <h3 className="font-bold text-cyan-900 mb-2">{translate('terms.publicFocus.title', lang)}</h3>
                  <p className="text-sm text-cyan-800 mb-3">
                    <strong>{translate('terms.publicFocus.intro', lang)}</strong>
                  </p>
                  <div className="bg-white rounded p-3 mb-3">
                    <p className="text-xs font-bold text-green-700 mb-1">✅ {translate('terms.publicFocus.includedTitle', lang)}:</p>
                    <ul className="text-xs text-gray-700 space-y-1 ml-4">
                      <li>• {translate('terms.publicFocus.included1', lang)}</li>
                      <li>• {translate('terms.publicFocus.included2', lang)}</li>
                      <li>• {translate('terms.publicFocus.included3', lang)}</li>
                      <li>• {translate('terms.publicFocus.included4', lang)}</li>
                      <li>• {translate('terms.publicFocus.included5', lang)}</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded p-3">
                    <p className="text-xs font-bold text-red-700 mb-1">❌ {translate('terms.publicFocus.excludedTitle', lang)}:</p>
                    <ul className="text-xs text-gray-700 space-y-1 ml-4">
                      <li>• {translate('terms.publicFocus.excluded1', lang)}</li>
                      <li>• {translate('terms.publicFocus.excluded2', lang)}</li>
                      <li>• {translate('terms.publicFocus.excluded3', lang)}</li>
                      <li>• {translate('terms.publicFocus.excluded4', lang)}</li>
                    </ul>
                  </div>
                  <p className="text-xs text-cyan-700 mt-3">
                    <strong>💡 {translate('terms.publicFocus.exceptionsTitle', lang)}:</strong> {translate('terms.publicFocus.exceptionsText', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Erlaubte Nutzung */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">✅</span>
                <div>
                  <h3 className="font-bold text-green-900 mb-2">{translate('terms.allowed.title', lang)}</h3>
                  <p className="text-sm text-green-800 mb-2">
                    <strong>{translate('terms.allowed.subtitle', lang)}</strong>
                  </p>
                  <ul className="text-sm text-green-800 space-y-1 ml-4">
                    <li>• {translate('terms.allowed.item1', lang)}</li>
                    <li>• {translate('terms.allowed.item2', lang)}</li>
                    <li>• {translate('terms.allowed.item3', lang)}</li>
                    <li>• {translate('terms.allowed.item4', lang)}</li>
                    <li>• {translate('terms.allowed.item5', lang)}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Verbotene Nutzung */}
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">❌</span>
                <div>
                  <h3 className="font-bold text-red-900 mb-2">{translate('terms.forbidden.title', lang)}</h3>
                  <p className="text-sm text-red-800 mb-2">
                    <strong>{translate('terms.forbidden.subtitle', lang)}</strong>
                  </p>
                  <ul className="text-sm text-red-800 space-y-1 ml-4">
                    <li>• {translate('terms.forbidden.item1', lang)}</li>
                    <li>• {translate('terms.forbidden.item2', lang)}</li>
                    <li>• {translate('terms.forbidden.item3', lang)}</li>
                    <li>• {translate('terms.forbidden.item4', lang)}</li>
                    <li>• {translate('terms.forbidden.item5', lang)}</li>
                  </ul>
                  <p className="text-xs text-red-700 mt-2">
                    ⚠️ <strong>{translate('terms.forbidden.consequences', lang)}</strong> {translate('terms.forbidden.consequencesText', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Foto-Regeln */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📸</span>
                <div>
                  <h3 className="font-bold text-amber-900 mb-2">{translate('terms.photos.title', lang)}</h3>
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>{translate('terms.photos.subtitle', lang)}</strong>
                  </p>
                  <ul className="text-sm text-amber-800 space-y-1 ml-4">
                    <li>• {translate('photoRules', lang)}</li>
                    <li>• {translate('terms.photos.rule2', lang)}</li>
                    <li>• {translate('terms.photos.rule3', lang)}</li>
                    <li>• {translate('terms.photos.rule4', lang)}</li>
                  </ul>
                  <p className="text-xs text-amber-700 mt-2">
                    ✓ {translate('terms.photos.moderation', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Haftungsausschluss */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚖️</span>
                <div>
                  <h3 className="font-bold text-purple-900 mb-2">{translate('terms.disclaimer.title', lang)}</h3>
                  <p className="text-sm text-purple-800 mb-2">
                    {translate('terms.disclaimer.description', lang)}
                  </p>
                  <p className="text-xs text-purple-700">
                    ⚠️ {translate('terms.disclaimer.noGuarantee', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Deine Rechte */}
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🔐</span>
                <div>
                  <h3 className="font-bold text-indigo-900 mb-2">{translate('terms.rights.title', lang)}</h3>
                  <p className="text-sm text-indigo-800 mb-2">
                    {translate('terms.rights.description', lang)}
                  </p>
                  <p className="text-xs text-indigo-700">
                    📧 {translate('terms.rights.contact', lang)}: restroomadvisor@gmx.de
                  </p>
                </div>
              </div>
            </div>

            {/* Link zur vollständigen Version */}
            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📄</span>
                <div>
                  <h3 className="font-bold text-cyan-900 mb-2">{translate('terms.fullVersion.title', lang)}</h3>
                  <p className="text-sm text-cyan-800 mb-2">
                    {translate('terms.fullVersion.description', lang)}
                  </p>
                  <a href="https://restroom-advisor.web.app/nutzungsbedingungen.html" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-cyan-600 hover:text-cyan-700 font-medium text-sm underline break-all">
                    restroom-advisor.web.app/nutzungsbedingungen.html
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Zurück zur Hauptseite */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button 
              onClick={() => setScr('welcome')} 
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              {translate('terms.backButton', lang)}
            </button>
          </div>
        </div>
      </div>
    </main>
    </>
  );
}

if (scr === 'datenschutz') {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => setScr('welcome')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold text-gray-800">{translate('privacy.title', lang)}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 legal-screen-padding">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{translate('privacy.heading', lang)}</h2>
          
          <div className="space-y-6 text-gray-700">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📱</span>
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">{translate('privacy.localStorage.title', lang)}</h3>
                  <p className="text-sm text-blue-800 mb-2">
                    {translate('privacy.localStorage.description', lang)}
                  </p>
                  <p className="text-xs text-blue-700">
                    ✅ {translate('privacy.localStorage.notTransferred', lang)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">☁️</span>
                <div>
                  <h3 className="font-bold text-green-900 mb-2">{translate('privacy.firebase.title', lang)}</h3>
                  <p className="text-sm text-green-800 mb-2">
                    {translate('privacy.firebase.description', lang)}
                  </p>
                  <p className="text-xs text-green-700">
                    ℹ️ {translate('privacy.firebase.moderated', lang)}
                  </p>
                  <p className="text-xs text-green-700 mt-2">
                    ⏱️ <strong>{translate('privacy.firebase.retention', lang)}</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📍</span>
                <div>
                  <h3 className="font-bold text-amber-900 mb-2">{translate('privacy.gps.title', lang)}</h3>
                  <p className="text-sm text-amber-800 mb-2">
                    {translate('privacy.gps.description', lang)}
                  </p>
                  <p className="text-xs text-amber-700">
                    ✅ {translate('privacy.gps.notStored', lang)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📸</span>
                <div>
                  <h3 className="font-bold text-red-900 mb-2">{translate('privacy.photos.title', lang)}</h3>
                  <p className="text-sm text-red-800 mb-2">
                    {translate('privacy.photos.description', lang)}
                  </p>
                  <p className="text-xs text-red-700">
                    ⚠️ {translate('privacy.photos.adminCheck', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Deine Rechte */}
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚖️</span>
                <div>
                  <h3 className="font-bold text-purple-900 mb-2">{translate('privacy.gdpr.title', lang)}</h3>
                  <p className="text-sm text-purple-800 mb-2">
                    {translate('privacy.gdpr.description', lang)}
                  </p>
                  <p className="text-xs text-purple-700">
                    📧 {translate('privacy.gdpr.contact', lang)}: restroomadvisor@gmx.de
                  </p>
                  <p className="text-xs text-purple-700 mt-2">
                    📋 <strong>{translate('privacy.gdpr.legalBasis', lang)}:</strong> {translate('privacy.gdpr.legalBasisText', lang)}
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    🔄 <strong>{translate('privacy.gdpr.revocation', lang)}:</strong> {translate('privacy.gdpr.revocationText', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Keine Cookies */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🍪</span>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{translate('privacy.cookies.title', lang)}</h3>
                  <p className="text-sm text-gray-800">
                    {translate('privacy.cookies.description', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Weitergabe */}
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🔒</span>
                <div>
                  <h3 className="font-bold text-indigo-900 mb-2">{translate('privacy.thirdParty.title', lang)}</h3>
                  <p className="text-sm text-indigo-800">
                    {translate('privacy.thirdParty.description', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* Beschwerderecht */}
            <div className="bg-rose-50 border-2 border-rose-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚖️</span>
                <div>
                  <h3 className="font-bold text-rose-900 mb-2">{translate('privacy.complaints.title', lang)}</h3>
                  <p className="text-sm text-rose-800 mb-2">
                    {translate('privacy.complaints.description', lang)}
                  </p>
                  <p className="text-xs text-rose-700">
                    <strong>{translate('privacy.complaints.authority', lang)}</strong><br/>
                    📧 {translate('privacy.complaints.email', lang)} | ☎️ {translate('privacy.complaints.phone', lang)}
                  </p>
                </div>
              </div>
            </div>

            {/* OpenStreetMap Attribution */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🗺️</span>
                <div>
                  <h3 className="font-bold text-blue-900 mb-2">{translate('privacy.osmAttribution.title', lang)}</h3>
                  <p className="text-sm text-blue-800 mb-2">
                    {translate('privacy.osmAttribution.description', lang)}
                  </p>
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>{translate('privacy.osmAttribution.mapData', lang)}:</strong> {translate('privacy.osmAttribution.contributors', lang)}
                  </p>
                  <p className="text-xs text-blue-700">
                    📄 {translate('privacy.osmAttribution.moreInfo', lang)} <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">openstreetmap.org/copyright</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Link zur vollständigen Version */}
            <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">📄</span>
                <div>
                  <h3 className="font-bold text-cyan-900 mb-2">{translate('privacy.fullVersion.title', lang)}</h3>
                  <p className="text-sm text-cyan-800 mb-2">
                    {translate('privacy.fullVersion.description', lang)}
                  </p>
                  <a href="https://restroom-advisor.web.app/datenschutz.html" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-cyan-600 hover:text-cyan-700 font-medium text-sm underline break-all">
                    restroom-advisor.web.app/datenschutz.html
                  </a>
                </div>
              </div>
            </div>
          </div>

         {/* Zurück zur Hauptseite */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button 
              onClick={() => setScr('welcome')} 
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              {translate('privacy.backButton', lang)}
            </button>
          </div>
        </div>
      </div>
    </main>
    </>
  );
}

// ADMIN-VORSCHAU-SCREEN
if (scr === 'admin-preview' && isAdmin && adminPreviewToilet) {
  const toilet = adminPreviewToilet;
  
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3 max-w-6xl mx-auto">
            <button 
              onClick={() => {
                setScr('admin');
                setAdminPreviewToilet(null);
              }} 
              className="p-2 hover:bg-blue-500 rounded-lg"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-bold text-white">👁️ Toiletten-Vorschau</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          {/* Status Badge */}
          <div className="mb-4">
            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 font-bold rounded-full">
              ⏳ PENDING - Wartet auf Freigabe
            </span>
          </div>

          {/* Hauptinfo */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{toilet.name}</h2>
            
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <MapPin size={20} className="text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-700 font-semibold">Adresse:</p>
                  <p className="text-gray-600">
                    {toilet.address && !toilet.address.includes('Unbekannt') 
                      ? toilet.address 
                      : 'Keine Adresse angegeben'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    📍 Koordinaten: {toilet.lat?.toFixed(6)}, {toilet.lng?.toFixed(6)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-xl">🏙️</span>
                <div>
                  <p className="text-gray-700 font-semibold">Stadt:</p>
                  <p className="text-gray-600">{toilet.city || 'Unbekannt'}</p>
                </div>
              </div>

              {toilet.cost && (
                <div className="flex items-start gap-2">
                  <span className="text-xl">💰</span>
                  <div>
                    <p className="text-gray-700 font-semibold">Kosten:</p>
                    <p className="text-gray-600">
                      {toilet.cost === 'kostenlos' ? 'Kostenlos' : 
                       toilet.cost === 'kostenpflichtig' ? `${toilet.costAmount || '?'}€` : 
                       'Unbekannt'}
                    </p>
                  </div>
                </div>
              )}

              {toilet.accessible && (
                <div className="flex items-start gap-2">
                  <span className="text-xl">♿</span>
                  <div>
                    <p className="text-gray-700 font-semibold">Barrierefreiheit:</p>
                    <p className="text-gray-600">
                      {toilet.accessible === 'ja' ? '✅ Barrierefrei' : 
                       toilet.accessible === 'nein' ? '❌ Nicht barrierefrei' : 
                       '❓ Unbekannt'}
                    </p>
                  </div>
                </div>
              )}

              {toilet.openingHours && (
                <div className="flex items-start gap-2">
                  <Clock size={20} className="text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-gray-700 font-semibold">Öffnungszeiten:</p>
                    <p className="text-gray-600 whitespace-pre-line">{toilet.openingHours}</p>
                  </div>
                </div>
              )}

              {toilet.amenities && toilet.amenities.length > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-xl">✨</span>
                  <div>
                    <p className="text-gray-700 font-semibold">Ausstattung:</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {toilet.amenities.map((amenity, i) => (
                        <span key={i} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2">
                <User size={20} className="text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-700 font-semibold">Vorgeschlagen von:</p>
                  <p className="text-gray-600">{toilet.suggestedBy || 'Unbekannt'}</p>
                  <p className="text-sm text-gray-500">
                    📅 {toilet.suggestedDate || 'Datum unbekannt'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Fotos */}
          {toilet.images && toilet.images.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">
                📸 Fotos ({toilet.images.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {toilet.images.map((img, idx) => {
                  const photoUrl = typeof img === 'string' ? img : (img.url || getPhotoUrl(img));
                  const photoStatus = typeof img === 'object' ? img.status : 'approved';
                  
                  return (
                    <div key={idx} className="relative">
                      <img 
                        src={photoUrl} 
                        alt={`Foto ${idx + 1} von ${toilet.name}`}
                        className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(photoUrl, '_blank')}
                      />
                      {/* Status-Badge */}
                      {photoStatus === 'pending' && (
                        <span className="absolute top-2 right-2 px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                          ⏳ PENDING
                        </span>
                      )}
                      {photoStatus === 'approved' && (
                        <span className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                          ✅
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Admin-Aktionen */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">🛠️ Admin-Aktionen</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Freigeben */}
              <button
                onClick={async () => {
                  if (!window.confirm(`Toilette "${toilet.name}" wirklich freigeben?`)) return;
                  
                  setLoadingAdmin(true);
                  try {
                    await approveToilet(toilet.id);
                    showToast('✅ Toilette erfolgreich freigegeben!', 'success');

                    await loadPendingToilets();
                    setAdminPreviewToilet(null);
                    setScr('admin');
                  } catch (error) {
                    console.error('❌ Fehler:', error);
                    showToast('Fehler beim Freigeben', 'error');
                  }
                  setLoadingAdmin(false);
                }}
                disabled={loadingAdmin}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <span className="text-2xl">✅</span>
                <span>Freigeben</span>
              </button>

              {/* Ablehnen */}
              <button
                onClick={async () => {
                  if (!window.confirm(`Toilette "${toilet.name}" wirklich ablehnen?`)) return;
                  
                  setLoadingAdmin(true);
                  try {
                    await rejectToilet(toilet.id);
                    showToast('✅ Toilette abgelehnt', 'success');

                    await loadPendingToilets();
                    setAdminPreviewToilet(null);
                    setScr('admin');
                  } catch (error) {
                    console.error('❌ Fehler:', error);
                    showToast('Fehler beim Ablehnen', 'error');
                  }
                  setLoadingAdmin(false);
                }}
                disabled={loadingAdmin}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-6 rounded-lg disabled:bg-gray-300 transition-colors flex items-center justify-center gap-2"
              >
                <span className="text-2xl">❌</span>
                <span>Ablehnen</span>
              </button>
            </div>

            {/* Zurück ohne Aktion */}
            <button
              onClick={() => {
                setAdminPreviewToilet(null);
                setScr('admin');
              }}
              className="w-full mt-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft size={20} />
              <span>Zurück zum Dashboard (ohne Aktion)</span>
            </button>
          </div>
        </div>
      </main>
    </>
  );
}

if (scr === 'profile') {
  const vt = getVis();
  const level = getLevel();
  const achievements = getAchievements();
  const uniqueToilets = new Set(checkIns.map(c => c.toiletId)).size;
  
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          <button onClick={() => setScr('main')} className="p-2 hover:bg-gray-100 rounded-lg"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold text-gray-800">{translate('profile.myProfile', lang)}</h1>
        </div>
      </div>
      <div className="max-w-6xl mx-auto p-4">
   <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-20 h-20 bg-amber-200 rounded-full flex items-center justify-center">
              <User size={40} className="text-amber-700" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-800">
                  {user?.name || 'Demo Nutzer'}
                </h2>
                <button onClick={() => setScr('edit-profile')} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <Edit2 size={18} className="text-gray-600" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {translate('profile.memberSince', lang)} {new Date(user?.createdAt || Date.now()).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}
              </p>
              <button 
                onClick={() => setScr('rank-selection')}
                className="mt-2 inline-block"
              >
                <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold transition-colors cursor-pointer ${
                  getSelectedRank().id === 'admin' 
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : getSelectedRank().id === 'beta_legend'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                }`}>
                  {getSelectedRank().icon} {getSelectedRank().name} <Edit2 size={12} />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-bold text-gray-800">{translate('profile.level', lang)} {level.level}: {translate(`badges.${level.key}`, lang)}</h3>
              <p className="text-sm text-gray-600">
                {uniqueToilets} / {level.next}
                {level.level < 7 ? ` ${translate('profile.toiletsUntilNext', lang)}` : ` - ${translate('profile.maxLevel', lang)}`}
              </p>
            </div>
            <div className="text-3xl font-bold text-amber-600">Lv.{level.level}</div>
          </div>
          {level.level < 7 && (
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div className="bg-amber-600 h-4 rounded-full transition-all" style={{width: `${level.progress}%`}}></div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{uniqueToilets}</div>
            <div className="text-sm text-gray-600">{translate('profile.different', lang)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{checkIns.length}</div>
            <div className="text-sm text-gray-600">{translate('profile.checkIns', lang)}</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{uRev.length}</div>
            <div className="text-sm text-gray-600">{translate('detail.reviews', lang)}</div>
          </div>
        </div>

        {/* Admin-Dashboard Button (nur für Admins) */}
        {isAdmin && (
          <div className="mb-6">
            <button 
              onClick={() => setScr('admin')}
              className="w-full relative py-4 px-6 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-3 transform hover:scale-[1.02] transition-all"
            >
              <span className="text-2xl">👑</span>
              <span className="text-lg">{translate('profile.adminDashboard', lang)}</span>
              {(pendingCount + photosCount + reportsCount + commentReportsCount) > 0 && (
                <span className="absolute -top-2 -right-2 bg-yellow-400 text-red-900 text-sm font-bold rounded-full w-8 h-8 flex items-center justify-center shadow-lg animate-pulse">
                  {pendingCount + photosCount + reportsCount + commentReportsCount}
                </span>
              )}
            </button>
          </div>
        )}
       
        <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {translate('profile.achievements', lang)} ({achievements.filter(a => a.unlocked).length}/{achievements.filter(a => !a.secret || a.unlocked).length} {translate('profile.achievementsUnlocked', lang)})
            {achievements.some(a => a.secret && a.unlocked) && <span className="ml-2 text-sm text-purple-600">✨ +{translate('profile.secret', lang)}</span>}
          </h3>
          <div className="grid grid-cols-1 gap-3">
            {achievements
              .filter(ach => !ach.secret || ach.unlocked)
              .map(ach => (
                <div key={ach.id} className={`flex items-center gap-4 p-3 rounded-lg ${ach.unlocked ? 'bg-amber-50 border-2 border-amber-200' : 'bg-gray-50 opacity-50'}`}>
                  <span className="text-3xl">{ach.icon}</span>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">
                      {translate(`achievements.${ach.key}.name`, lang)}
                      {ach.secret && <span className="ml-2 text-xs text-purple-600 font-bold">🔒 {translate('profile.secret', lang)}</span>}
                    </h4>
                    <p className="text-sm text-gray-600">{translate(`achievements.${ach.key}.desc`, lang)}</p>
                  </div>
                  {ach.unlocked && <span className="text-green-600 font-bold">✓</span>}
                </div>
              ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{translate('profile.visitedToilets', lang)} ({vt.length})</h3>
          {vt.length === 0 ? <p className="text-gray-500 text-center py-8">{translate('profile.noVisits', lang)}</p> : (
            <div className="space-y-3">
              {vt.map(t => (
                <div key={t.id} onClick={() => { 
                  setSel(t); 

                  if (t.lat && t.lng) {
                    setMapCenter([t.lat, t.lng]);
                    setMapZoom(16); 
                  }
                  
                  setScr('detail'); 
                }}
                  className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  {(() => {
                    if (t.images && t.images.length > 0) {
                      const approvedImages = getApprovedPhotos(t.images);
                      if (approvedImages.length > 0) {
                        return (
                          <img 
                            src={getPhotoUrl(approvedImages[0])} 
                            alt={`${translate('profile.visitedToiletAlt', lang)} ${t.name || translate('common.withoutName', lang)}`}
                            className="w-16 h-16 object-cover rounded-lg" 
                          />
                        );
                      }
                    }
                    return null;
                  })()}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800">{getToiletDisplayName(t, vt)}</h4>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock size={12} />{new Date(t.lastVisited).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}
                    </p>
                    {getCheckInCount(t.id) > 1 && (
                      <p className="text-xs text-green-600">{getCheckInCount(t.id)}{translate('profile.timesVisited', lang)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Star size={16} className="fill-amber-500 text-amber-500" />
                    <span className="font-semibold">{t.rating}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Version Info */}
        <div className="text-center py-4">
          <p className="text-sm text-gray-400">v{APP_VERSION}</p>
        </div>
      </div>
    </main>
    </>
  );
}

if (scr === 'edit-profile') {
  return (
    <>
      {/* 🎉 ACHIEVEMENT POPUP */}
      <AchievementPopup />
      
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3 max-w-6xl mx-auto">
            <button onClick={() => setScr('profile')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">{translate('profile.editProfile', lang)}</h1>
          </div>
        </div>
        <div className="max-w-2xl mx-auto p-6">
          <label className="block mb-4">
            <span className="block text-sm font-bold text-gray-700 mb-2">{translate('profile.yourName', lang)}</span>
            <input 
              type="text" 
              value={profileEditName !== null ? profileEditName : (user?.name || '')}
              onChange={(e) => {
                const value = e.target.value;
                const cleaned = value
                  .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '')
                  .slice(0, 30);
                setProfileEditName(cleaned);
              }}
              onFocus={() => {
                if (profileEditName === null) {
                  setProfileEditName(user?.name || '');
                }
              }}
              placeholder={translate('profile.yourNamePlaceholder', lang)}
              maxLength={30}
              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none" 
            />
            <p className="text-xs text-gray-500 mt-2">
              {(profileEditName !== null ? profileEditName : (user?.name || '')).length}/30 {translate('profile.characters', lang)}
            </p>
          </label>

          <button 
            onClick={() => { 
              const nameToSave = profileEditName || user?.name || '';
              const cleaned = nameToSave
                .replace(/[^a-zA-ZäöüÄÖÜß0-9\s\-_]/g, '')
                .slice(0, 30)
                .trim();
              
              if (cleaned.length >= 2) { 
                setUser({...user, name: cleaned}); 
                setProfileEditName('');
                showToast(translate('profile.nameChangeSuccess', lang), 'success');
                setScr('profile'); 
              } else {
                showToast(translate('profile.nameMinLength', lang), 'error');
              }
            }}
            disabled={!profileEditName && !user?.name}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-6 rounded-lg mb-4"
          >
            {translate('profile.changeNameButton', lang)}
          </button>
          
          {/* SPRACH-TOGGLE */}
          <div className="mb-4 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-gray-700">🌍 {translate('profile.language', lang)}</span>
            </div>
            
            {/* Toggle Switch */}
            <div 
              onClick={() => handleLanguageSelect(lang === 'de' ? 'en' : 'de')}
              className="relative w-full h-12 bg-white rounded-lg shadow-md cursor-pointer overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all"
            >
              {/* Sliding Background */}
              <div 
                className={`absolute top-0 h-full w-1/2 bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300 ease-in-out ${
                  lang === 'de' ? 'left-0' : 'left-1/2'
                }`}
              />
              
              {/* Deutsch Button */}
              <div className={`absolute left-0 top-0 w-1/2 h-full flex items-center justify-center gap-2 transition-all duration-300 ${
                lang === 'de' ? 'text-white font-bold' : 'text-gray-600'
              }`}>
                <span className="text-2xl">🇩🇪</span>
                <span className="text-base font-bold">DE</span>
              </div>
              
              {/* English Button */}
              <div className={`absolute right-0 top-0 w-1/2 h-full flex items-center justify-center gap-2 transition-all duration-300 ${
                lang === 'en' ? 'text-white font-bold' : 'text-gray-600'
              }`}>
                <span className="text-2xl">🇬🇧</span>
                <span className="text-base font-bold">EN</span>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mt-2 text-center">
              {translate('profile.tapToChangeLanguage', lang)}
            </p>
          </div>

          {/* RESET-BUTTON */}
          <div className="mt-6 pt-6 border-t-2 border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-3">{translate('profile.dangerZoneTitle', lang)}</h3>
            <p className="text-sm text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: translate('profile.resetDescription', lang) }} />
            <button 
              onClick={() => {
                if (window.confirm(`${translate('profile.resetConfirmTitle1', lang)}\n\n${translate('profile.resetConfirmItems', lang)}`)) {
                  if (window.confirm(`${translate('profile.resetConfirmTitle2', lang)}\n\n${translate('profile.resetConfirmFinal', lang)}`)) {
                    const createdAt = user?.createdAt;

                    localStorage.clear();

                    localStorage.setItem('restroomadvisor_user_created_at', createdAt || new Date().toISOString().split('T')[0]);
                    
                    showToast(translate('profile.resetSuccessMessage', lang), 'success', 1000);
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  }
                }
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              <span className="text-xl">🔄</span>
              <span>{translate('profile.resetButtonText', lang)}</span>
            </button>
            
            {/* Stadt-Wechsel-Dialog wieder aktivieren */}
            {localStorage.getItem('restroomadvisor_dont_ask_city_switch') === 'true' && (
              <div className="mt-4 pt-4 border-t border-gray-300">
                <p className="text-sm text-gray-600 mb-3">
                  {translate('profile.citySwitchDisabled', lang)}
                </p>
                <button 
                  onClick={() => {
                    localStorage.removeItem('restroomadvisor_dont_ask_city_switch');
                    setLastCitySwitchPrompt(0); 
                    showToast(translate('profile.citySwitchReenabled', lang), 'success');
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <span className="text-xl">🔔</span>
                  <span>{translate('profile.reEnableCitySwitchButton', lang)}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ==========================================
// RANK SELECTION SCREEN
// ==========================================
if (scr === 'rank-selection') {
  const allRanks = getAllAvailableRanks();
  const selectedRank = getSelectedRank();
  
  const handleRankSelect = (rankId) => {
    localStorage.setItem('restroomadvisor_selected_rank', rankId);
    showToast(translate('profile.rankChanged', lang) || 'Rank changed!', 'success', 2000);
  };
  
  const RankCard = ({ rank, isSelected }) => {
    if (!rank.unlocked) {
      // Locked rank
      return (
        <div className="bg-gray-100 rounded-lg p-4 opacity-50 cursor-not-allowed border-2 border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{rank.icon}</span>
              <div>
                <h4 className="font-bold text-gray-600">{rank.name}</h4>
                <p className="text-xs text-gray-500">🔒 {rank.requirement}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Unlocked rank
    return (
      <button
        onClick={() => handleRankSelect(rank.id)}
        className={`w-full text-left rounded-lg p-4 transition-all ${
          isSelected
            ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-4 border-amber-400 shadow-lg shadow-amber-200'
            : 'bg-white border-2 border-gray-200 hover:border-amber-300 hover:shadow-md'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{rank.icon}</span>
            <div>
              <h4 className="font-bold text-gray-800">{rank.name}</h4>
              {isSelected && (
                <p className="text-xs text-amber-600 font-semibold">⭐ Active rank</p>
              )}
              {!isSelected && (
                <p className="text-xs text-gray-500">{rank.requirement}</p>
              )}
            </div>
          </div>
          {isSelected && <span className="text-2xl">⭐</span>}
        </div>
      </button>
    );
  };
  
  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3 max-w-6xl mx-auto">
            <button onClick={() => setScr('profile')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold text-gray-800">{translate('profile.selectRank', lang) || 'Select Your Rank'}</h1>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-2xl mx-auto p-4">
          {/* Current Rank Display */}
          <div className="bg-gradient-to-br from-amber-100 to-yellow-100 rounded-xl p-6 mb-6 border-2 border-amber-300 shadow-lg">
            <p className="text-sm text-gray-600 mb-2">{translate('profile.currentRank', lang) || 'Current Rank'}</p>
            <div className="flex items-center gap-3">
              <span className="text-4xl">{selectedRank.icon}</span>
              <h2 className="text-2xl font-bold text-gray-800">{selectedRank.name}</h2>
            </div>
          </div>
          
          {/* Check-In Ranks */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              🎯 {translate('profile.checkInRanks', lang) || 'Check-In Ranks'}
            </h3>
            <div className="space-y-3">
              {allRanks.checkin.map(rank => (
                <RankCard key={rank.id} rank={rank} isSelected={selectedRank.id === rank.id} />
              ))}
            </div>
          </div>
          
          {/* Review Ranks */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              💬 {translate('profile.reviewRanks', lang) || 'Review Ranks'}
            </h3>
            <div className="space-y-3">
              {allRanks.review.map(rank => (
                <RankCard key={rank.id} rank={rank} isSelected={selectedRank.id === rank.id} />
              ))}
            </div>
          </div>
          
          {/* Achievement Ranks */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              🏆 {translate('profile.achievementRanks', lang) || 'Achievement Ranks'}
            </h3>
            <div className="space-y-3">
              {allRanks.achievement.map(rank => (
                <RankCard key={rank.id} rank={rank} isSelected={selectedRank.id === rank.id} />
              ))}
            </div>
          </div>
          
          {/* Special Ranks */}
          {allRanks.special.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                ⭐ {translate('profile.specialRanks', lang) || 'Special Ranks'}
              </h3>
              <div className="space-y-3">
                {allRanks.special.map(rank => (
                  <RankCard key={rank.id} rank={rank} isSelected={selectedRank.id === rank.id} />
                ))}
              </div>
            </div>
          )}          
        </div>
      </div>
    </>
  );
}

// ==========================================
  // ADMIN DASHBOARD
  // ==========================================
  if (scr === 'admin' && isAdmin) {
    return (
      <>
        {/* 🎉 ACHIEVEMENT POPUP */}
        <AchievementPopup />
        
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="min-h-screen bg-red-600">
          {/* Header */}
          <div className="bg-red-600 shadow-lg p-4 sticky top-0 z-10">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <div className="flex items-center gap-3">
                <button onClick={() => setScr('profile')} className="p-2 hover:bg-red-500 rounded-lg">
                  <ArrowLeft size={24} className="text-white" />
                </button>
                <h1 className="text-xl font-bold text-white">👑 Admin Dashboard</h1>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={async () => {
                    await logoutAdmin();
                    setIsAdmin(false);
                    setAdminUser(null);
                    setScr('welcome');
                    showToast('✅ Erfolgreich ausgeloggt', 'success');
                  }}
                  className="px-4 py-2 bg-white text-red-600 rounded-lg font-bold hover:bg-red-50"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen rounded-t-2xl">
           {/* Letzte 5 Bewertungen */}
            {recentReviews.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">⭐ Letzte Bewertungen</h2>
                  <button
                    onClick={loadRecentReviews}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    🔄 Aktualisieren
                  </button>
                </div>
                <div className="space-y-3">
                  {recentReviews.map((review) => (
                    <div
                      key={review.id}
                      onClick={() => {
                        if (review.toiletDocId) {
                          const targetCity = review.toiletCity;
                          if (targetCity) {
                            setCity(targetCity);
                            setScr('list');
                            setTimeout(async () => {
                              const { doc, getDoc } = await import('firebase/firestore');
                              const tSnap = await getDoc(doc(db, 'toilets', review.toiletDocId));
                              if (tSnap.exists()) {
                                setSel({ id: tSnap.id, ...tSnap.data() });
                                setScr('detail');
                              }
                            }, 300);
                          }
                        }
                      }}
                      className={`flex items-start gap-4 p-4 rounded-xl border border-gray-200 transition-colors ${
                        review.toiletDocId ? 'hover:bg-blue-50 cursor-pointer' : 'bg-gray-50'
                      }`}
                    >
                      {/* Rang-Icon */}
                      <div className="text-2xl flex-shrink-0 mt-0.5">
                        {review.userRank?.icon || '🚽'}
                      </div>
                      
                      {/* Inhalt */}
                      <div className="flex-1 min-w-0">
                        {/* Zeile 1: Name + Sterne + Datum */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800 text-sm flex-shrink-0">
                            {review.user || 'Anonym'}
                          </span>
                          {review.rating > 0 && (
                            <div className="flex gap-0.5 flex-shrink-0">
                              {[1,2,3,4,5].map(s => (
                                <span key={s} className={`text-xs ${s <= review.rating ? 'text-amber-400' : 'text-gray-300'}`}>★</span>
                              ))}
                            </div>
                          )}
                          {review.isPhotoOnly && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex-shrink-0">📸</span>
                          )}
                          <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                            {review.date ? new Date(review.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
                          </span>
                        </div>
                        {/* Toiletten-Name */}
                        <div className="text-xs font-medium text-blue-600 mb-0.5 truncate">
                          🚻 {review.toiletName}
                        </div>
                        {/* Stadt */}
                        {review.toiletCity && (
                          <div className="text-xs text-gray-400 mb-0.5">
                            📍 {review.toiletCity}
                          </div>
                        )}
                        {/* Kommentar */}
                        {review.text && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-1 leading-snug">
                            {review.text}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

           {/* Statistik */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-4xl mb-2">🚻</div>
                <h3 className="text-2xl font-bold text-green-600">{totalToiletsCount}</h3>
                <p className="text-gray-600">Gesamt-Toiletten</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-4xl mb-2">⏳</div>
                <h3 className="text-2xl font-bold text-gray-800">{pendingCount}</h3>
                <p className="text-gray-600">Pending Toiletten</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-4xl mb-2">📸</div>
                <h3 className="text-2xl font-bold text-gray-800">{photosCount}</h3>
                <p className="text-gray-600">Pending Fotos</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-4xl mb-2">⚠️</div>
                <h3 className="text-2xl font-bold text-gray-800">{reportsCount}</h3>
                <p className="text-gray-600">Schließmeldungen</p>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-4xl mb-2">🚩</div>
                <h3 className="text-2xl font-bold text-gray-800">{commentReportsCount}</h3>
                <p className="text-gray-600">Kommentar-Reports</p>
              </div>
            </div>

            {/* Tab-Navigation */}
            <div className="bg-white shadow-sm p-6 mb-6 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Admin Dashboard</h2>
              
              <div className="flex gap-3 overflow-x-auto pb-2">
                <button
                  onClick={() => setAdminTab('pending')}
                  className={`flex-shrink-0 py-3 px-6 rounded-lg font-semibold transition-colors relative ${
                    adminTab === 'pending' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🕒 Pending
                  {pendingCount > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      adminTab === 'pending'
                        ? 'bg-white text-amber-600'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {pendingCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={async () => {
                    setAdminTab('photos');
                    await loadPendingPhotos();
                  }}
                  className={`flex-shrink-0 py-3 px-6 rounded-lg font-semibold transition-colors relative ${
                    adminTab === 'photos'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📸 Fotos
                  {photosCount > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      adminTab === 'photos'
                        ? 'bg-white text-purple-600'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {photosCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setAdminTab('reports')}
                  className={`flex-shrink-0 py-3 px-6 rounded-lg font-semibold transition-colors relative ${
                    adminTab === 'reports' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📋 Schließungen
                  {reportsCount > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      adminTab === 'reports'
                        ? 'bg-white text-red-600'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {reportsCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => {
                    setAdminTab('commentReports');
                    loadCommentReports();
                  }}
                  className={`flex-shrink-0 py-3 px-6 rounded-lg font-semibold transition-colors relative ${
                    adminTab === 'commentReports'
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🚩 Meldungen
                  {commentReportsCount > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      adminTab === 'commentReports'
                        ? 'bg-white text-orange-600'
                        : 'bg-orange-100 text-orange-800'
                    }`}>
                      {commentReportsCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setAdminTab('cleanup')}
                  className={`flex-shrink-0 py-3 px-6 rounded-lg font-semibold transition-colors ${
                    adminTab === 'cleanup' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🧹 Bereinigung
                </button>
              </div>
            </div>

            {/* TAB CONTENT */}
  {adminTab === 'pending' && (
    <>
    {/* Action Buttons */}
    {selectedPendingIds.length > 0 && (
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex gap-4">
        <button
          onClick={async () => {
            if (!window.confirm(`${selectedPendingIds.length} Toilette(n) freigeben?`)) return;
            
            setLoadingAdmin(true);
            try {
              for (const id of selectedPendingIds) {
                await approveToilet(id);
              }
              showToast(`✅ ${selectedPendingIds.length} Toilette(n) freigegeben!`, 'success');
              setSelectedPendingIds([]);
              await loadPendingToilets();
            } catch (error) {
              console.error('❌ Fehler:', error);
              showToast('Fehler beim Freigeben', 'error');
            }
            setLoadingAdmin(false);
          }}
          disabled={loadingAdmin}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg disabled:bg-gray-300"
        >
          ✅ Freigeben ({selectedPendingIds.length})
        </button>
        <button
          onClick={async () => {
            if (!window.confirm(`${selectedPendingIds.length} Toilette(n) ablehnen?`)) return;
            
            setLoadingAdmin(true);
            try {
              for (const id of selectedPendingIds) {
                await rejectToilet(id);
              }
              showToast(`✅ ${selectedPendingIds.length} Toilette(n) abgelehnt!`, 'success');
              setSelectedPendingIds([]);
              await loadPendingToilets();
            } catch (error) {
              console.error('❌ Fehler:', error);
              showToast('Fehler beim Ablehnen', 'error');
            }
            setLoadingAdmin(false);
          }}
          disabled={loadingAdmin}
          className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg disabled:bg-gray-300"
        >
          ❌ Ablehnen ({selectedPendingIds.length})
        </button>
      </div>
    )}

    {/* Refresh-Button */}
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">🕒 Pending Toiletten</h2>
        <button 
          onClick={async () => {
            setLoadingAdmin(true);
            await loadPendingToilets();
            setLoadingAdmin(false);
            showToast('✅ Liste aktualisiert!', 'success', 2000);
          }}
          disabled={loadingAdmin}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg disabled:bg-gray-300"
        >
          🔄 Neu laden
        </button>
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>{pendingToilets.length} Vorschlag{pendingToilets.length !== 1 ? 'e' : ''}</strong> warten auf Prüfung.
          {pendingToilets.length > 0 && ' Wähle Toiletten aus und gib sie frei oder lehne sie ab.'}
        </p>
      </div>
    </div>

    {/* Pending Toiletten Liste */}
    <div className="space-y-4">
      {pendingToilets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Keine pending Toiletten!</h3>
          <p className="text-gray-600">Alle Vorschläge wurden bearbeitet.</p>
        </div>
      ) : (
        pendingToilets.map(toilet => (
          <div key={toilet.id} className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-start gap-4">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedPendingIds.includes(toilet.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedPendingIds([...selectedPendingIds, toilet.id]);
                  } else {
                    setSelectedPendingIds(selectedPendingIds.filter(id => id !== toilet.id));
                  }
                }}
                className="w-6 h-6 mt-1 cursor-pointer"
              />

              {/* Toiletten-Info */}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{toilet.name}</h3>
                <p className="text-gray-600 mb-2">
                  📍 {toilet.address && !toilet.address.includes('Unbekannt') 
                    ? toilet.address 
                    : formatCoordinates(toilet.lat, toilet.lng)}
                </p>
                <p className="text-sm text-gray-500 mb-2">🏙️ Stadt: {toilet.city}</p>
                <p className="text-sm text-gray-500 mb-2">👤 Vorgeschlagen von: {toilet.suggestedBy || 'Unbekannt'}</p>
                <p className="text-sm text-gray-500 mb-4">📅 Datum: {toilet.suggestedDate || 'Unbekannt'}</p>

                {/* Fotos */}
                {toilet.images && toilet.images.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-bold text-gray-700 mb-2">Fotos ({toilet.images.length}):</p>
                    <div className="flex gap-2 flex-wrap">
                      {toilet.images.map((img, idx) => (
                        <img 
                          key={idx} 
                          src={img} 
                          alt={`Foto ${idx + 1} von ${toilet.images.length} der vorgeschlagenen Toilette ${toilet.name || 'ohne Namen'} - Klicken zum Vergrößern`}
                          className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-80"
                          onClick={() => window.open(img, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                )}
                

                {/* Koordinaten */}
                <p className="text-xs text-gray-400">📍 Koordinaten: {toilet.lat?.toFixed(6)}, {toilet.lng?.toFixed(6)}</p>
              </div>

              {/* Einzelne Actions */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setAdminPreviewToilet(toilet);
                    setScr('admin-preview');
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg"
                >
                  👁️
                </button>
                
                <button
                  onClick={async () => {
                    setLoadingAdmin(true);
                    try {
                      await approveToilet(toilet.id);
                      showToast('✅ Toilette freigegeben!', 'success');
                      await loadPendingToilets();
                    } catch (error) {
                      console.error('❌ Fehler:', error);
                      showToast('Fehler beim Freigeben', 'error');
                    }
                    setLoadingAdmin(false);
                  }}
                  disabled={loadingAdmin}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg disabled:bg-gray-300"
                >
                  ✅
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm('Diese Toilette wirklich ablehnen?')) return;
                    
                    setLoadingAdmin(true);
                    try {
                      await rejectToilet(toilet.id);
                      showToast('✅ Toilette abgelehnt!', 'success');
                      await loadPendingToilets();
                    } catch (error) {
                      console.error('❌ Fehler:', error);
                      showToast('Fehler beim Ablehnen', 'error');
                    }
                    setLoadingAdmin(false);
                  }}
                  disabled={loadingAdmin}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg disabled:bg-gray-300"
                >
                  ❌
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </>
)}

{/* PENDING FOTOS */}
{adminTab === 'photos' && (
  <div>
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">📸 Pending Fotos freigeben</h2>
        <div className="flex gap-2">        
          <button 
            onClick={loadPendingPhotos}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg"
          >
            🔄 Neu laden
          </button>
        </div>
      </div>
      
      {pendingPhotos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <div className="text-6xl mb-4">✅</div>
          <p className="text-lg font-semibold">Keine pending Fotos!</p>
          <p className="text-sm">Alle Fotos wurden bereits freigegeben.</p>
        </div>
      )}
      
      {pendingPhotos.length > 0 && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>{pendingPhotos.length} Foto(s)</strong> warten auf Freigabe. 
              Prüfe die Fotos und gib sie frei oder lehne sie ab.
            </p>
          </div>
          
          {pendingPhotos.map((photo, index) => (
            <div key={`${photo.toiletId}-${photo.photoIndex}-${index}`} className="bg-white border-2 border-pink-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Foto-Vorschau */}
                <div>
                  <img 
                    src={photo.url} 
                    alt={`Zur Freigabe wartendes Toiletten-Foto von ${photo.uploadedBy || 'Unbekannt'} für ${photo.toiletName || 'unbekannte Toilette'}`}
                    className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-gray-500">
                      <strong>Status:</strong> <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-semibold">⏳ Pending</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      <strong>Hochgeladen:</strong> {photo.uploadedAt ? new Date(photo.uploadedAt).toLocaleString('de-DE') : 'Unbekannt'}
                    </p>
                    <p className="text-xs text-gray-500">
                      <strong>Kontext:</strong> {photo.fromComment ? '💬 Review-Foto' : '🖼️ Haupt-Foto'}
                    </p>
                  </div>
                </div>
                
                {/* Info & Aktionen */}
                <div className="space-y-3">
                  <div>
                    <h3 className="font-bold text-lg text-gray-800 mb-1">
                      {photo.toiletName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-1">
                      📍 {photo.toiletAddress}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      🏙️ {photo.toiletCity} | ID: {photo.toiletId}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-sm">
                      <strong>👤 Hochgeladen von:</strong> {photo.uploadedBy || 'Unbekannt'}
                    </p>
                    {photo.fromComment && (
                      <p className="text-sm">
                        <strong>💬 Von User:</strong> {photo.commentUser || 'Unbekannt'}
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={async () => {
                        await approvePhoto(
                          photo.toiletId, 
                          photo.photoIndex,
                          photo.fromComment,
                          photo.commentIndex
                        );
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                      ✅ Freigeben
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Dieses Foto wirklich ablehnen und löschen?')) return;
                        await rejectPhoto(
                          photo.toiletId, 
                          photo.photoIndex,
                          photo.fromComment,
                          photo.commentIndex
                        );
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                      ❌ Ablehnen
                    </button>
                  </div>
                  
                  {/* Toilette anzeigen Button */}
                  <button
                    onClick={async () => {
                      const allToiletsFromDB = await getAllToilets();
                      const toilet = allToiletsFromDB.find(t => t.id === photo.toiletId);
                      if (toilet) {
                        setSel(toilet);
                        setSelectedImageIndex(0);
                        setScr('detail');
                      }
                    }}
                    className="w-full bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    👁️ Toilette anzeigen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)}

{/* REPORTS */}
{adminTab === 'reports' && (
  <div>
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-800">⚠️ Meldungen</h2>
        <button 
          onClick={loadAllReports}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg"
        >
          🔄 Neu laden
        </button>
      </div>
      
      {allReports.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">✅</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Keine Meldungen!</h3>
          <p className="text-gray-600">Es gibt aktuell keine offenen Meldungen.</p>
        </div>
      ) : (
        <div>
          <p className="text-gray-600 mb-4">
            {allReports.length} Meldung{allReports.length !== 1 ? 'en' : ''} gesamt
          </p>
          
          {Object.entries(
            allReports.reduce((acc, report) => {
              if (!acc[report.toiletId]) {
                acc[report.toiletId] = [];
              }
              acc[report.toiletId].push(report);
              return acc;
            }, {})
          )
          .sort(([, a], [, b]) => b.length - a.length)
          .map(([toiletId, reports]) => {
            const firstReport = reports[0];
            return (
              <div key={toiletId} className="bg-gray-50 rounded-lg p-4 mb-4 border-2 border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-1">
                      {firstReport.toiletName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      📍 {firstReport.toiletAddress}
                    </p>
                    <p className="text-sm text-gray-500">
                      🏙️ Stadt: {firstReport.city}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-4 py-2 rounded-full font-bold ${
                      reports.length >= 3 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {reports.length}x gemeldet
                    </span>
                  </div>
                </div>
                
                {/* Alle Reports anzeigen */}
                <div className="space-y-2 mb-3">
                  {reports.map((report, idx) => {
                    const categoryIcons = {
                      closed: '🔒',
                      address: '📍',
                      hours: '🕐',
                      accessibility: '♿',
                      cost: '💶',
                      other: '📝'
                    };
                    
                    const categoryLabels = {
                      closed: 'Geschlossen',
                      address: 'Falsche Adresse',
                      hours: 'Falsche Öffnungszeiten',
                      accessibility: 'Barrierefreiheit',
                      cost: 'Kosten',
                      other: 'Sonstiges'
                    };

                    return (
                      <div key={idx} className="bg-white rounded p-3 text-sm border-l-4 border-amber-500">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-800">
                            {report.user}
                          </span>
                          <span className="text-gray-500">
                            {new Date(report.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                        
                        {/* Kategorie anzeigen (falls vorhanden) */}
                        {report.category && (
                          <div className="mb-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-semibold">
                              {categoryIcons[report.category] || '📝'} {categoryLabels[report.category] || report.category}
                            </span>
                          </div>
                        )}
                        
                        <p className="text-gray-700 mb-1">
                          <strong>Grund:</strong> {report.reason}
                        </p>
                        
                        {report.comment && (
                          <p className="text-gray-600 text-xs mt-2 bg-gray-50 p-2 rounded">
                            💬 {report.comment}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Actions */}
                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    {/* Nur "Schließen"-Button anzeigen wenn es eine "closed"-Meldung ist */}
                    {reports.some(r => !r.category || r.category === 'closed') && (
                      <button
                        onClick={async () => {
                          const reason = window.prompt(
                            `Toilette "${firstReport.toiletName}" als geschlossen markieren.\n\nGrund:`,
                            reports[0].reason
                          );
                          
                          if (!reason) return;
                          
                          const isTemporary = window.confirm('Ist die Schließung temporär? (Ja = Temporär, Nein = Permanent)');
                          
                          let closedUntil = null;
                          if (isTemporary) {
                            const dateStr = window.prompt('Bis wann geschlossen? (Format: YYYY-MM-DD)\nLeer lassen wenn unbekannt');
                            if (dateStr) {
                              closedUntil = dateStr;
                            }
                          }
                          
                          setLoadingAdmin(true);
                          try {
                            await closeToilet(toiletId, reason, closedUntil);
                            await deleteReportsForToilet(toiletId);
                            
                            const cityName = firstReport.city;
                            const reportKey = `${cityName}-${toiletId}`;
                            const updatedClRep = { ...clRep };
                            delete updatedClRep[reportKey];
                            setClRep(updatedClRep);
                            
                            await loadAllReports();
                            showToast('✅ Toilette als geschlossen markiert & Meldungen gelöscht!', 'success');
                          } catch (error) {
                            console.error('❌ Fehler:', error);
                            showToast('Fehler beim Schließen', 'error');
                          }
                          setLoadingAdmin(false);
                        }}
                        disabled={loadingAdmin}
                        className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-lg disabled:bg-gray-300"
                      >
                        🔒 Bestätigen & Schließen
                      </button>
                    )}
                    
                    {/* Button "Als erledigt markieren" für andere Kategorien */}
                    <button
                      onClick={async () => {
                        const categoryText = firstReport.category 
                          ? `(Kategorie: ${firstReport.category})` 
                          : '';
                        
                        if (!window.confirm(`Meldung für "${firstReport.toiletName}" ${categoryText} als erledigt markieren?\n\nDie Meldungen werden gelöscht.`)) return;
                        
                        setLoadingAdmin(true);
                        try {
                          await deleteReportsForToilet(toiletId);
                          
                          const cityName = firstReport.city;
                          const reportKey = `${cityName}-${toiletId}`;
                          const updatedClRep = { ...clRep };
                          delete updatedClRep[reportKey];
                          setClRep(updatedClRep);
                          
                          await loadAllReports();
                          showToast('✅ Meldung als erledigt markiert!', 'success');
                        } catch (error) {
                          console.error('❌ Fehler:', error);
                          showToast('Fehler beim Markieren', 'error');
                        }
                        setLoadingAdmin(false);
                      }}
                      disabled={loadingAdmin}
                      className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg disabled:bg-gray-300"
                    >
                      ✅ Als erledigt markieren
                    </button>
                    
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Meldungen für "${firstReport.toiletName}" wirklich als unbegründet ablehnen?\n\nDie Toilette bleibt geöffnet.`)) return;
                        
                        setLoadingAdmin(true);
                        try {
                          await deleteReportsForToilet(toiletId);
                          
                          const cityName = firstReport.city;
                          const reportKey = `${cityName}-${toiletId}`;
                          const updatedClRep = { ...clRep };
                          delete updatedClRep[reportKey];
                          setClRep(updatedClRep);
                          
                          await loadAllReports();
                          showToast('✅ Unbegründete Meldungen abgelehnt!', 'success');
                        } catch (error) {
                          console.error('❌ Fehler:', error);
                          showToast('Fehler beim Ablehnen', 'error');
                        }
                        setLoadingAdmin(false);
                      }}
                      disabled={loadingAdmin}
                      className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg disabled:bg-gray-300"
                    >
                      ❌ Ablehnen (unbegründet)
                    </button>
                  </div>
                  
                  <button
                    onClick={async () => {
                      // Erst in bereits geladenen Daten suchen
                      const allToilets = Object.values(tData).flat();
                      let toilet = allToilets.find(t => t.id === toiletId);
                      
                      // Wenn nicht gefunden, direkt aus Firebase laden
                      if (!toilet) {
                        try {
                          const toiletDoc = await getDoc(doc(db, 'toilets', toiletId));
                          if (toiletDoc.exists()) {
                            toilet = { id: toiletDoc.id, ...toiletDoc.data() };
                          }
                        } catch (error) {
                          console.error('Fehler beim Laden der Toilette:', error);
                        }
                      }
                      
                      if (toilet) {
                        setSel(toilet);
                        setCity(toilet.city);

                        if (toilet.lat && toilet.lng) {
                          setMapCenter([toilet.lat, toilet.lng]);
                          setMapZoom(16); 
                        }
                        
                        setScr('detail');
                      } else {
                        showToast('Toilette nicht gefunden', 'error');
                      }
                    }}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg"
                  >
                    👁️ Toilette ansehen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}

{/* KOMMENTAR-MELDUNGEN */}
{adminTab === 'commentReports' && (
  <div className="bg-white rounded-xl shadow-lg p-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-2xl font-bold text-gray-800">🚩 Gemeldete Kommentare</h2>
      <button
        onClick={loadCommentReports}
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg"
      >
        🔄 Neu laden
      </button>
    </div>
    
    {commentReports.length === 0 ? (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">✨</div>
        <p className="text-gray-500">Keine offenen Meldungen. Alles sauber!</p>
      </div>
    ) : (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 mb-4">
          {commentReports.length} Meldung{commentReports.length !== 1 ? 'en' : ''} zu überprüfen
        </p>
        
        {commentReports.map((report) => (
          <div key={report.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
            {/* Header mit Meldungsgrund */}
            <div className="flex items-center justify-between mb-3">
              <span className="px-2 py-1 bg-red-100 text-red-700 text-sm rounded-full font-medium">
                🚩 {report.reportReason}
              </span>
              <span className="text-xs text-gray-500">
                Gemeldet: {new Date(report.reportedAt).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            {/* Gemeldeter Kommentar */}
            <div className="bg-white rounded-lg p-3 mb-3 border">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-gray-800">{report.commentUser}</span>
                <div className="flex">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      size={12}
                      className={j < report.commentRating ? 'fill-amber-500 text-amber-500' : 'text-gray-300'}
                    />
                  ))}
                </div>
              </div>
              <p className="text-gray-700 text-sm">
                {report.commentText || <em className="text-gray-400">Kein Kommentartext</em>}
              </p>
            </div>
            
            {/* Melder-Info */}
            <p className="text-xs text-gray-500 mb-3">
              Gemeldet von: <span className="font-medium">{report.reportedBy}</span>
            </p>
            
            {/* Aktionen */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleDeleteReportedComment(report)}
                disabled={loadingAdmin}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                🗑️ Kommentar löschen
              </button>
              <button
                onClick={() => handleIgnoreReport(report)}
                disabled={loadingAdmin}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
              >
                ✓ Unbegründet
              </button>
              <button
                onClick={async () => {
                  const allToiletsFromDB = await getAllToilets();
                  const toilet = allToiletsFromDB.find(t => t.id === report.toiletId);
                  if (toilet) {
                    setSel(toilet);
                    setCity(toilet.city);
                    setSelectedImageIndex(0);

                    if (toilet.lat && toilet.lng) {
                      setMapCenter([toilet.lat, toilet.lng]);
                      setMapZoom(16); 
                    }
                    
                    setScr('detail');
                  } else {
                    showToast('Toilette nicht gefunden', 'error');
                  }
                }}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-semibold rounded-lg"
              >
                📍 Toilette ansehen
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}

{/* BEREINIGUNG (CLEANUP) */}
{adminTab === 'cleanup' && (
  <div className="bg-white rounded-xl shadow-lg p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-lg font-bold text-gray-800 mb-3">🔍 Datenqualität & Bereinigung</h3>
      {cleaningResults.length > 0 && (
        <button
          onClick={() => {
            setCleaningResults([]);
            setSelectedCleanIds([]);
            setLoadingCleaning(false); 
            // const rejectedIds = JSON.parse(localStorage.getItem('restroomadvisor_rejectedQualityIssues') || '[]');
            showToast('🔄 Vorschau zurückgesetzt', 'info');
          }}
          className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-bold"
        >
          🔄 Zurücksetzen & Neu
        </button>
      )}
    </div>
    
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
      <p className="text-blue-800">
        <strong>Info:</strong> Findet Qualitätsprobleme: Öffnungszeiten im Titel, zu lange Titel, nicht-deutsche Einträge, Sonderzeichen.
      </p>
    </div>
    
    {/* Lade-Button */}
    {cleaningResults.length === 0 && (
      <div className="text-center py-8">
        <div className="text-4xl mb-3">🧹</div>
        <p className="text-gray-600 mb-4 text-sm">
          Lade alle Toiletten und zeige Vorschau der Bereinigung
        </p>
        <button
          onClick={loadCleaningPreview}
          disabled={loadingCleaning}
          className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg disabled:bg-gray-300 text-sm"
        >
          {loadingCleaning ? '⏳ Analysiere...' : '🔍 Qualitäts-Check starten'}
        </button>
      </div>
    )}
    
    {/* Ergebnisse */}
    {cleaningResults.length > 0 && (
      <>
        {/* Kompakte Statistik */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-purple-50 border border-purple-200 rounded p-2 text-center">
            <div className="text-xl font-bold text-purple-700">{cleaningResults.length}</div>
            <div className="text-xs text-purple-600">Toiletten</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
            <div className="text-xl font-bold text-blue-700">
              {new Set(cleaningResults.map(r => r.city)).size}
            </div>
            <div className="text-xs text-blue-600">Städte</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
            <div className="text-xl font-bold text-green-700">{selectedCleanIds.length}</div>
            <div className="text-xs text-green-600">Ausgewählt</div>
          </div>
        </div>
        
        {/* Action-Buttons */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedCleanIds.length === cleaningResults.length}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedCleanIds(cleaningResults.map(r => r.id));
                } else {
                  setSelectedCleanIds([]);
                }
              }}
              className="w-4 h-4"
            />
            <label htmlFor="selectAll" className="text-sm font-semibold text-gray-700 cursor-pointer">
              Alle
            </label>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                if (window.confirm('Vorschau verwerfen?')) {
                  setCleaningResults([]);
                  setSelectedCleanIds([]);
                }
              }}
              className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded text-xs font-bold"
            >
              ❌ Abbrechen
            </button>
            <button
              onClick={confirmSelectedCleanings}
              disabled={selectedCleanIds.length === 0 || loadingCleaning}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-bold disabled:bg-gray-300"
            >
              ✅ Auswahl ({selectedCleanIds.length})
            </button>
            <button
              onClick={confirmAllCleanings}
              disabled={loadingCleaning}
              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold disabled:bg-gray-300"
            >
              ✅ Alle ({cleaningResults.length})
            </button>
          </div>
        </div>
        
        {/* Kompakte Liste */}
        <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50">
          {cleaningResults.map((result) => (
            <div 
              key={result.id} 
              className={`border rounded p-2 text-sm ${
                selectedCleanIds.includes(result.id)
                  ? 'border-purple-400 bg-purple-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={selectedCleanIds.includes(result.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedCleanIds(prev => [...prev, result.id]);
                    } else {
                      setSelectedCleanIds(prev => prev.filter(id => id !== result.id));
                    }
                  }}
                  className="w-4 h-4 mt-0.5"
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Vorher:</p>
                      <p className="text-xs text-gray-800 line-through truncate">{result.oldName}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">Nachher:</p>
                      <p className="text-xs font-bold text-green-700 truncate">{result.newName}</p>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 border border-purple-200 rounded px-2 py-1 mb-1">
                    <p className="text-xs text-purple-800 font-semibold">⏰ {result.openingHours}</p>
                  </div>
                  
                  <p className="text-xs text-gray-500">🏙️ {result.city}</p>
                </div>
                
                {/* Action-Buttons */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => rejectCleaning(result.id)}
                    className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-bold rounded"
                    title="Überspringen - wird nicht mehr angezeigt"
                  >
                    ❌
                  </button>
                  
                  <button
                    onClick={() => {
                      setEditMode(true);
                      setEditToiletId(result.id);
                      setSNam(result.newName || result.oldName || '');
                      setSAdd(result.address || '');
                      setSCity(result.city || '');
                      if (result.lat && result.lng) {
                        setSCoordinates({ lat: result.lat, lng: result.lng });
                      }
                      setScr('suggest');
                    }}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded"
                  >
                    ✏️
                  </button>

                  {result.canAutoFix && (
                    <button
                      onClick={() => confirmSingleCleaning(result.id)}
                      className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded"
                      title="Automatisch bereinigen"
                    >
                      ✅
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    )}
  </div>
)}
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default App;